import { createDirectus, rest, authentication, readMe, updateMe, readItems, readItem, createItem, updateItem, deleteItem, uploadFiles, readUser, readUsers } from '@directus/sdk';
import { env } from './env';
import { ScheduleItem } from './types';
import { getWIBTimeForDirectus, getWIBTime } from './timeUtils';
import { networkErrorHandler } from './networkErrorHandler';

// Define the Directus schema
interface DirectusSchema {
  directus_users: {
    id: string;
    first_name?: string;
    last_name?: string;
    email: string;
    password?: string;
    location?: string;
    title?: string;
    description?: string;
    tags?: string[];
    avatar?: string;
    language?: string;
    theme?: 'auto' | 'light' | 'dark';
    tfa_secret?: string;
    status: 'active' | 'invited' | 'draft' | 'suspended' | 'deleted';
    role: string;
    token?: string;
    last_access?: string;
    last_page?: string;
    provider?: string;
    external_identifier?: string;
    auth_data?: any;
    email_notifications?: boolean;
    created_on?: string;
    modified_on?: string;
  };
  emp_visits: {
    id: string;
    user_id: string; // References directus_users.id directly
    customer_id?: number; // References Customer_Tambak.id (optional)
    customer_name: string;
    customer_address: string;
    customer_phone?: string;
    customer_email?: string;
    visit_type: 'sales' | 'service' | 'follow_up' | 'demo' | 'support';
    visit_purpose?: string;
    latitude: number;
    longitude: number;
    location_accuracy?: number;
    start_time: string;
    end_time?: string;
    duration?: number; // Calculated in minutes
    status: 'scheduled' | 'active' | 'completed' | 'cancelled' | 'no_show';
    satisfaction_rating?: number; // 1-5 scale
    revenue_potential?: number;
    actual_revenue?: number;
    notes?: string;
    internal_notes?: string; // Private notes not visible to customer
    next_action?: string;
    follow_up_date?: string;
    tags?: string[]; // Visit tags for categorization
    weather_condition?: string;
    travel_distance?: number; // In kilometers
    travel_time?: number; // In minutes
    selfie?: string; // References directus_files.id for arrival selfie photo
    frontlocation?: string; // References directus_files.id for Gerbang Tambak photo
    Farm_area?: string; // References directus_files.id for Area Tambak photo
    created_at: string;
    updated_at: string;
    user_created?: string;
    user_updated?: string;
  };
  
  customer_visit_evidence: {
    id: string;
    // visit_id: string; // References emp_visits.id
    // user_id: string; // References directus_users.id
    type: 'photo' | 'audio' | 'document' | 'screenshot' | 'signature' | 'form';
    category: 'before' | 'during' | 'after' | 'damage' | 'completion' | 'signature';
    filename: string;
    original_filename?: string;
    file_size: number;
    mime_type: string;
    file_url?: string;
    thumbnail_url?: string;
    description?: string;
    is_required: boolean;
    is_customer_visible: boolean; // Whether customer can see this evidence
    latitude?: number;
    longitude: number;
    location_accuracy?: number;
    file_id?: string;
    captured_at: string;
    uploaded_at?: string;
    processing_status: 'pending' | 'processing' | 'completed' | 'failed';
    metadata?: any; // Additional file metadata (EXIF, etc.)
    created_at: string;
    updated_at: string;
    user_created?: string;
    user_updated?: string;
  };
  
  Customer_Tambak: {
    id: number;
    No_pelanggan: string; // Foreign key to Data_Customer.No_pelanggan
    Nama_Pelanggan_Tambak: string; // Farm/tambak name
    Alamat?: string;
    Contacs?: string;
    PIC?: any; // JSON field with multiple PICs
    ukuranTambak?: string; // Farm size
    tahunDibangun?: number; // Year built
    tglPembelianTerakhir?: string; // Last purchase date
    tglInteraksiTerakhir?: string; // Last interaction date
    source?: string; // Data source
    farmIsActive?: boolean; // Whether the farm/tambak is currently active/operational
    created_at?: string;
    updated_at?: string;
  };

  coupon: {
    id: string; // UUID
    nama: string;
    details?: string;
    validFrom?: string;
    validUntil?: string;
    user_created?: string;
    date_created?: string;
  };

  couponUser: {
    id: number;
    claimed?: boolean;
    issuedTime?: string;
    coupon?: any[]; // M2M relation to coupon
    user?: any[]; // M2M relation to directus_users
    user_created?: string;
    date_created?: string;
  };

  emp_promotions: {
    id: string;
    title: string;
    description: string;
    promotion_type: 'discount' | 'bundle' | 'cashback' | 'special_offer';
    discount_percentage?: number;
    discount_amount?: number;
    valid_from: string;
    valid_to: string;
    terms_conditions: string;
    banner_image?: string;
    target_audience: 'all_salespersons' | 'senior_only' | 'new_joiners';
    is_active: boolean;
    date_created?: string;
    date_updated?: string;
  };
}

// Get Directus URL from environment - use direct URL for now to test functionality
const directusUrl = env.VITE_DIRECTUS_URL;

if (env.DEBUG) console.warn('🌐 Directus URL:', directusUrl);

// Create Directus client with authentication support
export const directus = createDirectus<DirectusSchema>(directusUrl)
  .with(rest())
  .with(authentication('json'));

// Authentication state management
export interface DirectusAuthState {
  user: any | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export class DirectusAuthManager {
  private static instance: DirectusAuthManager;
  private authState: DirectusAuthState = {
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: false,
    error: null
  }
  
  // Redux store dispatch function for logout actions
  private storeDispatch: ((action: any) => void) | null = null;

  // Enhanced token management properties
  private refreshTimer: NodeJS.Timeout | null = null;
  private keepAliveTimer: NodeJS.Timeout | null = null;
  private refreshTokenTimer: NodeJS.Timeout | null = null;
  private tokenExpiryTime: number | null = null;
  private refreshToken: string | null = null;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<void> | null = null;
  private gracefulLogoutTimer: NodeJS.Timeout | null = null;
  private isBackgroundRefreshing = false;
  private userActivityState = {
    isTyping: false,
    isFormFocused: false,
    lastActivity: Date.now(),
    activityLevel: 'idle' as 'idle' | 'light' | 'active' | 'intensive'
  };
  private storedCredentials: { email: string; password: string } | null = null;
  private isReAuthenticating: boolean = false; // Track when doing credential re-authentication
  
  // Enhanced configurable intervals for better token persistence
  private readonly REFRESH_BUFFER_TIME = 8 * 60 * 1000; // 8 minutes before expiry (increased buffer)
  private readonly KEEP_ALIVE_INTERVAL = 90 * 1000; // 1.5 minutes keep-alive ping (more frequent)
  private readonly TOKEN_CHECK_INTERVAL = 20 * 1000; // 20 seconds token validity check (more frequent)
  private readonly MAX_RETRY_ATTEMPTS = 2; // Reduced retry attempts to prevent loops
  private readonly RETRY_DELAY = 1500; // 1.5 seconds between retries (faster recovery)
  private readonly PROACTIVE_REFRESH_TIME = 10 * 60 * 1000; // 10 minutes before expiry for proactive refresh
  
  // Network status tracking
  private isOnline: boolean = true;
  private retryCount: number = 0;
  private lastUserActivity: number = Date.now();
  private activityTimer: NodeJS.Timeout | null = null;
  private lastGentleValidation: number = 0; // Track last gentle validation time
  private readonly USER_ACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes of inactivity
  private readonly GENTLE_VALIDATION_THROTTLE = 10 * 1000; // 10 seconds between gentle validations

  constructor() {
    this.loadPersistedAuth();
    this.initializeTokenManagement();
    this.initializeNetworkMonitoring();
    this.initializeUserActivityMonitoring();
  };
  
  private listeners: ((state: DirectusAuthState) => void)[] = [];

  static getInstance(): DirectusAuthManager {
    if (!DirectusAuthManager.instance) {
      DirectusAuthManager.instance = new DirectusAuthManager();
    }
    return DirectusAuthManager.instance;
  }

  /**
   * Subscribe to authentication state changes
   */
  subscribe(listener: (state: DirectusAuthState) => void): () => void {
    this.listeners.push(listener);
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of state changes
   */
  private notify(): void {
    this.listeners.forEach(listener => listener(this.authState));
  }

  /**
   * Set store dispatch function for Redux integration
   */
  setStoreDispatch(dispatch: (action: any) => void): void {
    this.storeDispatch = dispatch;
    console.log('🔧 Redux store dispatch registered for token expiration handling');
  }

  /**
   * Check if an error indicates token expiration
   */
  private isTokenExpiredError(error: any): boolean {
    if (!error) return false;
    
    // 401 is always unauthorized/token expired
    if (error.status === 401) {
      return true;
    }
    
    // 403 can be either token expired OR permission denied
    // Only treat as token expired if there's specific indication
    if (error.status === 403) {
      // Check for specific token-related error messages
      if (error.message && typeof error.message === 'string') {
        const message = error.message.toLowerCase();
        if (message.includes('token expired') || 
            message.includes('session expired') ||
            message.includes('invalid token') ||
            message.includes('jwt expired')) {
          return true;
        }
        // If it's just "forbidden" or "access denied", treat as permission issue, not token expiry
        if (message.includes('forbidden') || message.includes('access denied')) {
          return false;
        }
      }
      
      // Check for Directus-specific token expiration codes
      if (error.extensions?.code === 'TOKEN_EXPIRED' || error.extensions?.code === 'INVALID_TOKEN') {
        return true;
      }
      
      // If no specific token indicators, treat 403 as permission denied (not token expiry)
      return false;
    }
    
    // Check for specific "Token expired" message in other status codes
    if (error.message && typeof error.message === 'string') {
      const message = error.message.toLowerCase();
      if (message.includes('token expired') || 
          message.includes('unauthorized') ||
          message.includes('session expired') ||
          message.includes('invalid token') ||
          message.includes('jwt expired')) {
        return true;
      }
    }
    
    // Check for Directus-specific error structure
    if (error.errors && Array.isArray(error.errors)) {
      return error.errors.some((err: any) => 
        err.message?.toLowerCase().includes('token expired') ||
        err.message?.toLowerCase().includes('invalid token') ||
        err.message?.toLowerCase().includes('jwt expired') ||
        err.extensions?.code === 'TOKEN_EXPIRED' ||
        err.extensions?.code === 'INVALID_TOKEN'
      );
    }
    
    // Check for extensions code directly
    if (error.extensions?.code === 'TOKEN_EXPIRED' || error.extensions?.code === 'INVALID_TOKEN') {
      return true;
    }
    
    return false;
  }

  /**
   * Handle API errors with automatic logout on token expiration
   */
  async handleApiError(error: any, operation: string = 'API request'): Promise<void> {
    if (this.isTokenExpiredError(error)) {
      console.log(`🚪 Token expired error detected in ${operation} - forcing logout`);
      await this.forceLogout(`Token expired during ${operation}`);
      throw new Error('Session expired. Please log in again.');
    }
    
    // For other errors, just re-throw
    throw error;
  }

  /**
   * Update authentication state
   */
  private updateState(updates: Partial<DirectusAuthState>): void {
    this.authState = { ...this.authState, ...updates };
    this.notify();
  }

  /**
   * Silent update - updates state without triggering notifications
   * Used for background token refresh to avoid disrupting user interactions
   */
  private silentUpdateState(updates: Partial<DirectusAuthState>): void {
    this.authState = { ...this.authState, ...updates };
    // Note: No notify() call here - this prevents React re-renders
    console.log('🔕 Silent state update performed (no UI disruption)');
  }

  /**
   * Get current authentication state
   */
  getState(): DirectusAuthState {
    return { ...this.authState };
  }

  /**
   * Load persisted authentication from localStorage
   */
  private loadPersistedAuth(): void {
    try {
      const persisted = localStorage.getItem('directus_auth');
      if (persisted) {
        const { token, user, refreshToken, tokenExpiryTime } = JSON.parse(persisted);
        if (token) {
          this.authState = {
            ...this.authState,
            token,
            user,
            isAuthenticated: !!user
          };
          this.refreshToken = refreshToken || null;
          this.tokenExpiryTime = tokenExpiryTime || null;
          
          // Set the token in directus client
          directus.setToken(token);
          
          // Note: The refresh token from localStorage won't be available to the SDK
          // after page reload. The SDK's internal state is lost. This means automatic
          // token refresh might fail, requiring re-authentication.
          
          // Validate token immediately if we have expiry time
          if (this.tokenExpiryTime) {
            const timeUntilExpiry = this.tokenExpiryTime - Date.now();
            if (timeUntilExpiry <= 0) {
              this.clearPersistedAuth();
              return;
            }
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load persisted auth:', error);
      this.clearPersistedAuth();
    }
  }

  /**
   * Persist authentication to localStorage
   */
  private persistAuth(): void {
    try {
      if (this.authState.token && this.authState.user) {
        localStorage.setItem('directus_auth', JSON.stringify({
          token: this.authState.token,
          user: this.authState.user,
          refreshToken: this.refreshToken,
          tokenExpiryTime: this.tokenExpiryTime,
          lastRefresh: Date.now()
        }));
      } else {
        this.clearPersistedAuth();
      }
    } catch (error) {
      console.warn('Failed to persist auth:', error);
    }
  }

  /**
   * Clear persisted authentication
   */
  private clearPersistedAuth(): void {
    try {
      localStorage.removeItem('directus_auth');
      this.refreshToken = null;
      this.tokenExpiryTime = null;
    } catch (error) {
      console.warn('Failed to clear persisted auth:', error);
    }
  }

  /**
   * Initialize network monitoring for connection-aware token management
   */
  private initializeNetworkMonitoring(): void {
    if (typeof window !== 'undefined') {
      // Monitor online/offline status
      window.addEventListener('online', () => {
        this.isOnline = true;
        this.retryCount = 0;
        
        // Resume token management when back online
        if (this.authState.isAuthenticated) {
          this.performTokenValidation();
        }
      });

      window.addEventListener('offline', () => {
        this.isOnline = false;
      });

      // Check initial online status
      this.isOnline = navigator.onLine;
    }
  }

  /**
   * Initialize user activity monitoring to optimize token refresh
   */
  private initializeUserActivityMonitoring(): void {
    if (typeof window !== 'undefined') {
      const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      
      const updateActivity = () => {
        this.lastUserActivity = Date.now();
      };

      // Add event listeners for user activity
      activityEvents.forEach(event => {
        window.addEventListener(event, updateActivity, { passive: true });
      });

      // Start activity monitoring timer
      this.activityTimer = setInterval(() => {
        const timeSinceActivity = Date.now() - this.lastUserActivity;
        
        // If user has been inactive for too long, reduce token refresh frequency
        if (timeSinceActivity > this.USER_ACTIVITY_TIMEOUT) {
          // Stop frequent checks but keep basic keep-alive
          if (this.refreshTokenTimer) {
            clearInterval(this.refreshTokenTimer);
            this.refreshTokenTimer = null;
            
            // Set up less frequent checking for inactive users
            this.refreshTokenTimer = setInterval(() => {
              if (this.isOnline && this.authState.isAuthenticated) {
                this.performTokenValidation();
              }
            }, 2 * 60 * 1000); // Check every 2 minutes instead of 20 seconds
          }
        }
      }, 5 * 60 * 1000); // Check activity every 5 minutes
    }
  }

  /**
   * Initialize token management system
   */
  private initializeTokenManagement(): void {
    // Start token management if user is already authenticated
    if (this.authState.isAuthenticated && this.authState.token) {
      this.startTokenManagement();
    }

    // Listen for visibility changes to handle app switching
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && this.authState.isAuthenticated) {
          console.log('👁️ App became visible - performing non-disruptive token check');
          // Use gentle validation that won't trigger refresh cycles
          this.performGentleTokenValidation();
        }
      });
    }

    // Listen for focus events  
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', () => {
        if (this.authState.isAuthenticated) {
          console.log('🎯 App gained focus - performing non-disruptive token check');
          // Use gentle validation that won't trigger refresh cycles
          this.performGentleTokenValidation();
        }
      });
    }
  }

  /**
   * Start automatic token management with enhanced refresh logic
   */
  private startTokenManagement(): void {
    
    // Clear existing timers
    this.stopTokenManagement();

    // Start frequent keep-alive pings
    this.keepAliveTimer = setInterval(() => {
      if (this.isOnline) {
        this.performKeepAlive();
      }
    }, this.KEEP_ALIVE_INTERVAL);

    // Start regular token validation checks
    this.refreshTokenTimer = setInterval(() => {
      if (this.isOnline && this.authState.isAuthenticated) {
        this.performTokenValidation();
      }
    }, this.TOKEN_CHECK_INTERVAL);

    // Calculate when to refresh token (if we know expiry time)
    if (this.tokenExpiryTime) {
      const timeUntilRefresh = this.tokenExpiryTime - Date.now() - this.REFRESH_BUFFER_TIME;
      if (timeUntilRefresh > 0) {
        this.refreshTimer = setTimeout(() => {
          if (this.isOnline) {
            this.performTokenRefresh();
          }
        }, timeUntilRefresh);
      } else {
        // Token is close to expiry or expired, refresh immediately
        if (this.isOnline) {
          this.performTokenRefresh();
        }
      }
    } else {
      // If we don't know expiry time, estimate based on Directus default (15 minutes)
      const estimatedExpiry = Date.now() + (15 * 60 * 1000); // 15 minutes
      this.tokenExpiryTime = estimatedExpiry;
      this.persistAuth(); // Save the estimated expiry
      
      const timeUntilRefresh = estimatedExpiry - Date.now() - this.REFRESH_BUFFER_TIME;
      if (timeUntilRefresh > 0) {
        this.refreshTimer = setTimeout(() => {
          if (this.isOnline) {
            this.performTokenRefresh();
          }
        }, timeUntilRefresh);
      } else {
        // Token expires very soon, refresh immediately
        if (this.isOnline) {
          this.performTokenRefresh();
        }
      }
    }

    // Add proactive refresh timer (earlier than main refresh for redundancy)
    if (this.tokenExpiryTime) {
      const timeUntilProactiveRefresh = this.tokenExpiryTime - Date.now() - this.PROACTIVE_REFRESH_TIME;
      if (timeUntilProactiveRefresh > 0 && timeUntilProactiveRefresh < this.tokenExpiryTime - Date.now() - this.REFRESH_BUFFER_TIME) {
        setTimeout(() => {
          if (this.isOnline && this.authState.isAuthenticated) {
            // Proactive token refresh triggered - background operation
            // Ensure this is treated as a background refresh to prevent UI disruption
            this.performTokenRefresh();
          }
        }, timeUntilProactiveRefresh);
      }
    }
  }

  /**
   * Stop automatic token management
   */
  private stopTokenManagement(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
    if (this.refreshTokenTimer) {
      clearInterval(this.refreshTokenTimer);
      this.refreshTokenTimer = null;
    }
    if (this.activityTimer) {
      clearInterval(this.activityTimer);
      this.activityTimer = null;
    }
    if (this.gracefulLogoutTimer) {
      clearTimeout(this.gracefulLogoutTimer);
      this.gracefulLogoutTimer = null;
    }
    this.isRefreshing = false;
    this.refreshPromise = null;
  }

  /**
   * Perform keep-alive ping to maintain session
   */
  private async performKeepAlive(): Promise<void> {
    if (!this.authState.isAuthenticated || !this.authState.token || !this.isOnline) {
      return;
    }

    try {
      
      // Ensure token is set
      directus.setToken(this.authState.token);
      
      // Simple ping to maintain session
      await directus.request(readMe());
      
      // Reset retry count on success
      this.retryCount = 0;
    } catch (error: any) {
      if (env.DEBUG) console.warn('⚠️ Keep-alive failed:', error?.message || 'Unknown error');
      
      // If keep-alive fails, try to refresh token
      if (error?.status === 401 || error?.status === 403) {
        await this.performTokenRefresh();
      }
    }
  }

  /**
   * Gentle token validation for standby/visibility scenarios
   * This version is non-disruptive and won't trigger refresh cycles
   */
  private async performGentleTokenValidation(): Promise<void> {
    if (!this.authState.isAuthenticated || !this.authState.token) {
      return;
    }

    // Skip if already refreshing to prevent conflicts
    if (this.isRefreshing) {
      console.log('🔕 Token validation skipped - refresh in progress');
      return;
    }

    // Throttle gentle validations to prevent rapid-fire requests
    const now = Date.now();
    if (now - this.lastGentleValidation < this.GENTLE_VALIDATION_THROTTLE) {
      console.log('🔕 Gentle validation throttled - too recent');
      return;
    }
    this.lastGentleValidation = now;

    try {
      console.log('🕊️ Performing gentle token validation (non-disruptive)');
      
      // Ensure token is set
      directus.setToken(this.authState.token);
      
      // Validate token by getting current user with timeout
      const user = await Promise.race([
        directus.request(readMe()),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 5000))
      ]);
      
      const token = await directus.getToken();
      
      if (user && token) {
        console.log('✅ Gentle token validation successful');
        
        // Update auth state if needed (silent update to avoid UI disruption)
        if (this.authState.user?.id !== user.id) {
          this.silentUpdateState({
            user,
            token,
            isAuthenticated: true,
            error: null
          });
          this.persistAuth();
        }
      }
    } catch (error: any) {
      console.warn('⚠️ Gentle token validation failed (non-critical):', error?.message || 'Unknown error');
      
      // For gentle validation, just log the issue - don't trigger refresh/logout
      // The regular token management system will handle any real issues
      if (error?.message?.includes('Timeout')) {
        console.log('🌐 Token validation timeout - likely network issue, will retry later');
      } else {
        console.log('🔕 Token validation failed gently - deferring to regular token management');
      }
      
      // Don't trigger refresh/logout cycles for standby scenarios
      // Let the background token management handle it naturally
    }
  }

  /**
   * Perform token validation (used for regular token management)
   */
  private async performTokenValidation(): Promise<void> {
    if (!this.authState.isAuthenticated || !this.authState.token) {
      return;
    }

    try {
      
      // Ensure token is set
      directus.setToken(this.authState.token);
      
      // Validate token by getting current user
      const user = await directus.request(readMe());
      const token = await directus.getToken();
      
      if (user && token) {
        
        // Update auth state if needed
        if (this.authState.user?.id !== user.id) {
          this.updateState({
            user,
            token,
            isAuthenticated: true,
            error: null
          });
          this.persistAuth();
        }
      }
    } catch (error: any) {
      if (env.DEBUG) console.warn('⚠️ Token validation failed:', error?.message || 'Unknown error');
      
      // Check if this is a token expiry that needs handling
      const isTokenExpired = error?.[0]?.extensions?.code === "TOKEN_EXPIRED" ||
                            error?.status === 401 || 
                            error?.status === 403;
      
      if (isTokenExpired) {
        // Token expired during validation - attempting refresh instead of logout
        
        // Don't call logout immediately during proactive refresh
        // Instead, try to refresh the token first
        try {
          await this.performTokenRefresh();
          console.log('✅ Token refresh after validation failure succeeded');
        } catch (refreshError) {
          console.warn('⚠️ Token refresh after validation failure failed:', refreshError);
          
          // Only logout if refresh also fails and we're not in the middle of a background refresh
          if (!this.isBackgroundRefreshing) {
            console.log('🚪 Token validation and refresh both failed - forcing logout');
            await this.forceLogout('Token expired and refresh failed during validation');
          } else {
            console.log('🔕 Background refresh in progress - deferring logout decision');
          }
        }
      }
    }
  }

  /**
   * Perform token refresh with retry logic and race condition protection
   */
  private async performTokenRefresh(): Promise<void> {
    if (!this.authState.isAuthenticated || !this.isOnline) {
      return;
    }

    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing) {
      return this.refreshPromise || Promise.resolve();
    }

    // Check if user is actively interacting - defer if needed
    if (this.shouldDeferTokenOperation()) {
      const delay = this.getOptimalTokenOperationDelay();
      console.log(`⏸️ Deferring token refresh for ${delay}ms due to user activity:`, {
        typing: this.userActivityState.isTyping,
        formFocused: this.userActivityState.isFormFocused,
        activityLevel: this.userActivityState.activityLevel
      });
      
      setTimeout(() => {
        if (this.authState.isAuthenticated && !this.isRefreshing) {
          this.performTokenRefresh();
        }
      }, delay);
      return;
    }

    // Mark as background refresh to use non-disruptive methods
    this.isBackgroundRefreshing = true;
    this.isRefreshing = true;
    // Starting non-disruptive background token refresh
    this.refreshPromise = this._performTokenRefreshInternal();

    try {
      await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.isBackgroundRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * Internal token refresh implementation with retry logic
   */
  private async _performTokenRefreshInternal(): Promise<void> {
    const maxRetries = this.MAX_RETRY_ATTEMPTS;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        
        // Try to refresh the token - prefer manual refresh if we have the token
        let refreshSuccess = false;
        
        // First, try manual refresh if we have a stored refresh token
        if (this.refreshToken) {
          // Attempting manual refresh with stored refresh token first
          try {
            refreshSuccess = await this.manualRefreshToken(this.refreshToken);
            if (refreshSuccess) {
              console.log('✅ Manual refresh successful');
              return; // Success, exit early
            }
          } catch (manualRefreshError: any) {
            console.log('❌ Manual refresh failed, trying SDK refresh:', manualRefreshError?.message);
          }
        }
        
        // If manual refresh failed or no token, try SDK refresh as fallback
        if (!refreshSuccess) {
          try {
            // Attempting SDK refresh as fallback
            await directus.refresh();
            refreshSuccess = true;
            console.log('✅ SDK refresh successful');
          } catch {
            console.log('❌ Both manual and SDK refresh failed');
            refreshSuccess = false;
          }
        }
        
        // If all refresh attempts failed, try credential-based re-authentication as final fallback
        if (!refreshSuccess) {
          const storedCreds = this.getStoredCredentials();
          if (storedCreds && !this.isReAuthenticating) {
            console.log('🔄 Attempting credential-based re-authentication as final fallback');
            try {
              this.isReAuthenticating = true;
              await this.silentReAuthentication(storedCreds.email, storedCreds.password);
              console.log('✅ Credential-based re-authentication successful');
              return; // Success, exit the retry loop
            } catch (reAuthError) {
              console.log('❌ Credential-based re-authentication failed:', reAuthError);
            } finally {
              this.isReAuthenticating = false;
            }
          }
          
          throw new Error('Session expired. Please log in again.');
        }
        
        // Get the new token and user info
        const user = await directus.request(readMe());
        const token = await directus.getToken();
        
        if (user && token) {
          
          // Calculate new expiry time (estimate 15 minutes from now)
          this.tokenExpiryTime = Date.now() + (15 * 60 * 1000);
          this.retryCount = 0; // Reset retry count on success
          
          // Silent update to avoid disrupting user interactions during background refresh
          this.silentUpdateState({
            user,
            token,
            isAuthenticated: true,
            error: null
          });
          
          // Cancel any pending graceful logout since refresh succeeded
          this.cancelGracefulLogout();
          
          // Persist the new token with updated expiry (silent)
          this.persistAuth();
          
          // Continue token management without full restart to avoid timer disruption
          this.updateTokenManagementTimers();
          return; // Success - exit retry loop
        }
      } catch (error: any) {
        console.error(`❌ Token refresh attempt ${attempt} failed:`, error);
        
        // Check if it's a network error, authentication error, or session expiry
        const isNetworkError = error.message?.includes('fetch') || 
                              error.message?.includes('NetworkError') ||
                              !this.isOnline;
        
        const isAuthError = error.status === 401 || error.status === 403;
        
        const isSessionExpired = error.message?.includes('Session expired') ||
                                error.message?.includes('Please log in again');
        
        if (isAuthError || isSessionExpired) {
          
          // Try one last fallback: check if we can re-authenticate with stored credentials
          const storedCredentials = this.getStoredCredentials();
          if (storedCredentials && !this.isReAuthenticating) {
            console.log('🔄 Attempting fallback re-authentication with stored credentials');
            try {
              this.isReAuthenticating = true;
              await this.silentReAuthentication(storedCredentials.email, storedCredentials.password);
              console.log('✅ Fallback re-authentication successful');
              return; // Success, exit the retry loop
            } catch (reAuthError) {
              console.warn('⚠️ Fallback re-authentication failed:', reAuthError);
            } finally {
              this.isReAuthenticating = false;
            }
          }
          
          console.log('🚪 All authentication recovery attempts failed');
          // Instead of immediate logout, set a flag for graceful logout
          this.scheduleGracefulLogout();
          return;
        }
        
        if (isNetworkError && attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY * attempt));
          continue;
        }
        
        if (attempt === maxRetries) {
          console.error('❌ All token refresh attempts failed');
          
          // If we still have some time left on current token, schedule another retry
          if (this.tokenExpiryTime && this.tokenExpiryTime > Date.now() + (60 * 1000)) {
            setTimeout(() => {
              if (this.authState.isAuthenticated && this.isOnline) {
                this.performTokenRefresh();
              }
            }, 30000);
          } else {
            console.log('🚪 Token refresh failed completely - forcing logout');
            await this.forceLogout('Token expired and refresh failed after multiple attempts');
          }
        }
      }
    }
  }

  /**
   * Update only the token-related timers without full restart
   * This prevents disruption during background token refresh
   */
  private updateTokenManagementTimers(): void {
    console.log('🔄 Updating token management timers (non-disruptive)');
    
    // Only clear and restart the refresh timer, keep keep-alive and validation running
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    // Calculate new refresh time based on updated expiry
    if (this.tokenExpiryTime) {
      const timeUntilRefresh = this.tokenExpiryTime - Date.now() - this.REFRESH_BUFFER_TIME;
      if (timeUntilRefresh > 0) {
        this.refreshTimer = setTimeout(() => {
          if (this.isOnline) {
            this.performTokenRefresh();
          }
        }, timeUntilRefresh);
        console.log(`⏰ Token refresh rescheduled in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`);
      }
    }
  }

  /**
   * Schedule a graceful logout that doesn't immediately disrupt user
   * Shows notification first, then logs out after delay
   */
  private scheduleGracefulLogout(): void {
    console.log('🕐 Scheduling graceful logout to avoid disrupting user interaction');
    
    // Clear any existing timer
    if (this.gracefulLogoutTimer) {
      clearTimeout(this.gracefulLogoutTimer);
    }

    // Notify user first without immediate action
    this.silentUpdateState({
      error: 'Session expired. Please complete your current action and refresh the page.'
    });

    // Schedule actual logout after a delay to allow user to finish current action
    this.gracefulLogoutTimer = setTimeout(async () => {
      console.log('⏰ Performing graceful logout after delay');
      await this.logout();
    }, 30000); // 30 second delay to let user finish current interaction
  }

  /**
   * Cancel graceful logout if token refresh succeeds
   */
  private cancelGracefulLogout(): void {
    if (this.gracefulLogoutTimer) {
      clearTimeout(this.gracefulLogoutTimer);
      this.gracefulLogoutTimer = null;
      console.log('✅ Graceful logout cancelled - token refresh succeeded');
    }
  }

  /**
   * Update user activity state to inform token refresh timing
   */
  public updateUserActivity(activity: {
    isTyping?: boolean;
    isFormFocused?: boolean;
    activityLevel?: 'idle' | 'light' | 'active' | 'intensive';
  }): void {
    this.userActivityState = {
      ...this.userActivityState,
      ...activity,
      lastActivity: Date.now()
    };
  }

  /**
   * Check if token operations should be deferred due to user activity
   */
  private shouldDeferTokenOperation(): boolean {
    const { isTyping, isFormFocused, activityLevel } = this.userActivityState;
    return isTyping || isFormFocused || activityLevel === 'intensive';
  }

  /**
   * Get optimal delay for token operations based on user activity
   */
  private getOptimalTokenOperationDelay(): number {
    const { isTyping, isFormFocused, activityLevel } = this.userActivityState;
    
    if (isTyping) return 5000; // Wait 5s if user is typing
    if (isFormFocused) return 3000; // Wait 3s if form is focused
    if (activityLevel === 'intensive') return 2000; // Wait 2s for intensive activity
    if (activityLevel === 'active') return 1000; // Wait 1s for active use
    return 0; // No delay for light activity or idle
  }

  /**
   * Helper method to construct proper Directus URLs without double slashes
   */
  private getDirectusUrl(path: string): string {
    const baseUrl = directusUrl.replace(/\/$/, ''); // Remove trailing slash
    const cleanPath = path.startsWith('/') ? path : `/${path}`; // Ensure leading slash
    return `${baseUrl}${cleanPath}`;
  }

  /**
   * Manual refresh token implementation as fallback when SDK refresh fails
   * This directly calls the Directus /auth/refresh endpoint with the stored refresh token
   */
  private async manualRefreshToken(_refreshToken: string): Promise<boolean> {
    try {
      // Performing token refresh using SDK refresh mechanism
      // Debug: refresh token available
      
      // Use SDK's refresh method instead of direct fetch for better security
      // Add timeout to prevent hanging requests
      await Promise.race([
        directus.refresh(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Token refresh timeout')), 10000))
      ]);
      
      // Check if we now have a valid token
      const token = directus.getToken();
      if (token) {
        console.log('✅ SDK refresh successful, updating internal state');
        
        // Update our internal state to match SDK state
        this.authState.token = token;
        this.tokenExpiryTime = Date.now() + (15 * 60 * 1000); // Default 15min expiry
        
        return true;
      } else {
        console.error('❌ SDK refresh did not provide new token');
        return false;
      }
    } catch (error: any) {
      console.error('❌ SDK refresh request failed:', error);
      return false;
    }
  }

  /**
   * Silent re-authentication that doesn't trigger full app reload
   * This is used during credential-based recovery to avoid disrupting user experience
   */
  private async silentReAuthentication(email: string, password: string): Promise<void> {
    console.log('🔕 Performing silent re-authentication to avoid app reload');
    
    try {
      // Don't update loading state during silent re-auth to avoid UI disruption
      const previousLoadingState = this.authState.isLoading;
      
      // Store credentials for future fallback use
      this.storeCredentials(email, password);
      
      // Authenticate with Directus
      const loginResult = await directus.login(email, password);
      
      // Get current user info
      const user = await directus.request(readMe());
      
      // Get the authentication token
      const token = await directus.getToken();

      // Calculate token expiry time (Directus default is 15 minutes)
      this.tokenExpiryTime = Date.now() + (15 * 60 * 1000);
      this.retryCount = 0; // Reset retry count on fresh login

      // Try to capture refresh token from login result
      if (loginResult && typeof loginResult === 'object' && 'refresh_token' in loginResult) {
        this.refreshToken = loginResult.refresh_token as string;
        console.log('🔑 Captured refresh token from silent re-auth');
      }

      // ✅ Use silent update to avoid triggering React remounts and provider reinitialization
      this.silentUpdateState({
        user,
        token,
        isAuthenticated: true,
        isLoading: previousLoadingState, // Preserve previous loading state
        error: null
      });

      // Persist the authentication with expiry time
      this.persistAuth();
      
      // Ensure token is set in directus client immediately
      console.log('🔑 Setting fresh token in directus client after silent re-auth');
      directus.setToken(token);
      
      // DON'T restart token management during silent re-auth to avoid disruption
      // The existing token management should continue running
      console.log('✅ Silent re-authentication completed without app disruption');
      
    } catch (error) {
      console.error('❌ Silent re-authentication failed:', error);
      throw error;
    }
  }
  
  /**
   * Store credentials securely (encrypted) for fallback re-authentication
   */
  private storeCredentials(email: string, password: string): void {
    try {
      // Simple encryption for credentials (in production, use more secure methods)
      const credentials = {
        email,
        password: btoa(password) // Base64 encoding (replace with proper encryption in production)
      };
      this.storedCredentials = { email, password };
      localStorage.setItem('directus_creds', JSON.stringify(credentials));
    } catch (error) {
      console.warn('Failed to store credentials securely:', error);
    }
  }

  /**
   * Get stored credentials for fallback authentication
   */
  private getStoredCredentials(): { email: string; password: string } | null {
    try {
      if (this.storedCredentials) {
        return this.storedCredentials;
      }
      
      const stored = localStorage.getItem('directus_creds');
      if (stored) {
        const credentials = JSON.parse(stored);
        return {
          email: credentials.email,
          password: atob(credentials.password) // Decode from base64
        };
      }
    } catch (error) {
      console.warn('Failed to retrieve stored credentials:', error);
    }
    return null;
  }

  /**
   * Clear stored credentials
   */
  private clearStoredCredentials(): void {
    try {
      this.storedCredentials = null;
      localStorage.removeItem('directus_creds');
    } catch (error) {
      console.warn('Failed to clear stored credentials:', error);
    }
  }

  /**
   * Login with email and password
   */
  async login(email: string, password: string): Promise<void> {
    this.updateState({ isLoading: true, error: null });

    try {
      // Store credentials for fallback re-authentication (before attempting login)
      this.storeCredentials(email, password);
      
      // Authenticate with Directus
      const loginResult = await directus.login(email, password);
      
      // Get current user info
      const user = await directus.request(readMe());
      
      // Get the authentication token
      const token = await directus.getToken();

      // Calculate token expiry time (Directus default is 15 minutes)
      this.tokenExpiryTime = Date.now() + (15 * 60 * 1000);
      this.retryCount = 0; // Reset retry count on fresh login

      // Try to capture refresh token from login result
      if (loginResult && typeof loginResult === 'object') {
        // Check multiple possible locations for the refresh token
        const refreshToken = (loginResult as any).refresh_token || 
                           (loginResult as any).data?.refresh_token ||
                           null;
        
        if (refreshToken) {
          this.refreshToken = refreshToken;
          console.log('🔑 Captured refresh token from login response');
        } else {
          console.log('🔑 No refresh token in login response - trying to get from SDK');
          // Try to get the refresh token from the SDK's internal storage
          try {
            // The SDK might store the refresh token internally, we can try to access it
            this.refreshToken = null; // We'll rely on SDK's internal refresh mechanism
            console.log('🔑 Will rely on SDK internal refresh token management');
          } catch {
            console.warn('⚠️ Could not access SDK refresh token');
            this.refreshToken = null;
          }
        }
      } else {
        console.log('🔑 Invalid login result format, will rely on SDK internal management');
        this.refreshToken = null;
      }

      this.updateState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });

      // Persist the authentication with expiry time
      this.persistAuth();
      
      // Ensure token is set in directus client immediately
      directus.setToken(token);
      
      // Start automatic token management
      this.startTokenManagement();
      
      // Authentication successful
    } catch (error) {
      console.error('❌ Directus authentication failed:', error);
      this.updateState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Authentication failed'
      });
      this.clearPersistedAuth();
      throw error;
    }
  }

  /**
   * Force logout when token refresh fails completely
   * This will dispatch Redux logout action and clear all auth state
   */
  private async forceLogout(reason: string): Promise<void> {
    console.log('🚪 Force logout triggered:', reason);
    
    // Dispatch Redux logout action if available
    if (this.storeDispatch) {
      try {
        // Import the logout action dynamically to avoid circular dependencies
        const { logoutUser } = await import('../store/slices/authSlice');
        console.log('📤 Dispatching Redux logout action');
        await this.storeDispatch(logoutUser() as any);
      } catch (error) {
        console.error('❌ Failed to dispatch Redux logout:', error);
      }
    }
    
    // Continue with normal logout process
    await this.logout();
  }

  /**
   * Logout and clear authentication state
   */
  async logout(): Promise<void> {
    try {
      await directus.logout();
    } catch (error) {
      console.warn('Logout warning:', error);
    }

    // Stop token management
    this.stopTokenManagement();

    this.updateState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null
    });

    this.clearPersistedAuth();
    this.clearStoredCredentials(); // Clear stored credentials on logout
    // Logged out successfully
  }

  /**
   * Check if user is currently authenticated
   */
  async checkAuth(): Promise<boolean> {
    // Prevent multiple simultaneous auth checks
    if (this.authState.isLoading) return this.authState.isAuthenticated;
    
    this.updateState({ isLoading: true });

    try {
      // If we have a persisted token, set it first
      if (this.authState.token) {
        directus.setToken(this.authState.token);
      }

      // Try to get current user
      const user = await directus.request(readMe());
      const token = await directus.getToken();

      if (user && token) {
        this.updateState({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });
        this.persistAuth();
        
        // Ensure token is set in directus client
        directus.setToken(token);
        
        // Start token management for existing session
        this.startTokenManagement();
        
        return true;
      }
    } catch (error) {
      console.warn('Auth check failed:', error);
    }

    this.updateState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null
    });
    this.clearPersistedAuth();
    return false;
  }

  /**
   * Validate current token without full authentication flow
   * Lightweight token validation for session checks
   */
  async validateCurrentToken(): Promise<boolean> {
    try {
      // If no token exists, return false
      if (!this.authState.token || !this.authState.isAuthenticated) {
        return false;
      }

      // Set the token for validation
      directus.setToken(this.authState.token);

      // Make a lightweight API call to validate token
      const user = await directus.request(readMe());
      
      return !!user; // Return true if user data is successfully retrieved
    } catch (error) {
      console.warn('🚨 Token validation failed:', error);
      
      // If it's a token expiration error, handle it
      if (this.isTokenExpiredError(error)) {
        console.log('🚪 Token expired during validation');
        return false;
      }
      
      // For other errors (network, etc.), don't invalidate the session immediately
      // Return false but don't logout - let the calling code decide
      return false;
    }
  }

  /**
   * Refresh authentication token
   */
  async refresh(): Promise<void> {
    try {
      await directus.refresh();
      const user = await directus.request(readMe());
      const token = await directus.getToken();

      this.updateState({
        user,
        token,
        isAuthenticated: true,
        error: null
      });
      
      // Persist the refreshed token
      this.persistAuth();
      
      // Restart token management
      this.startTokenManagement();
      
    } catch (error) {
      console.error('Token refresh failed:', error);
      await this.logout();
      throw error;
    }
  }

  /**
   * Get the current preferred token
   */
  getPreferredToken(): string | null {
    return this.authState.token;
  }

  /**
   * Update user state with new data (for profile updates)
   */
  updateUserState(userData: any): void {
    this.updateState({
      ...this.authState,
      user: { ...this.authState.user, ...userData }
    });
    this.persistAuth();
  }

  /**
   * Force immediate token refresh (public method)
   */
  async forceTokenRefresh(): Promise<boolean> {
    if (!this.authState.isAuthenticated) {
      if (env.DEBUG) console.warn('⚠️ Cannot refresh token: user not authenticated');
      return false;
    }

    try {
      await this.performTokenRefresh();
      return this.authState.isAuthenticated;
    } catch (error) {
      console.error('❌ Force token refresh failed:', error);
      return false;
    }
  }

  /**
   * Get token expiry information
   */
  getTokenInfo(): {
    hasToken: boolean;
    isExpired: boolean;
    expiresAt: Date | null;
    timeUntilExpiry: number | null;
    willExpireSoon: boolean;
  } {
    const hasToken = !!this.authState.token;
    const expiresAt = this.tokenExpiryTime ? new Date(this.tokenExpiryTime) : null;
    const timeUntilExpiry = this.tokenExpiryTime ? this.tokenExpiryTime - Date.now() : null;
    const isExpired = timeUntilExpiry !== null && timeUntilExpiry <= 0;
    const willExpireSoon = timeUntilExpiry !== null && timeUntilExpiry <= this.REFRESH_BUFFER_TIME;

    return {
      hasToken,
      isExpired,
      expiresAt,
      timeUntilExpiry,
      willExpireSoon
    };
  }

  /**
   * Check if session recovery is needed on app startup
   */
  async recoverSession(): Promise<boolean> {
    if (!this.authState.isAuthenticated || !this.authState.token) {
      return false;
    }

    try {
      
      const tokenInfo = this.getTokenInfo();
      
      if (tokenInfo.isExpired) {
        await this.performTokenRefresh();
        return this.authState.isAuthenticated;
      } else if (tokenInfo.willExpireSoon) {
        await this.performTokenRefresh();
        return this.authState.isAuthenticated;
      } else {
        // Token is still valid, just validate it
        await this.performTokenValidation();
        return this.authState.isAuthenticated;
      }
    } catch (error) {
      console.error('❌ Session recovery failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const authManager = DirectusAuthManager.getInstance();

// Enhanced helper function with automatic token refresh capabilities and retry mechanism
export const ensureAuthenticated = async (retryCount: number = 0): Promise<string> => {
  const maxRetries = 3;
  const authState = authManager.getState();
  
  
  if (!authState.isAuthenticated) {
    console.error('❌ User not authenticated');
    throw new Error('User not authenticated');
  }

  // Check token expiry status
  const tokenInfo = authManager.getTokenInfo();
  
  if (tokenInfo.isExpired) {
    const refreshSuccess = await authManager.forceTokenRefresh();
    if (!refreshSuccess) {
      throw new Error('Token expired and refresh failed');
    }
  } else if (tokenInfo.willExpireSoon) {
    // Don't wait for refresh, let it happen in background
    authManager.forceTokenRefresh().catch(error => {
      if (env.DEBUG) console.warn('⚠️ Background token refresh failed:', error);
    });
  }
  
  // Get the current token (potentially refreshed)
  const currentState = authManager.getState();
  const preferredToken = currentState.token;
  
  if (!preferredToken) {
    console.error('❌ No token available after authentication checks');
    throw new Error('No authentication token available');
  }
  
  // FORCE TOKEN CONSISTENCY: Always ensure directus client uses the preferred token
  const currentDirectusToken = await directus.getToken();
  if (currentDirectusToken !== preferredToken) {
    directus.setToken(preferredToken);
  }
  
  // VALIDATION: Test the token with a quick API call
  try {
    await directus.request(readMe());
    return preferredToken;
  } catch (error: any) {
    if (env.DEBUG) console.warn('⚠️ Token validation failed:', error?.message || 'Unknown error', `(retry ${retryCount}/${maxRetries})`);
    
    // If token is invalid, trigger refresh directly without recursion
    if (error?.status === 401 || error?.status === 403) {
      if (retryCount < maxRetries) {
        
        try {
          // Use direct token refresh instead of checkAuth to avoid recursion
          const refreshSuccess = await authManager.forceTokenRefresh();
          if (refreshSuccess) {
            // Wait a moment for the token to be properly set
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Retry the authentication check
            return await ensureAuthenticated(retryCount + 1);
          }
          throw new Error('Token refresh succeeded but authentication still failed');
        } catch (refreshError: any) {
          console.error('❌ Token refresh failed:', refreshError?.message || 'Unknown error');
          
          // If we still have retries left, try once more
          if (retryCount < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
            return await ensureAuthenticated(retryCount + 1);
          }
          
          throw new Error('Authentication token expired and refresh failed after multiple attempts');
        }
      } else {
        console.error('❌ Maximum retry attempts reached, authentication failed');
        throw new Error('Authentication failed after maximum retry attempts');
      }
    } else {
      // For non-auth errors, throw immediately
      throw error;
    }
  }
};

// Enhanced API request wrapper with automatic retry logic
const executeWithRetry = async <T>(
  operation: () => Promise<T>, 
  operationName: string = 'API operation',
  maxRetries: number = 2
): Promise<T> => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Ensure authentication before each attempt
      await ensureAuthenticated();
      
      // Execute the operation
      const result = await operation();
      
      return result;
    } catch (error: any) {
      console.error(`❌ ${operationName} failed on attempt ${attempt + 1}:`, error?.message || 'Unknown error');
      
      // Enhanced error classification for better handling
      const isAuthError = error?.status === 401 || error?.status === 403;
      const isNetworkError = error?.message?.includes('fetch') || 
                            error?.message?.includes('NetworkError') ||
                            error?.message?.includes('Failed to fetch') ||
                            !navigator.onLine;
      
      // Network connectivity 404s (server unreachable) - key fix for the reported issue
      const isNetworkConnectivity404 = error?.status === 404 && (
        error?.message?.includes('fetch') ||
        error?.message?.includes('ERR_NETWORK') ||
        error?.message?.includes('ERR_CONNECTION') ||
        !navigator.onLine ||
        error?.code === 'NETWORK_ERROR'
      );
      
      // Distinguish between legitimate 404s (resource not found) and connectivity 404s
      const isConnectivity404 = isNetworkConnectivity404 && !error?.response?.data;
      
      console.log(`🔍 Error analysis for ${operationName}:`, {
        status: error?.status,
        isAuthError,
        isNetworkError,
        isConnectivity404,
        online: navigator.onLine,
        errorMessage: error?.message?.substring(0, 100)
      });
      
      if ((isAuthError || isNetworkError || isConnectivity404) && attempt < maxRetries) {
        console.log(`🔄 Retrying ${operationName} (attempt ${attempt + 2}/${maxRetries + 1})...`);
        
        // Different retry strategies based on error type
        if (isConnectivity404) {
          // For connectivity 404s, use longer delays and check online status
          console.log('🌐 Network connectivity 404 detected - checking connection...');
          
          if (!navigator.onLine) {
            console.log('📱 Device is offline - waiting for connection...');
            // Wait for online event or timeout
            const waitForOnline = new Promise<void>((resolve) => {
              const onlineHandler = () => {
                window.removeEventListener('online', onlineHandler);
                console.log('🌐 Connection restored');
                resolve();
              };
              window.addEventListener('online', onlineHandler);
              // Timeout after 10 seconds
              setTimeout(() => {
                window.removeEventListener('online', onlineHandler);
                resolve();
              }, 10000);
            });
            await waitForOnline;
          }
          
          // Extended delay for connectivity issues
          const delay = Math.min(2000 * Math.pow(2, attempt), 10000); // Max 10 seconds
          console.log(`⏳ Waiting ${delay}ms before connectivity retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          
        } else {
          // Standard retry delay for other errors
          const delay = Math.min(1000 * Math.pow(2, attempt), 5000); // Max 5 seconds
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        
        // For auth errors, force token refresh
        if (isAuthError) {
          try {
            console.log('🔑 Refreshing token due to auth error...');
            await authManager.forceTokenRefresh();
          } catch (refreshError) {
            if (env.DEBUG) console.warn('⚠️ Token refresh failed during retry:', refreshError);
          }
        }
        
        continue; // Retry the operation
      }
      
      // If we've exhausted retries or it's not a retryable error, handle gracefully
      if (attempt === maxRetries) {
        console.error(`❌ ${operationName} failed after ${maxRetries + 1} attempts`);
        
        // Try to handle known network errors gracefully before throwing
        if (networkErrorHandler.handleKnown404Error(error, operationName)) {
          console.log(`🌐 Network 404 handled gracefully for ${operationName} - preventing page reload`);
          // Return a default/empty result instead of throwing to prevent app crashes
          return null; // Operations should handle null returns gracefully
        }
      }
      throw error;
    }
  }
  
  // This should never be reached, but TypeScript requires it
  throw new Error(`${operationName} failed after all retry attempts`);
};

// Utility functions for data transformation
/**
 * Transform customer_visits database record to frontend Visit interface
 */
function transformCustomerVisitToVisit(visit: any): any {
  // Enhanced extraction of customer name from visit_notes with multiple patterns
  let customerNameFromNotes = '';
  
  if (visit.visit_notes) {
    // Look for "Visit for" pattern and extract everything until a stopping point
    const match = visit.visit_notes.match(/Visit for (.+?)(?:\.|\s+Customer|\s+interaction|\s+follow)/i);
    if (match && match[1] && match[1].trim() && match[1].trim() !== visit.customer_tambak_pelanggan) {
      customerNameFromNotes = match[1].trim();
      // Clean up trailing punctuation
      customerNameFromNotes = customerNameFromNotes.replace(/[,\s]+$/, '');
    } else {
      // Fallback: try to get everything after "Visit for" until end of sentence or obvious break
      const fallbackMatch = visit.visit_notes.match(/Visit for ([^.]+?)(?:\.|$)/i);
      if (fallbackMatch && fallbackMatch[1] && fallbackMatch[1].trim() && fallbackMatch[1].trim() !== visit.customer_tambak_pelanggan) {
        customerNameFromNotes = fallbackMatch[1].trim();
        // Clean up if it contains common stop words at the end
        customerNameFromNotes = customerNameFromNotes.replace(/\s+(Customer|interaction|follow-up|follow|visit).*$/i, '');
        customerNameFromNotes = customerNameFromNotes.replace(/[,\s]+$/, '');
      }
    }
  }
  
  // Debug log to help with troubleshooting
  if (import.meta.env?.DEV) {
    console.log('🔍 Customer name extraction debug:', {
      visit_notes: visit.visit_notes,
      extracted_name: customerNameFromNotes,
      customer_tambak_pelanggan: visit.customer_tambak_pelanggan,
      final_name: customerNameFromNotes || visit.customer_tambak_pelanggan || 'Unknown Customer'
    });
  }
  
  return {
    id: visit.id,
    employee_id: visit.user_created || visit.PIC, // Use PIC field
    customer_no: visit.customer_tambak_pelanggan,
    status: visit.status,
    start_time: visit.visit_date,
    end_time: visit.end_time,
    location_lat: visit.visit_coordinates?.lat,
    location_lng: visit.visit_coordinates?.lng,
    notes: visit.visit_notes,
    customer_satisfaction: visit.satisfaction_rating,
    revenue_generated: null, // Not available in customer_visits
    
    // Computed/derived fields for UI compatibility - prioritize extracted name
    customer_name: customerNameFromNotes || visit.customer_tambak_pelanggan || 'Unknown Customer',
    customer_address: visit.visit_location || '', 
    latitude: visit.visit_coordinates?.lat, // Alias for compatibility
    longitude: visit.visit_coordinates?.lng, // Alias for compatibility
    satisfaction_rating: visit.satisfaction_rating, // Direct mapping
    revenue_potential: null, // Not available
    customer_tambak_id: visit.customer_tambak_pelanggan, // Alias for compatibility
    created_at: visit.date_created || visit.created_at,
    updated_at: visit.date_updated || visit.updated_at,
    Progress: visit.Progress
  };
}

// Utility functions for common operations
export const directusOperations = {
  // User operations (using directus_users directly)

  // ============================================================================
  // SIMPLIFIED USER OPERATIONS - Using directus_users directly
  // ============================================================================
  
  /**
   * Get current user profile - simplified to use directus_users directly
   */
  async getCurrentUserProfile() {
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');
    
    return currentUser;
  },

  /**
   * Update current user profile fields
   */
  async updateCurrentUserProfile(data: { 
    first_name?: string; 
    last_name?: string; 
    title?: string;
    location?: string;
    description?: string;
  }) {
    await ensureAuthenticated();
    
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');
    
    
    const result = await directus.request(updateItem('directus_users', currentUser.id, {
      ...data,
      modified_on: getWIBTimeForDirectus()
    }));
    
    // Update auth manager state with new user data
    authManager.updateUserState({ ...currentUser, ...data });
    
    return result;
  },

  // LEGACY EMPLOYEE OPERATIONS - Keep for backward compatibility but mark deprecated
  async getCurrentEmployeeProfile() {
    if (env.DEBUG) console.warn('⚠️ getCurrentEmployeeProfile is deprecated. Use getCurrentUserProfile instead.');
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');
    
    // Using directus_users directly - no separate employee table needed
    
    // Fallback: convert directus_user to employee format
    return {
      id: currentUser.id,
      employee_id: currentUser.id,
      full_name: `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.email,
      first_name: currentUser.first_name || '',
      last_name: currentUser.last_name || '',
      email: currentUser.email,
      department_id: null,
      daily_target: 5, // Default target
      is_active: currentUser.status === 'active',
      created_at: currentUser.created_on,
      updated_at: currentUser.modified_on
    };
  },

  async updateCurrentEmployeeProfile(data: { daily_target?: number; department_id?: number }) {
    if (env.DEBUG) console.warn('⚠️ updateCurrentEmployeeProfile is deprecated. Use updateCurrentUserProfile instead.');
    
    // For backward compatibility, just update basic user info
    const updateData: any = {};
    if (data.daily_target) {
      updateData.description = `Daily target: ${data.daily_target}`;
    }
    
    return this.updateCurrentUserProfile(updateData);
  },

  /**
   * Helper to extract daily target from user description field
   */
  extractDailyTargetFromUser(user: any): number {
    if (user.description && typeof user.description === 'string') {
      const match = user.description.match(/daily target:\s*(\d+)/i);
      if (match) {
        return parseInt(match[1], 10);
      }
    }
    return 5; // Default daily target
  },

  /**
   * Get dashboard data using directus_users directly
   */
  async getDashboardData() {
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');
    

    // Get visits and stats - visits are now linked to directus_user directly
    const [recentVisits, activeVisit] = await Promise.all([
      this.getVisits(5),
      this.getActiveVisit()
    ]);

    // Calculate basic stats
    const today = getWIBTime().split('T')[0];
    const todayVisits = recentVisits.filter(visit => 
      visit.created_at.startsWith(today)
    );

    // Extract daily target from user description or use default
    const dailyTarget = this.extractDailyTargetFromUser(currentUser);

    return {
      profile: {
        id: currentUser.id,
        employee_id: currentUser.id,
        full_name: `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.email,
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
        email: currentUser.email,
        department_id: null,
        daily_target: dailyTarget,
        is_active: currentUser.status === 'active',
        created_at: currentUser.created_on,
        updated_at: currentUser.modified_on
      },
      stats: {
        daily_visits: todayVisits.length,
        daily_target: dailyTarget,
        completion_rate: todayVisits.length > 0 ? 
          (todayVisits.filter(v => v.status === 'completed').length / todayVisits.length) * 100 : 0,
        total_duration: todayVisits.reduce((sum, v) => sum + (v.duration || 0), 0),
        evidence_count: 0, // Would need to calculate from evidence
        last_visit: recentVisits[0]?.created_at
      },
      recent_visits: recentVisits,
      active_visit: activeVisit,
      has_active_visit: !!activeVisit
    };
  },

  async getEmployeeStats() {
    const dashboardData = await this.getDashboardData();
    return dashboardData.stats;
  },

  async getCurrentUser() {
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');
    
    return currentUser;
  },

  // ============================================================================
  // NEW CUSTOMER VISITS OPERATIONS - Using emp_visits table
  // ============================================================================
  
  /**
   * Get customer visits for current user
   */
  async getCustomerVisits(limit = 50) {
    await ensureAuthenticated();
    
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');
    
    
    try {
      const visits = await directus.request(readItems('emp_visits', {
        // Temporarily remove user filtering to avoid permission issues
        // filter: { user_created: { _eq: currentUser.id } },
        limit,
        sort: ['-date_created'],
        fields: [
          'id',
          'customer_tambak_pelanggan',
          'visit_date',
          'visit_location',
          'visit_coordinates',
          'visit_purpose',
          'visit_notes',
          'PIC',
          'status',
          'date_created'
        ]
      }));
      
      return visits.map(visit => this.processCustomerVisitWithData(visit));
    } catch (error: any) {
      console.error('❌ Error getting customer visits:', error);
      
      if (error?.status === 403) {
        if (env.DEBUG) console.warn('⚠️ 403 Forbidden - User may not have permission to access emp_visits');
        return [];
      }
      
      if (error?.status === 401) {
        if (env.DEBUG) console.warn('⚠️ 401 Unauthorized - Token may be expired, attempting refresh');
        try {
          // Use enhanced authentication check with retry logic
          await ensureAuthenticated();
          const retryVisits = await directus.request(readItems('emp_visits', {
            limit,
            sort: ['-date_created'],
            fields: [
              'id',
              'customer_tambak_pelanggan',
              'visit_date',
              'visit_location',
              'visit_coordinates',
              'visit_purpose',
              'visit_notes',
              'PIC',
              'status',
              'date_created'
            ]
          }));
          return retryVisits.map(visit => this.processCustomerVisitWithData(visit));
        } catch (retryError: any) {
          console.error('❌ Retry after authentication recovery failed:', retryError);
          return [];
        }
      }
      
      return [];
    }
  },

  /**
   * Get active customer visit for current user
   */
  async getActiveCustomerVisit() {
    await ensureAuthenticated();
    
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');
    
    
    try {
      const result = await directus.request(readItems('emp_visits', {
        filter: { 
          status: { _in: ['active', 'scheduled'] }
        },
        limit: 1,
        fields: [
          'id',
          'customer_tambak_pelanggan',
          'visit_date',
          'visit_location',
          'visit_coordinates',
          'visit_purpose',
          'visit_notes',
          'PIC',
          'status'
        ]
      }));
      
      const visit = result[0] ? this.processCustomerVisitWithData(result[0]) : null;
      return { data: visit };
    } catch (error: any) {
      console.error('❌ Error getting active customer visit:', error);
      
      if (error?.status === 403) {
        if (env.DEBUG) console.warn('⚠️ 403 Forbidden - User may not have permission to access emp_visits');
        return { data: null };
      }
      
      if (error?.status === 401) {
        if (env.DEBUG) console.warn('⚠️ 401 Unauthorized - Token may be expired, attempting refresh');
        try {
          await authManager.forceTokenRefresh();
          const retryResult = await directus.request(readItems('emp_visits', {
            filter: { 
              status: { _in: ['active', 'scheduled'] }
            },
            limit: 1,
            fields: [
              'id',
              'customer_tambak_pelanggan',
              'visit_date',
              'visit_location',
              'visit_coordinates',
              'visit_purpose',
              'visit_notes',
              'PIC',
              'status'
            ]
          }));
          const visit = retryResult[0] ? this.processCustomerVisitWithData(retryResult[0]) : null;
          return { data: visit };
        } catch (retryError: any) {
          console.error('❌ Retry after token refresh failed:', retryError);
          return { data: null };
        }
      }
      
      return { data: null };
    }
  },

  /**
   * Get today's schedule for current user (scheduled visits for today)
   */
  async getTodaySchedule() {
    await ensureAuthenticated();
    
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');
    
    
    // Get today's date in YYYY-MM-DD format
    const todayStr = getWIBTime().split('T')[0];
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = getWIBTime().split('T')[0]; // For next day scheduling
    
    try {
      // Step 1: Get customer visits data
      const visits = await directus.request(readItems('emp_visits', {
        filter: { 
          _and: [
            { PIC: { _eq: currentUser.id } },
            { visit_date: { _gte: todayStr + 'T00:00:00' } },
            { visit_date: { _lt: tomorrowStr + 'T00:00:00' } },
            { status : { _eq: 'planned'}}
          ]
        },
        sort: ['visit_date'],
        fields: [
          'id',
          'PIC',
          'customer_tambak_pelanggan',
          'visit_location',
          'visit_coordinates',
          'visit_purpose',
          'visit_date',
          'status'
        ]
      }));
      
      
      // Step 2: Get unique customer numbers for lookup
      const customerNumbers = [...new Set(visits.map(visit => visit.customer_tambak_pelanggan).filter(Boolean))];
      
      // Step 3: Lookup customer names from Customer_Tambak
      let customerLookup: Record<string, string> = {};
      if (customerNumbers.length > 0) {
        try {
          const customers = await directus.request(readItems('Customer_Tambak', {
            filter: {
              No_pelanggan: { _in: customerNumbers }
            },
            fields: ['No_pelanggan', 'Nama_Pelanggan_Tambak']
          }));
          
          
          // Create lookup map: No_pelanggan -> Nama_Pelanggan_Tambak
          customerLookup = customers.reduce((acc: Record<string, string>, customer: any) => {
            if (customer.No_pelanggan && customer.Nama_Pelanggan_Tambak) {
              acc[customer.No_pelanggan] = customer.Nama_Pelanggan_Tambak;
            }
            return acc;
          }, {});
          
        } catch (lookupError: any) {
          if (env.DEBUG) console.warn('⚠️ Customer lookup failed:', lookupError);
          
          if (lookupError?.response?.status === 403) {
            if (env.DEBUG) console.warn('⚠️ 403 Forbidden - User may not have permission to access Customer_Tambak table');
            if (env.DEBUG) console.warn('⚠️ Continuing without customer names - will show customer numbers only');
          } else if (lookupError?.response?.status === 404) {
            if (env.DEBUG) console.warn('⚠️ 404 Not Found - Customer_Tambak table may not exist or accessible');
          } else {
            if (env.DEBUG) console.warn('⚠️ Unexpected error during customer lookup:', lookupError?.message);
          }
          
          // Continue without customer names - graceful degradation
        }
      }
      
      // Step 4: Transform data with customer names
      return visits.map((visit: any): ScheduleItem => {
        const customerNumber = visit.customer_tambak_pelanggan || 'Unknown';
        const customerName = customerLookup[customerNumber] || '';
        const displayName = customerName 
          ? `${customerNumber}, ${customerName}`
          : customerNumber;
        

        return {
          id: visit.id,
          customer_name: displayName,
          customer_address: visit.visit_location || '', // Gunakan visit_location sebagai address
          customer_phone: '', // Not available in current schema
          customer_email: '', // Not available in current schema
          visit_type: 'sales', // Default since not available in current schema
          visit_purpose: visit.visit_purpose || '',
          start_time: visit.visit_date,
          status: visit.status,
          latitude: visit.visit_coordinates?.lat || 0,
          longitude: visit.visit_coordinates?.lng || 0,
          location_accuracy: undefined, // Not available in current schema
          time_display: new Date(visit.visit_date).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }),
          user_id: visit.PIC, // Map PIC to user_id for compatibility
          customer_id: undefined, // Not available in current schema
          duration: undefined, // Not available in current schema
          notes: undefined, // Not available in current schema
          internal_notes: undefined, // Not available in current schema
          satisfaction_rating: undefined, // Not available in current schema
          revenue_potential: undefined, // Not available in current schema
          actual_revenue: undefined, // Not available in current schema
          next_action: undefined, // Not available in current schema
          follow_up_date: undefined, // Not available in current schema
          tags: undefined, // Not available in current schema
          weather_condition: undefined, // Not available in current schema
          travel_distance: undefined, // Not available in current schema
          travel_time: undefined, // Not available in current schema
          end_time: undefined, // Not available in current schema
          created_at: undefined, // Not available in current schema
          updated_at: undefined // Not available in current schema
        };
      });
    } catch (error: any) {
      console.error('❌ Error getting today\'s schedule:', error);
      
      if (error?.status === 403) {
        if (env.DEBUG) console.warn('⚠️ 403 Forbidden - User may not have permission to access emp_visits');
        return [];
      }
      
      if (error?.status === 401) {
        if (env.DEBUG) console.warn('⚠️ 401 Unauthorized - Token may be expired, attempting refresh');
        try {
          await authManager.forceTokenRefresh();
          
          // Retry with same logic as main function
          const visits = await directus.request(readItems('emp_visits', {
            filter: { 
              _and: [
                { PIC: { _eq: currentUser.id } },
                { visit_date: { _gte: todayStr + 'T00:00:00' } },
                { visit_date: { _lt: tomorrowStr + 'T00:00:00' } }
              ]
            },
            sort: ['visit_date'],
            fields: [
              'id',
              'PIC',
              'customer_tambak_pelanggan',
              'visit_location',
              'visit_coordinates',
              'visit_purpose',
              'visit_date',
              'status'
            ]
          }));
          
          // Lookup customer names
          const customerNumbers = [...new Set(visits.map(visit => visit.customer_tambak_pelanggan).filter(Boolean))];
          let customerLookup: Record<string, string> = {};
          
          if (customerNumbers.length > 0) {
            try {
              const customers = await directus.request(readItems('Customer_Tambak', {
                filter: {
                  No_pelanggan: { _in: customerNumbers }
                },
                fields: ['No_pelanggan', 'Nama_Pelanggan_Tambak']
              }));
              
              customerLookup = customers.reduce((acc: Record<string, string>, customer: any) => {
                if (customer.No_pelanggan && customer.Nama_Pelanggan) {
                  acc[customer.No_pelanggan] = customer.Nama_Pelanggan;
                }
                return acc;
              }, {});
            } catch (lookupError) {
              if (env.DEBUG) console.warn('⚠️ Customer lookup failed on retry:', lookupError);
            }
          }
          
          return visits.map((visit: any): ScheduleItem => {
            const customerNumber = visit.customer_tambak_pelanggan || 'Unknown';
            const customerName = customerLookup[customerNumber] || '';
            const displayName = customerName 
              ? `${customerNumber}, ${customerName}`
              : customerNumber;

            return {
              id: visit.id,
              customer_name: displayName,
              customer_address: visit.visit_location || '',
              customer_phone: '',
              customer_email: '',
              visit_type: 'sales',
              visit_purpose: visit.visit_purpose || '',
              start_time: visit.visit_date,
              status: visit.status,
              latitude: visit.visit_coordinates?.lat || 0,
              longitude: visit.visit_coordinates?.lng || 0,
              location_accuracy: undefined,
              time_display: new Date(visit.visit_date).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }),
              user_id: visit.PIC,
              customer_id: undefined,
              duration: undefined,
              notes: undefined,
              internal_notes: undefined,
              satisfaction_rating: undefined,
              revenue_potential: undefined,
              actual_revenue: undefined,
              next_action: undefined,
              follow_up_date: undefined,
              tags: undefined,
              weather_condition: undefined,
              travel_distance: undefined,
              travel_time: undefined,
              end_time: undefined,
              created_at: undefined,
              updated_at: undefined
            };
          });
        } catch (retryError: any) {
          console.error('❌ Retry after token refresh failed:', retryError);
          return [];
        }
      }
      
      return [];
    }
  },

  /**
   * Create new customer visit
   */
  async createCustomerVisit(visitData: {
    customer_id?: number;
    customer_name: string;
    customer_address: string;
    customer_phone?: string;
    customer_email?: string;
    visit_type: 'sales' | 'service' | 'follow_up' | 'demo' | 'support';
    visit_purpose?: string;
    latitude: number;
    longitude: number;
    location_accuracy?: number;
    notes?: string;
    scheduled_time?: string;
  }) {
    await ensureAuthenticated();
    
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');
    
    
    const now = getWIBTimeForDirectus();
    const isScheduled = visitData.scheduled_time && new Date(visitData.scheduled_time) > new Date();
    
    return directus.request(createItem('emp_visits', {
      ...visitData,
      user_id: currentUser.id,
      start_time: visitData.scheduled_time || now,
      status: isScheduled ? 'scheduled' : 'active',
      created_at: now,
      updated_at: now,
      user_created: currentUser.id
    }));
  },

  /**
   * Update customer visit
   */
  async updateCustomerVisit(visitId: string, data: {
    end_time?: string;
    duration?: number;
    status?: 'scheduled' | 'active' | 'completed' | 'cancelled' | 'no_show';
    satisfaction_rating?: number;
    revenue_potential?: number;
    actual_revenue?: number;
    notes?: string;
    internal_notes?: string;
    next_action?: string;
    follow_up_date?: string;
    tags?: string[];
    weather_condition?: string;
    travel_distance?: number;
    travel_time?: number;
  }) {
    await ensureAuthenticated();
    
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');
    
    
    return directus.request(updateItem('customer_visits', visitId, {
      ...data,
      updated_at: getWIBTimeForDirectus(),
      user_updated: currentUser.id
    }));
  },

  /**
   * Process customer visit with customer data
   */
  processCustomerVisitWithData(visit: any) {
    return {
      ...visit,
      // Map customer data if available
      customer_data: visit.customer_id ? {
        id: visit.customer_id.id || visit.customer_id,
        name: visit.customer_id.NamaPelangganTambak || visit.customer_name,
        address: visit.customer_id.Alamat || visit.customer_address,
        phone: visit.customer_id.Contacs || visit.customer_phone,
        size: visit.customer_id.ukuranTambak,
        built_year: visit.customer_id.tahunDibangun,
        last_purchase: visit.customer_id.tglPembelianTerakhir,
        last_interaction: visit.customer_id.tglInteraksiTerakhir,
        source: visit.customer_id.source,
        pic: visit.customer_id.PIC,
        sic: visit.customer_id.SIC,
        description: visit.customer_id.Description,
        status: visit.customer_id.statusAktif,
        script: visit.customer_id.script
      } : null,
      // Ensure backward compatibility
      employee_id: visit.user_id,
      customer_name: visit.customer_name || visit.customer_id?.NamaPelangganTambak,
      customer_address: visit.customer_address || visit.customer_id?.Alamat
    };
  },


  // Evidence operations
  async getVisitEvidence(visitId: string) {
    await ensureAuthenticated();
    return directus.request(readItems('customer_visit_evidence', {
      filter: { visit_id: { _eq: visitId } },
      sort: ['-created_at']
    }));
  },

  async createEvidence(evidenceData: Omit<DirectusSchema['customer_visit_evidence'], 'id' | 'created_at'>) {
    await ensureAuthenticated();
    return directus.request(createItem('customer_visit_evidence', {
      ...evidenceData,
      created_at: getWIBTimeForDirectus()
    }));
  },

  async uploadEvidence(
    visitId: string,
    file: File,
    metadata: {
      type: 'photo' | 'audio' | 'document' | 'screenshot';
      description?: string;
      is_required: boolean;
      latitude?: number;
      longitude?: number;
    },
    _onProgress?: (progress: number) => void
  ) {
    await ensureAuthenticated();
    
    // Get current user to get employee data
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');
    
    // Get employee by user ID to get the employee ID
    const employee = await this.getEmployeeByUserId(currentUser.id);
    if (!employee) throw new Error('Employee not found');
      
    // First upload the file
      const uploadedFile = await this.uploadFile(file);

    // Then create the evidence record
    return this.createEvidence({
        visit_id: visitId,
      employee_id: employee.id,
      type: metadata.type,
        filename: file.name,
        file_size: file.size,
        mime_type: file.type,
      description: metadata.description,
      is_required: metadata.is_required,
      latitude: metadata.latitude,
      longitude: metadata.longitude,
        file_id: uploadedFile.id,
      captured_at: getWIBTimeForDirectus()
    });
  },

  async deleteEvidence(evidenceId: string) {
    await ensureAuthenticated();
    return directus.request(deleteItem('customer_visit_evidence', evidenceId));
  },

  // IMPROVED: Customer operations (using Data_Customer as primary with Customer_Tambak joins)
  async getCustomersImproved() {
    await ensureAuthenticated();
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');

    console.log(`🔍 IMPROVED: Getting Customer_Tambak records for PIC user ${currentUser.id} (${currentUser.first_name} ${currentUser.last_name})`);

    try {
      // ============================================================================
      // TEMPORARY FILTER: Only show Customer_Tambak records where PIC = current user
      // ============================================================================
      // OLD CODE (TEMPORARILY DISABLED):
      // This code fetches all customers from Data_Customer and merges with Customer_Tambak extensions
      // To re-enable: uncomment this section and comment out the PIC-filtered code below
      /*
      // Method 1: Get all customers from Data_Customer (master data)
      const dataCustomers = await directus.request(readItems('Data_Customer' as any, {
        fields: [
          '*', // All Data_Customer fields
        ],
        sort: ['Nama_Pelanggan'] // Sort by customer name
      }));

      console.log('✅ Data_Customer records:', dataCustomers.length);

      if (dataCustomers.length === 0) {
        console.warn('⚠️ No customers found in Data_Customer');
        return [];
      }

      // Method 2: Get Customer_Tambak data for extensions
      const customerNumbers = dataCustomers.map(c => c.No_pelanggan).filter(Boolean);
      let tambakLookup: Record<string, any> = {};

      if (customerNumbers.length > 0) {
        try {
          console.log('🔗 Fetching Customer_Tambak extensions for', customerNumbers.length, 'customers');
          const tambakData = await directus.request(readItems('Customer_Tambak', {
            filter: {
              No_pelanggan: { _in: customerNumbers }
            },
            fields: [
              '*',
              'PIC.id', 'PIC.first_name', 'PIC.last_name', 'PIC.email',
              'approved_by.id', 'approved_by.first_name', 'approved_by.last_name', 'approved_by.email'
            ]
          }));

          console.log('✅ Customer_Tambak extensions:', tambakData.length);

          // Create lookup map
          tambakLookup = tambakData.reduce((acc: Record<string, any>, tambak: any) => {
            if (tambak.No_pelanggan) {
              acc[tambak.No_pelanggan] = tambak;
            }
            return acc;
          }, {});
        } catch (tambakError) {
          console.warn('⚠️ Customer_Tambak lookup failed, continuing with Data_Customer only:', tambakError);
        }
      }

      // Method 3: Merge data with Customer_Tambak extensions
      const mergedCustomers = dataCustomers.map((customer: any) => {
        const tambakData = tambakLookup[customer.No_pelanggan] || {};

        return {
          // Primary data from Data_Customer
          id: customer.id,
          No_pelanggan: customer.No_pelanggan,
          name: customer.Nama_Pelanggan,
          Nama_Pelanggan: customer.Nama_Pelanggan,
          address: customer.Alamat,
          Alamat: customer.Alamat,
          phone: customer.Kontak,
          Phone: customer.Kontak,
          email: customer.Email,
          Email: customer.Email,
          Type_pelanggan: customer.Type_pelanggan,
          Kabupaten: customer.Kota,
          Provinsi: customer.Provinsi,

          // Extended data from Customer_Tambak (if available)
          tambak_id: tambakData.id,
          NamaPelangganTambak: tambakData.Nama_Pelanggan_Tambak || customer.Nama_Pelanggan,
          Nama_Pelanggan_Tambak: tambakData.Nama_Pelanggan_Tambak || customer.Nama_Pelanggan,
          Contacs: tambakData.Contacs || customer.Kontak,
          contact_person: tambakData.Contacs || customer.Kontak,
          ukuranTambak: tambakData.ukuranTambak,
          tambak_size: tambakData.ukuranTambak,
          tahunDibangun: tambakData.tahunDibangun,
          year_built: tambakData.tahunDibangun,
          tglPembelianTerakhir: tambakData.tglPembelianTerakhir,
          last_purchase_date: tambakData.tglPembelianTerakhir,
          tglInteraksiTerakhir: tambakData.tglInteraksiTerakhir,
          last_visit: tambakData.tglInteraksiTerakhir,
          lastInteraction: tambakData.tglInteraksiTerakhir,
          source: tambakData.source || 'Data_Customer',
          lead_source: tambakData.source || 'Data_Customer',

          // User relationships from Customer_Tambak
          PIC: tambakData.PIC,
          assigned_pic: tambakData.PIC ?
            `${tambakData.PIC.first_name || ''} ${tambakData.PIC.last_name || ''}`.trim() || tambakData.PIC.email || tambakData.PIC.id :
            '',
          pic_id: tambakData.PIC?.id,

          // Approval workflow from Customer_Tambak
          approval_status: tambakData.approval_status || 'approved',
          approval_date: tambakData.approval_date,
          approved_by: tambakData.approved_by ?
            `${tambakData.approved_by.first_name || ''} ${tambakData.approved_by.last_name || ''}`.trim() || tambakData.approved_by.email || tambakData.approved_by.id :
            '',
          approval_notes: tambakData.approval_notes,

          // Status fields
          statusAktif: tambakData.statusAktif !== undefined ? tambakData.statusAktif : 1,
          farmIsActive: tambakData.farmIsActive !== undefined ? tambakData.farmIsActive : true,
          status: 'active',
          priority: 'medium' as const,

          // Computed/default fields for compatibility
          distance: '-',
          revenue: 'Rp 0',
          created_at: tambakData.date_created || customer.date_created || getWIBTimeForDirectus(),
          updated_at: tambakData.date_updated || customer.date_updated || getWIBTimeForDirectus(),

          // Source identification
          data_source: 'Data_Customer_Primary',
          has_tambak_data: !!tambakData.id,
          idcust: customer.No_pelanggan
        };
      });

      console.log('✅ IMPROVED: Merged', mergedCustomers.length, 'customers with tambak extensions');
      return mergedCustomers;
      */
      // ============================================================================
      // END OLD CODE
      // ============================================================================

      // NEW CODE: PIC-filtered Customer_Tambak only
      console.log('🔗 Fetching Customer_Tambak records filtered by current user PIC');
      const tambakData = await directus.request(readItems('Customer_Tambak', {
        filter: {
          PIC: { _eq: currentUser.id }
        },
        fields: [
          '*',
          // User lookups for PIC and approval
          'PIC.id', 'PIC.first_name', 'PIC.last_name', 'PIC.email',
          'approved_by.id', 'approved_by.first_name', 'approved_by.last_name', 'approved_by.email'
        ],
        sort: ['Nama_Pelanggan_Tambak']
      }));

      console.log(`✅ Customer_Tambak records for current user: ${tambakData.length}`);

      if (tambakData.length === 0) {
        console.warn(`⚠️ No Customer_Tambak records found for PIC user ${currentUser.id}`);
        return [];
      }

      // Transform Customer_Tambak records to unified customer format
      const customers = tambakData.map((tambak: any) => ({
        // Primary identification
        id: tambak.id,
        tambak_id: tambak.id,
        No_pelanggan: tambak.No_pelanggan,
        idcust: tambak.No_pelanggan,

        // Names
        name: tambak.Nama_Pelanggan_Tambak || tambak.No_pelanggan,
        Nama_Pelanggan: tambak.Nama_Pelanggan_Tambak,
        NamaPelangganTambak: tambak.Nama_Pelanggan_Tambak,
        Nama_Pelanggan_Tambak: tambak.Nama_Pelanggan_Tambak,

        // Contact information
        address: tambak.Alamat || '',
        Alamat: tambak.Alamat,
        phone: tambak.Contacs || '',
        Phone: tambak.Contacs,
        Contacs: tambak.Contacs,
        contact_person: tambak.Contacs,
        email: tambak.Email || '',
        Email: tambak.Email,

        // Location
        Kabupaten: tambak.Kabupaten,
        Provinsi: tambak.Provinsi,

        // Tambak-specific fields
        ukuranTambak: tambak.ukuranTambak,
        tambak_size: tambak.ukuranTambak,
        tahunDibangun: tambak.tahunDibangun,
        year_built: tambak.tahunDibangun,
        tglPembelianTerakhir: tambak.tglPembelianTerakhir,
        last_purchase_date: tambak.tglPembelianTerakhir,
        tglInteraksiTerakhir: tambak.tglInteraksiTerakhir,
        last_visit: tambak.tglInteraksiTerakhir,
        lastInteraction: tambak.tglInteraksiTerakhir,
        source: tambak.source || 'Customer_Tambak',
        lead_source: tambak.source || 'Customer_Tambak',

        // User relationships
        PIC: tambak.PIC,
        assigned_pic: tambak.PIC ?
          `${tambak.PIC.first_name || ''} ${tambak.PIC.last_name || ''}`.trim() || tambak.PIC.email || tambak.PIC.id :
          '',
        pic_id: tambak.PIC?.id,

        // Approval workflow
        approval_status: tambak.approval_status || 'approved',
        approval_date: tambak.approval_date,
        approved_by: tambak.approved_by ?
          `${tambak.approved_by.first_name || ''} ${tambak.approved_by.last_name || ''}`.trim() || tambak.approved_by.email || tambak.approved_by.id :
          '',
        approval_notes: tambak.approval_notes,

        // Status fields
        statusAktif: tambak.statusAktif !== undefined ? tambak.statusAktif : 1,
        farmIsActive: tambak.farmIsActive !== undefined ? tambak.farmIsActive : true,
        status: 'active',
        priority: 'medium' as const,

        // Timestamps
        created_at: tambak.date_created || getWIBTimeForDirectus(),
        updated_at: tambak.date_updated || getWIBTimeForDirectus(),

        // Source identification
        data_source: 'Customer_Tambak_Filtered',
        has_tambak_data: true,
        source_priority: 'Customer_Tambak'
      }));

      console.log(`✅ IMPROVED: Returning ${customers.length} Customer_Tambak records for PIC user ${currentUser.id}`);
      console.log('🔍 Sample customer:', {
        name: customers[0]?.name,
        No_pelanggan: customers[0]?.No_pelanggan,
        pic: customers[0]?.assigned_pic
      });

      return customers;
      
    } catch (error) {
      console.error('❌ IMPROVED customer fetch failed:', error);
      
      // Fallback to original Customer_Tambak method
      console.log('🔄 Falling back to original Customer_Tambak method');
      return this.getCustomers();
    }
  },

  // COMPREHENSIVE: Get all customers from both Data_Customer and Customer_Tambak with complete coverage
  async getCustomersComprehensive() {
    await ensureAuthenticated();
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');

    console.log(`🔍 COMPREHENSIVE: Getting Customer_Tambak records for PIC user ${currentUser.id} (${currentUser.first_name} ${currentUser.last_name})`);

    try {
      // ============================================================================
      // TEMPORARY FILTER: Only get Customer_Tambak records where PIC = current user
      // ============================================================================
      // OLD CODE (TEMPORARILY DISABLED):
      // This code fetches all customers from Data_Customer and Customer_Tambak with comprehensive merging
      // To re-enable: uncomment this section and comment out the PIC-filtered code below
      /*
      // Phase 1: Get all customers from Data_Customer (master data)
      console.log('📊 Phase 1: Fetching all Data_Customer records');
      const dataCustomers = await directus.request(readItems('Data_Customer' as any, {
        fields: [
          'id',
          'No_pelanggan',
          'Nama_Pelanggan',
          'Alamat',
          'Phone',
          'Email',
          'Kabupaten',
          'Provinsi',
          'Tipe_Pelanggan'
        ],
        sort: ['Nama_Pelanggan'],
        limit: -1
      }));

      console.log(`✅ Phase 1: Found ${dataCustomers.length} customers in Data_Customer`);

      // Phase 2: Get ALL Customer_Tambak data
      console.log('🏞️ Phase 2: Fetching all Customer_Tambak records');
      const allTambakCustomers = await directus.request(readItems('Customer_Tambak', {
        fields: [
          '*',
          'PIC.id', 'PIC.first_name', 'PIC.last_name', 'PIC.email',
          'approved_by.id', 'approved_by.first_name', 'approved_by.last_name', 'approved_by.email'
        ],
        sort: ['Nama_Pelanggan_Tambak'],
        limit: -1
      }));

      console.log(`✅ Phase 2: Found ${allTambakCustomers.length} customers in Customer_Tambak`);

      // Create lookup maps
      const dataCustomerMap = new Map(dataCustomers.map(c => [c.No_pelanggan, c]));
      const tambakCustomerMap = new Map(allTambakCustomers.map(c => [c.No_pelanggan, c]));

      // Phase 3: Build comprehensive customer list
      const comprehensiveCustomers: any[] = [];
      const processedCustomerIds = new Set<string>();

      // Step 3a: Process all Data_Customer records with Customer_Tambak extensions
      for (const dataCustomer of dataCustomers) {
        const customerId = dataCustomer.No_pelanggan;
        if (!customerId) continue;

        const tambakData = tambakCustomerMap.get(customerId) || {};
        processedCustomerIds.add(customerId);

        comprehensiveCustomers.push({
          id: dataCustomer.id,
          idcust: customerId,
          No_pelanggan: customerId,
          name: dataCustomer.Nama_Pelanggan || tambakData.Nama_Pelanggan_Tambak || customerId,
          Nama_Pelanggan: dataCustomer.Nama_Pelanggan,
          Alamat: dataCustomer.Alamat,
          Phone: dataCustomer.Phone,
          Email: dataCustomer.Email,
          Kota: dataCustomer.Kota,
          Provinsi: dataCustomer.Provinsi,
          tambak_id: tambakData.id,
          NamaPelangganTambak: tambakData.Nama_Pelanggan_Tambak || dataCustomer.Nama_Pelanggan,
          address: dataCustomer.Alamat || tambakData.Alamat || '',
          ukuranTambak: tambakData.ukuranTambak,
          PIC: tambakData.PIC,
          data_source: 'Data_Customer_Primary',
          has_data_customer: true,
          has_tambak_data: !!tambakData.id,
          // ... (additional fields)
        });
      }

      // Step 3b: Add Customer_Tambak-only customers
      const tambakOnlyCustomers = allTambakCustomers.filter(tambak =>
        tambak.No_pelanggan && !processedCustomerIds.has(tambak.No_pelanggan)
      );

      for (const tambakCustomer of tambakOnlyCustomers) {
        comprehensiveCustomers.push({
          id: tambakCustomer.id,
          idcust: tambakCustomer.No_pelanggan,
          name: tambakCustomer.Nama_Pelanggan_Tambak,
          data_source: 'Customer_Tambak_Only',
          has_data_customer: false,
          has_tambak_data: true,
          // ... (additional fields)
        });
      }

      comprehensiveCustomers.sort((a, b) => {
        const nameA = a.name || a.NamaPelangganTambak || a.idcust || '';
        const nameB = b.name || b.NamaPelangganTambak || b.idcust || '';
        return nameA.localeCompare(nameB);
      });

      return comprehensiveCustomers;
      */
      // ============================================================================
      // END OLD CODE
      // ============================================================================

      // NEW CODE: PIC-filtered Customer_Tambak only
      console.log('🏞️ Fetching Customer_Tambak records filtered by current user PIC');
      const allTambakCustomers = await directus.request(readItems('Customer_Tambak', {
        filter: {
          PIC: { _eq: currentUser.id }
        },
        fields: [
          '*',
          // User lookups for PIC and approval
          'PIC.id', 'PIC.first_name', 'PIC.last_name', 'PIC.email',
          'approved_by.id', 'approved_by.first_name', 'approved_by.last_name', 'approved_by.email'
        ],
        sort: ['Nama_Pelanggan_Tambak'],
        limit: -1 // Get all records for current user
      }));

      console.log(`✅ Customer_Tambak records for current user: ${allTambakCustomers.length}`);

      if (allTambakCustomers.length === 0) {
        console.warn(`⚠️ No Customer_Tambak records found for PIC user ${currentUser.id}`);
        return [];
      }

      // Transform Customer_Tambak records to unified customer format
      const customers = allTambakCustomers.map((tambak: any) => ({
        // Primary identification
        id: tambak.id,
        tambak_id: tambak.id,
        No_pelanggan: tambak.No_pelanggan,
        idcust: tambak.No_pelanggan,

        // Names
        name: tambak.Nama_Pelanggan_Tambak || tambak.No_pelanggan,
        Nama_Pelanggan: tambak.Nama_Pelanggan_Tambak,
        NamaPelangganTambak: tambak.Nama_Pelanggan_Tambak,
        Nama_Pelanggan_Tambak: tambak.Nama_Pelanggan_Tambak,

        // Contact information
        address: tambak.Alamat || '',
        Alamat: tambak.Alamat,
        phone: tambak.Contacs || '',
        Phone: tambak.Contacs,
        Contacs: tambak.Contacs,
        contact_person: tambak.Contacs,
        email: tambak.Email || '',
        Email: tambak.Email,

        // Location
        Kabupaten: tambak.Kabupaten,
        Kota: tambak.Kabupaten,
        Provinsi: tambak.Provinsi,

        // Tambak-specific fields
        ukuranTambak: tambak.ukuranTambak,
        tambak_size: tambak.ukuranTambak,
        tahunDibangun: tambak.tahunDibangun,
        year_built: tambak.tahunDibangun,
        tglPembelianTerakhir: tambak.tglPembelianTerakhir,
        last_purchase_date: tambak.tglPembelianTerakhir,
        tglInteraksiTerakhir: tambak.tglInteraksiTerakhir,
        last_visit: tambak.tglInteraksiTerakhir,
        lastInteraction: tambak.tglInteraksiTerakhir,
        source: tambak.source || 'Customer_Tambak',
        lead_source: tambak.source || 'Customer_Tambak',
        script: tambak.script,
        Description: tambak.Description,

        // User relationships
        PIC: tambak.PIC,
        assigned_pic: tambak.PIC ?
          `${tambak.PIC.first_name || ''} ${tambak.PIC.last_name || ''}`.trim() || tambak.PIC.email || tambak.PIC.id :
          '',
        pic_id: tambak.PIC?.id,

        // Approval workflow
        approval_status: tambak.approval_status || 'approved',
        approval_date: tambak.approval_date,
        approved_by: tambak.approved_by ?
          `${tambak.approved_by.first_name || ''} ${tambak.approved_by.last_name || ''}`.trim() || tambak.approved_by.email || tambak.approved_by.id :
          '',
        approval_notes: tambak.approval_notes,

        // Status fields
        statusAktif: tambak.statusAktif !== undefined ? tambak.statusAktif : 1,
        farmIsActive: tambak.farmIsActive !== undefined ? tambak.farmIsActive : true,
        priority: tambak.statusAktif === 1 ? 'high' : 'medium',
        status: tambak.statusAktif === 1 ? 'active' : 'inactive',

        // Timestamps
        created_at: tambak.date_created || tambak.created_at || new Date().toISOString(),
        updated_at: tambak.date_updated || tambak.updated_at || new Date().toISOString(),

        // Source identification
        data_source: 'Customer_Tambak_Filtered',
        has_data_customer: false,
        has_tambak_data: true,
        source_priority: 'Customer_Tambak',
        tambak_name: tambak.Nama_Pelanggan_Tambak
      }));

      console.log(`✅ COMPREHENSIVE: Returning ${customers.length} Customer_Tambak records for PIC user ${currentUser.id}`);
      console.log('🔍 Sample customer:', {
        name: customers[0]?.name,
        No_pelanggan: customers[0]?.No_pelanggan,
        pic: customers[0]?.assigned_pic,
        data_source: customers[0]?.data_source
      });

      return customers;
      
    } catch (error) {
      console.error('❌ COMPREHENSIVE customer fetch failed:', error);
      
      // Fallback to improved method
      console.log('🔄 Falling back to improved Data_Customer + Customer_Tambak method');
      return this.getCustomersImproved();
    }
  },

  // ALPHABETICAL SORTING: Data_Customer-based alphabetical sorting for All Customer tab
  async getAllCustomersSortedAlphabetically(sortOrder: 'asc' | 'desc' = 'asc', limit?: number) {
    await ensureAuthenticated();
    const currentUser = authManager.getState().user;

    if (!currentUser) {
      console.error('❌ No authenticated user found');
      return [];
    }

    console.log(`🔤 ALPHABETICAL: Getting Customer_Tambak records sorted ${sortOrder === 'asc' ? 'A-Z' : 'Z-A'} for PIC user ${currentUser.id}`);
    console.log(`📊 Sort order: ${sortOrder} (${sortOrder === 'asc' ? 'A to Z' : 'Z to A'})`);
    if (limit) {
      console.log(`🔢 Limit: ${limit} customers`);
    }

    try {
      // ONLY query Customer_Tambak with PIC filter and alphabetical sort
      const sortField = sortOrder === 'asc' ? 'Nama_Pelanggan_Tambak' : '-Nama_Pelanggan_Tambak';

      const queryOptions: any = {
        fields: [
          '*',
          'PIC.id',
          'PIC.first_name',
          'PIC.last_name',
          'PIC.email',
          'approved_by.id',
          'approved_by.first_name',
          'approved_by.last_name',
          'approved_by.email'
        ],
        sort: [sortField],
        filter: {
          PIC: { _eq: currentUser.id }, // REQUIRED: PIC filter
          Nama_Pelanggan_Tambak: { _nnull: true } // Only customers with names
        }
      };

      if (limit) {
        queryOptions.limit = limit;
      }

      console.log('📊 Fetching Customer_Tambak with PIC filter and alphabetical sort...');
      const tambakCustomers = await directus.request(readItems('Customer_Tambak', queryOptions));

      console.log(`✅ Customer_Tambak alphabetical fetch: ${tambakCustomers.length} customers for PIC ${currentUser.id}`);

      if (tambakCustomers.length === 0) {
        console.warn(`⚠️ No customers found for PIC user ${currentUser.id}`);
        return [];
      }

      // Format Customer_Tambak records
      const formatUserName = (user: any): string => {
        if (!user) return '';
        if (typeof user === 'string') return user;
        return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || user.id || '';
      };

      const alphabeticalCustomers = tambakCustomers.map((customer: any) => ({
        // Primary identifiers
        id: customer.id,
        No_pelanggan: customer.No_pelanggan,
        idcust: customer.No_pelanggan || customer.id.toString(),

        // Name (basis of alphabetical sort)
        name: customer.Nama_Pelanggan_Tambak,
        Nama_Pelanggan: customer.Nama_Pelanggan_Tambak,
        NamaPelangganTambak: customer.Nama_Pelanggan_Tambak,
        Nama_Pelanggan_Tambak: customer.Nama_Pelanggan_Tambak,
        tambak_name: customer.Nama_Pelanggan_Tambak,

        // Contact
        address: customer.Alamat || '',
        Alamat: customer.Alamat || '',
        phone: customer.Contacs || '',
        Contacs: customer.Contacs,
        contact_person: formatUserName(customer.PIC),
        pic_id: customer.PIC?.id || '',

        // Tambak-specific fields
        ukuranTambak: customer.ukuranTambak,
        tambak_size: customer.ukuranTambak,
        tahunDibangun: customer.tahunDibangun,
        year_built: customer.tahunDibangun,
        farmIsActive: customer.statusAktif === 1,
        statusAktif: customer.statusAktif,

        // Approval fields
        approval_status: customer.approval_status || 'pending',
        approval_date: customer.approval_date,
        approved_by: formatUserName(customer.approved_by),
        approval_notes: customer.approval_notes,

        // PIC relationship
        PIC: customer.PIC,
        assigned_pic: formatUserName(customer.PIC),

        // Computed fields for compatibility
        priority: 'medium' as const,
        status: 'active' as const,
        distance: '-',
        revenue: 'Rp 0',
        lastInteraction: customer.tglInteraksiTerakhir ?
          new Date(customer.tglInteraksiTerakhir).toLocaleDateString() : 'Belum ada',
        tglInteraksiTerakhir: customer.tglInteraksiTerakhir,
        tglPembelianTerakhir: customer.tglPembelianTerakhir,

        // Data source tracking
        data_source: 'Customer_Tambak_Alphabetical',
        primary_source: 'Customer_Tambak',
        sort_method: 'alphabetical',
        sort_order: sortOrder,
        has_tambak_data: true,
        source: 'Customer_Tambak',

        // Timestamps
        created_at: customer.date_created,
        updated_at: customer.date_updated
      }));

      console.log(`🎯 ALPHABETICAL RESULT: ${alphabeticalCustomers.length} customers sorted ${sortOrder === 'asc' ? 'A-Z' : 'Z-A'} for PIC ${currentUser.id}`);

      // Log sample of alphabetical order
      if (alphabeticalCustomers.length > 0) {
        const sampleSize = Math.min(5, alphabeticalCustomers.length);
        console.log(`📝 Sample ${sortOrder === 'asc' ? 'A-Z' : 'Z-A'} order:`);
        alphabeticalCustomers.slice(0, sampleSize).forEach((customer, index) => {
          console.log(`   ${index + 1}. ${customer.No_pelanggan}: "${customer.name}"`);
        });
      }

      return alphabeticalCustomers;

    } catch (error) {
      console.error('❌ Alphabetical customer fetch failed:', error);
      return [];
    }
  },

  // Customer operations (using Customer_Tambak table) - LEGACY
  async getCustomers() {
    await ensureAuthenticated();
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');

    console.log(`🔍 LEGACY: Making enhanced request to Customer_Tambak for PIC user ${currentUser.id}`);

    try {
      // TEMPORARY FILTER: Only get Customer_Tambak records where PIC = current user
      const result = await directus.request(readItems('Customer_Tambak', {
        filter: {
          PIC: { _eq: currentUser.id }
        },
        fields: [
          '*', // All basic fields
          // User lookup for PIC
          'PIC.id',
          'PIC.first_name',
          'PIC.last_name',
          'PIC.email',
          // User lookup for approved_by
          'approved_by.id',
          'approved_by.first_name',
          'approved_by.last_name',
          'approved_by.email'
        ]
      }));
      
      if (result.length > 0) {
        console.log('Enhanced customer result:', {
          name: result[0].Nama_Pelanggan_Tambak,
          approval_status: result[0].approval_status,
          pic: result[0].PIC,
          approved_by: result[0].approved_by
        });
      }
      
      // ENHANCEMENT: Try to add Tipe_Pelanggan to all customers
      const enhancedResult = await this.enhanceCustomersWithTipePelanggan(result);
      return enhancedResult;
    } catch (error) {
      console.error('❌ Enhanced Customer_Tambak query failed:', error);
      
      // Fallback: try without user lookups but with approval fields and PIC filter
      try {
        const fallbackResult = await directus.request(readItems('Customer_Tambak', {
          filter: {
            PIC: { _eq: currentUser.id }
          },
          fields: [
            '*' // All fields including approval fields
          ]
        }));
        // ENHANCEMENT: Try to add Tipe_Pelanggan to fallback results
        const enhancedFallback = await this.enhanceCustomersWithTipePelanggan(fallbackResult);
        return enhancedFallback;
      } catch (fallbackError) {
        console.error('❌ Fallback also failed:', fallbackError);

        // Final fallback: try basic query with PIC filter
        try {
          const basicResult = await directus.request(readItems('Customer_Tambak', {
            filter: {
              PIC: { _eq: currentUser.id }
            }
          }));
          // ENHANCEMENT: Try to add Tipe_Pelanggan to basic results
          const enhancedBasic = await this.enhanceCustomersWithTipePelanggan(basicResult);
          return enhancedBasic;
        } catch (basicError) {
          console.error('❌ All queries failed:', basicError);
          throw error;
        }
      }
    }
  },

  async createCustomer(customerData: {
    NamaPelangganTambak: string;
    Alamat?: string;
    ukuranTambak?: string;
    Contacs?: string;
    tahunDibangun?: number;
    approval_notes?: string;
    statusAktif?: number;
    farmIsActive?: boolean;
    coordinates?: { lat: number; lng: number };
    phoneNumbers?: string[];
  }) {
    await ensureAuthenticated();
    
    // Get current user ID for PIC field auto-fill
    const currentUser = authManager.getState().user;
    if (!currentUser) {
      throw new Error('No authenticated user found for PIC field');
    }
    
    
    // Map field names according to Newcustomer.md requirements
    // Note: Remove Contacs from Customer_Tambak as phone numbers will be stored in Phone_Customer
    const mappedData = {
      Nama_Pelanggan_Tambak: customerData.NamaPelangganTambak,
      Alamat: customerData.Alamat,
      ukuranTambak: customerData.ukuranTambak,
      tahunDibangun: customerData.tahunDibangun,
      PIC: currentUser.id, // Auto-fill with logged in user ID
      approval_notes: customerData.approval_notes,
      statusAktif: customerData.statusAktif ?? 1, // Default to active
      farmIsActive: customerData.farmIsActive ?? true, // Default farm to active
      tglInteraksiTerakhir: getWIBTimeForDirectus(),
      source: "App Telegram", // Keep source field as it's in the example
      approval_status: "pending", // Default approval status
      coordinates: customerData.coordinates // GPS coordinates in format {"lat": value, "lng": value}
    };
    
    try {
      // Step 1: Generate a unique No_pelanggan ID for the new customer
      // We'll create a simple ID based on timestamp + random number for uniqueness
      const timestamp = Date.now().toString().slice(-8); // Last 8 digits of timestamp
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0'); // 3-digit random number
      const generatedNoPelanggan = `T${timestamp}${random}`; // Format: T12345678001
      
      // Step 2: Create a basic Data_Customer record first (required for foreign key)
      const dataCustomerData = {
        No_pelanggan: generatedNoPelanggan,
        Nama_Pelanggan: customerData.NamaPelangganTambak,
        Alamat: customerData.Alamat || '',
        Status: 'Active',
        // Add minimal required fields
        coordinates: customerData.coordinates
      };
      
      try {
        await directus.request(createItem('Data_Customer', dataCustomerData));
        console.log('✅ Data_Customer created with No_pelanggan:', generatedNoPelanggan);
      } catch (dataCustomerError) {
        console.error('❌ Failed to create Data_Customer, trying to continue with Customer_Tambak only:', dataCustomerError);
        // Continue without Data_Customer if it fails (some fields might be missing)
      }
      
      // Step 3: Add the No_pelanggan reference to Customer_Tambak data
      mappedData.No_pelanggan = generatedNoPelanggan;
      
      // Step 4: Create Customer_Tambak record
      const tambakResult = await directus.request(createItem('Customer_Tambak', mappedData));
      console.log('✅ Customer_Tambak created with ID:', tambakResult.id);
      
      // Step 5: Handle phone numbers if provided
      if (customerData.phoneNumbers && customerData.phoneNumbers.length > 0) {
        console.log('📞 Creating phone records for customer:', generatedNoPelanggan);
        
        // Step 6: Create Phone_Customer records for each phone number
        const phonePromises = customerData.phoneNumbers.map(async (phoneNumber) => {
          const phoneData = {
            No_pelanggan: generatedNoPelanggan, // Reference to Data_Customer
            Phone_Number: phoneNumber.trim(),
            Phone_Parsed: phoneNumber.trim() // Store the same value in Phone_Parsed
          };
          
          try {
            const phoneResult = await directus.request(createItem('Phone_Customer', phoneData));
            console.log('✅ Phone number added:', phoneNumber, 'for customer:', generatedNoPelanggan);
            return phoneResult;
          } catch (phoneError) {
            console.error('❌ Failed to create phone record:', phoneError, phoneData);
            // Don't fail the entire customer creation if phone creation fails
            return null;
          }
        });
        
        // Wait for all phone records to be created (or attempted)
        const phoneResults = await Promise.allSettled(phonePromises);
        const successfulPhones = phoneResults.filter(result => result.status === 'fulfilled' && result.value !== null).length;
        console.log(`📞 Phone records created: ${successfulPhones}/${customerData.phoneNumbers.length}`);
      }
      
      return tambakResult;
    } catch (error) {
      console.error('❌ Failed to create customer:', error);
      console.error('📋 Payload that failed:', mappedData);
      
      // Enhanced error logging
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      
      // Re-throw for handling at higher level
      throw error;
    }
  },

  async testPermissions() {
    
    const authState = authManager.getState();
    console.log('Permission check auth state:', {
      isAuthenticated: authState.isAuthenticated,
      hasToken: !!authState.token,
      userId: authState.user?.id,
      userEmail: authState.user?.email,
      userRole: authState.user?.role
    });

    // Ensure token is set before testing
    await ensureAuthenticated();

    // Test current user info
    try {
      await directus.request(readMe());
    } catch (error) {
      console.error('❌ Cannot read current user:', error);
    }

    // Note: emp_employees table is no longer used - using directus_users directly

    // Test Customer_Tambak access
    try {
      await directus.request(readItems('Customer_Tambak', {
        fields: ['*'], 
        limit: 1 
      }));
    } catch (error) {
      console.error('❌ Customer_Tambak access denied:', error.message);
      console.error('Status:', error.status);
    }

    // Test Data_Customer access
    try {
      await directus.request(readItems('Data_Customer' as any, {
        fields: ['No_pelanggan', 'Nama_Pelanggan'], 
        limit: 1 
      }));
    } catch (error) {
      console.error('❌ Data_Customer access denied:', error);
      console.error('❌ Data_Customer Error details:', {
        message: error.message,
        status: error.status,
        code: error.code
      });
    }

    // Test Data_Customer count specifically
    try {
      await directus.request(readItems('Data_Customer' as any, {
        limit: 0,
        meta: 'total_count'
      }));
    } catch (error) {
      console.error('❌ Data_Customer count test failed:', error);
    }
  },

  async testDataCustomerAccess() {
    return this.testPermissions();
  },

  async testNopelanganSearch(query: string = 'test') {
    
    try {
      // Test search with only No_pelanggan field
      const result = await directus.request(readItems('Customer_Tambak', {
        fields: ['*'],
        filter: {
          No_pelanggan: { _icontains: query }
        },
        limit: 5
      }));
      
      return result;
    } catch (error) {
      console.error('❌ No_pelanggan search failed:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        response: error.response
      });
      
      // Try alternative field names that might exist
      const alternativeFields = ['id', 'customer_id', 'idcust', 'customer_no', 'pelanggan_no'];
      
      for (const fieldName of alternativeFields) {
        try {
          await directus.request(readItems('Customer_Tambak', {
            fields: ['*'],
            filter: {
              [fieldName]: { _icontains: query }
            },
            limit: 1
          }));
        } catch {
          // Unsupported alternative fields are expected; continue probing the remaining candidates.
        }
      }
      
      throw error;
    }
  },

  // Helper function to rank search results by relevance
  rankSearchResults(customers: any[], query: string): any[] {
    if (!query.trim() || !customers.length) {
      return customers;
    }

    const normalizedQuery = query.toLowerCase().trim();
    
    const rankedResults = customers.map(customer => {
      let score = 0;
      const customerName = (customer.Nama_Pelanggan_Tambak || '').toLowerCase();
      const customerNo = (customer.No_pelanggan || '').toLowerCase();
      const customerAddress = (customer.Alamat || '').toLowerCase();
      
      // Exact matches (highest priority - 1000 points)
      if (customerName === normalizedQuery) score += 1000;
      if (customerNo === normalizedQuery) score += 1000;
      
      // Starts with matches (high priority - 500 points)
      if (customerName.startsWith(normalizedQuery)) score += 500;
      if (customerNo.startsWith(normalizedQuery)) score += 500;
      
      // Contains matches (medium priority - 100-200 points)
      if (customerName.includes(normalizedQuery)) score += 100;
      if (customerNo.includes(normalizedQuery)) score += 200; // Customer ID slightly higher priority
      if (customerAddress.includes(normalizedQuery)) score += 50; // Address lower priority
      
      // Activity bonus (small boost for active customers)
      if (customer.farmIsActive === true || customer.statusAktif === 1) score += 25;
      
      return { ...customer, searchScore: score };
    });

    // Sort by search score (highest first), then by name for tie-breaking
    return rankedResults.sort((a, b) => {
      if (b.searchScore !== a.searchScore) {
        return b.searchScore - a.searchScore;
      }
      // Tie-breaker: alphabetical by name
      return (a.Nama_Pelanggan_Tambak || '').localeCompare(b.Nama_Pelanggan_Tambak || '');
    });
  },

  // COMPREHENSIVE: Search all customers from BOTH Data_Customer and Customer_Tambak tables
  async searchCustomersComprehensive(query: string, options: {
    page?: number;
    limit?: number;
    offset?: number;
  } = {}) {
    await ensureAuthenticated();
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');

    const { page = 1, limit = 20, offset } = options;
    const actualOffset = offset || (page - 1) * limit;

    console.log(`🔍 PIC-FILTERED SEARCH: Searching Customer_Tambak with PIC=${currentUser.id} for query:`, query);

    try {
      // TEMPORARY: Only search Customer_Tambak with PIC filter (Data_Customer search disabled)
      console.log('🏞️ Searching Customer_Tambak with PIC filter and query');
      const allTambakCustomers = await directus.request(readItems('Customer_Tambak', {
        filter: {
          _and: [
            { PIC: { _eq: currentUser.id } }, // REQUIRED: PIC filter
            ...(query.trim() === '' ? [] : [{
              _or: [
                { Nama_Pelanggan_Tambak: { _icontains: query } },
                { No_pelanggan: { _icontains: query } },
                { Alamat: { _icontains: query } }
              ]
            }])
          ]
        },
        fields: ['*', 'PIC.id', 'PIC.first_name', 'PIC.last_name', 'PIC.email'],
        sort: ['Nama_Pelanggan_Tambak'],
        limit: limit * 2
      }));

      console.log(`✅ Customer_Tambak filtered results: ${allTambakCustomers.length} for PIC user ${currentUser.id}`);

      // Format Customer_Tambak records for display
      console.log('📝 Formatting Customer_Tambak records');
      const formattedResults = allTambakCustomers.map((customer: any) => ({
        // Primary identifiers from Customer_Tambak
        id: customer.id,
        No_pelanggan: customer.No_pelanggan,
        name: customer.Nama_Pelanggan_Tambak,
        Nama_Pelanggan: customer.Nama_Pelanggan_Tambak,
        NamaPelangganTambak: customer.Nama_Pelanggan_Tambak,
        address: customer.Alamat || '',
        Alamat: customer.Alamat || '',
        phone: customer.Contacs || '',
        Phone: customer.Contacs || '',

        // Customer_Tambak specific data
        Nama_Pelanggan_Tambak: customer.Nama_Pelanggan_Tambak,
        tambak_name: customer.Nama_Pelanggan_Tambak,
        ukuranTambak: customer.ukuranTambak,
        tambak_size: customer.ukuranTambak,
        tahunDibangun: customer.tahunDibangun,
        year_built: customer.tahunDibangun,

        // Workflow fields
        approval_status: customer.approval_status || 'pending',
        approval_date: customer.approval_date,
        approval_notes: customer.approval_notes,
        approved_by: customer.approved_by,

        // User relationships (PIC data)
        PIC: customer.PIC,
        assigned_pic: customer.PIC ?
          `${customer.PIC.first_name || ''} ${customer.PIC.last_name || ''}`.trim() || customer.PIC.email || customer.PIC.id :
          '',
        pic_id: customer.PIC?.id,

        // Status fields
        statusAktif: customer.statusAktif,
        farmIsActive: customer.statusAktif === 1,

        // Timestamps
        created_at: customer.date_created,
        updated_at: customer.date_updated,

        // Source identification
        has_tambak_data: true,
        data_source: 'Customer_Tambak_Only',

        // Computed fields for compatibility
        idcust: customer.No_pelanggan || customer.id.toString(),
        contact_person: '',
        priority: 'medium' as const,
        status: 'active' as const,
        distance: '-',
        revenue: 'Rp 0',
        lastInteraction: 'Belum ada',
        last_visit: '',
        source: 'Customer_Tambak'
      }));

      console.log('✅ Customer_Tambak formatting complete:', formattedResults.length, 'customers');

      // Apply pagination to results
      const startIndex = actualOffset;
      const endIndex = startIndex + limit;
      const paginatedResults = formattedResults.slice(startIndex, endIndex);

      console.log('✅ CUSTOMER_TAMBAK SEARCH COMPLETE:', formattedResults.length, 'total customers');
      console.log('📄 Paginated results:', paginatedResults.length, 'customers (page', page, 'of', Math.ceil(formattedResults.length / limit), ')');

      return {
        data: paginatedResults,
        page,
        limit,
        offset: actualOffset,
        hasMore: endIndex < formattedResults.length,
        totalFound: formattedResults.length
      };
      
    } catch (error) {
      console.error('❌ Customer_Tambak search failed:', error);
      console.log('🔄 Fallback: Using legacy search method');

      // Fallback to legacy Customer_Tambak search
      return this.searchCustomers(query, options);
    }
  },

  // ENHANCED: Comprehensive search with phone number lookup and PIC name resolution for maps view
  async searchCustomersForMapsView(query: string, options: { 
    limit?: number;
    customerType?: string;
  } = {}) {
    await ensureAuthenticated();
    
    const { limit = -1, customerType = 'all' } = options; // -1 means no limit for maps view
    
    console.log('🗺️ MAPS SEARCH: Comprehensive search for maps view with query:', query, 'type:', customerType);
    
    try {
      // Phase 1: Search Data_Customer with comprehensive fields
      const dataCustomerSearch = directus.request(readItems('Data_Customer' as any, {
        ...(query.trim() === '' ? {} : {
          filter: {
            _or: [
              { Nama_Pelanggan: { _icontains: query } },
              { No_pelanggan: { _icontains: query } },
              { Alamat: { _icontains: query } },
              { Kontak: { _icontains: query } },
              { Email: { _icontains: query } },
              { Kota: { _icontains: query } },
              { Provinsi: { _icontains: query } },
              { Kodepos: { _icontains: query } }
            ]
          }
        }),
        ...(customerType !== 'all' && customerType !== 'customer_tambak' ? {
          filter: {
            ...(query.trim() === '' ? {} : {
              _or: [
                { Nama_Pelanggan: { _icontains: query } },
                { No_pelanggan: { _icontains: query } },
                { Alamat: { _icontains: query } },
                { Kontak: { _icontains: query } },
                { Email: { _icontains: query } },
                { Kota: { _icontains: query } },
                { Provinsi: { _icontains: query } },
                { Kodepos: { _icontains: query } }
              ]
            }),
            Tipe_Pelanggan: { _eq: customerType }
          }
        } : {}),
        fields: ['*'],
        sort: ['Nama_Pelanggan'],
        limit: limit === -1 ? undefined : limit
      }));

      // Phase 2: Search Customer_Tambak (only if customerType allows it)
      const tambakCustomerSearch = customerType === 'customer_tambak' || customerType === 'all' 
        ? directus.request(readItems('Customer_Tambak', {
            ...(query.trim() === '' ? {} : {
              filter: {
                _or: [
                  { Nama_Pelanggan_Tambak: { _icontains: query } },
                  { No_pelanggan: { _icontains: query } },
                  { Alamat: { _icontains: query } },
                  { Contacs: { _icontains: query } }
                ]
              }
            }),
            fields: [
              '*',
              'PIC.id', 'PIC.first_name', 'PIC.last_name', 'PIC.email'
            ],
            sort: ['Nama_Pelanggan_Tambak'],
            limit: limit === -1 ? undefined : limit
          }))
        : Promise.resolve([]);

      // Phase 3: Search Phone_Customer for phone number matches
      const phoneSearch = query.trim() 
        ? directus.request(readItems('Phone_Customer' as any, {
            filter: {
              _or: [
                { Phone_Number: { _icontains: query } },
                { Phone_Parsed: { _icontains: query } }
              ]
            },
            fields: ['*'],
            limit: limit === -1 ? undefined : limit
          }))
        : Promise.resolve([]);

      // Execute all searches in parallel
      const [dataCustomers, tambakCustomers, phoneMatches] = await Promise.all([
        dataCustomerSearch,
        tambakCustomerSearch,
        phoneSearch
      ]);

      console.log('📊 Search results:', {
        dataCustomers: dataCustomers.length,
        tambakCustomers: tambakCustomers.length,
        phoneMatches: phoneMatches.length
      });

      // Phase 4: Get additional customer data for phone matches
      const phoneCustomerNumbers = phoneMatches.map((p: any) => p.No_pelanggan).filter(Boolean);
      let phoneLinkedCustomers: any[] = [];
      
      if (phoneCustomerNumbers.length > 0) {
        try {
          // Get Data_Customer records linked to phone matches
          phoneLinkedCustomers = await directus.request(readItems('Data_Customer' as any, {
            filter: {
              No_pelanggan: { _in: phoneCustomerNumbers }
            },
            fields: ['*'],
            sort: ['Nama_Pelanggan']
          }));
          
          console.log('📞 Phone-linked customers found:', phoneLinkedCustomers.length);
        } catch (phoneError) {
          console.warn('⚠️ Phone-linked customer lookup failed:', phoneError);
        }
      }

      // Phase 5: Get Customer_Tambak extensions for all Data_Customer results
      const allDataCustomerNumbers = [
        ...dataCustomers.map((c: any) => c.No_pelanggan),
        ...phoneLinkedCustomers.map((c: any) => c.No_pelanggan)
      ].filter(Boolean);
      
      let tambakLookup: Record<string, any> = {};
      
      if (allDataCustomerNumbers.length > 0) {
        try {
          const tambakData = await directus.request(readItems('Customer_Tambak', {
            filter: {
              No_pelanggan: { _in: allDataCustomerNumbers }
            },
            fields: [
              '*',
              'PIC.id', 'PIC.first_name', 'PIC.last_name', 'PIC.email'
            ]
          }));
          
          tambakLookup = tambakData.reduce((acc: Record<string, any>, tambak: any) => {
            if (tambak.No_pelanggan) {
              acc[tambak.No_pelanggan] = tambak;
            }
            return acc;
          }, {});
          
          console.log('✅ Tambak extensions loaded:', Object.keys(tambakLookup).length);
        } catch (tambakError) {
          console.warn('⚠️ Tambak extensions lookup failed:', tambakError);
        }
      }

      // Phase 6: Build phone lookup map
      const phoneLookup = phoneMatches.reduce((acc: Record<string, string[]>, phone: any) => {
        if (phone.No_pelanggan) {
          if (!acc[phone.No_pelanggan]) {
            acc[phone.No_pelanggan] = [];
          }
          acc[phone.No_pelanggan].push(phone.Phone_Number || phone.Phone_Parsed || '');
        }
        return acc;
      }, {});

      // Phase 7: Process and merge all results
      const processCustomer = (customer: any, source: string, tambakData?: any) => {
        const combinedTambakData = tambakData || tambakLookup[customer.No_pelanggan] || {};
        const phoneNumbers = phoneLookup[customer.No_pelanggan] || [];
        
        return {
          // Core identification
          id: customer.id || combinedTambakData.id || customer.No_pelanggan,
          No_pelanggan: customer.No_pelanggan,
          
          // Names (with priority handling)
          name: customer.Nama_Pelanggan || customer.Nama_Pelanggan_Tambak || '',
          Nama_Pelanggan: customer.Nama_Pelanggan,
          NamaPelangganTambak: customer.Nama_Pelanggan_Tambak || combinedTambakData.Nama_Pelanggan_Tambak,
          tambak_name: customer.Nama_Pelanggan_Tambak || combinedTambakData.Nama_Pelanggan_Tambak,
          
          // Address information
          address: customer.Alamat || combinedTambakData.Alamat || '',
          Alamat: customer.Alamat || combinedTambakData.Alamat,
          Kota: customer.Kota,
          Provinsi: customer.Provinsi,
          Kodepos: customer.Kodepos,
          
          // Contact information (enhanced with phone lookup)
          phone: phoneNumbers.length > 0 ? phoneNumbers[0] : (customer.Kontak || customer.Contacs || ''),
          Contacs: customer.Contacs || customer.Kontak,
          phoneNumbers: phoneNumbers,
          Email: customer.Email,
          
          // Customer type
          Tipe_Pelanggan: customer.Tipe_Pelanggan || '',
          tipe_pelanggan: customer.Tipe_Pelanggan || '',
          
          // PIC information (with name resolution)
          PIC: combinedTambakData.PIC?.id || customer.PIC,
          assigned_pic: combinedTambakData.PIC ? 
            `${combinedTambakData.PIC.first_name || ''} ${combinedTambakData.PIC.last_name || ''}`.trim() || 
            combinedTambakData.PIC.email || 
            combinedTambakData.PIC.id : 
            '',
          pic_name: combinedTambakData.PIC ? 
            `${combinedTambakData.PIC.first_name || ''} ${combinedTambakData.PIC.last_name || ''}`.trim() : 
            '',
          pic_email: combinedTambakData.PIC?.email || '',
          pic_id: combinedTambakData.PIC?.id,
          
          // Coordinates for map view
          coordinates: customer.coordinates || combinedTambakData.coordinates,
          latitude: customer.coordinates?.lat || combinedTambakData.coordinates?.lat,
          longitude: customer.coordinates?.lng || combinedTambakData.coordinates?.lng,
          
          // Additional metadata
          data_source: source,
          has_phone_match: phoneNumbers.length > 0,
          search_matched_phone: phoneNumbers.length > 0,
          
          // Standard compatibility fields
          idcust: customer.No_pelanggan,
          priority: 'medium' as const,
          status: 'active' as const,
          farmIsActive: true,
          approval_status: combinedTambakData.approval_status || 'approved'
        };
      };

      // Merge results
      const results: any[] = [];
      const seenCustomers = new Set<string>();

      // Process Data_Customer results (primary)
      dataCustomers.forEach((customer: any) => {
        if (!seenCustomers.has(customer.No_pelanggan)) {
          results.push(processCustomer(customer, 'Data_Customer_Primary'));
          seenCustomers.add(customer.No_pelanggan);
        }
      });

      // Process phone-linked customers
      phoneLinkedCustomers.forEach((customer: any) => {
        if (!seenCustomers.has(customer.No_pelanggan)) {
          results.push(processCustomer(customer, 'Data_Customer_Phone_Match'));
          seenCustomers.add(customer.No_pelanggan);
        }
      });

      // Process Customer_Tambak-only results
      if (customerType === 'customer_tambak' || customerType === 'all') {
        tambakCustomers.forEach((tambak: any) => {
          if (!seenCustomers.has(tambak.No_pelanggan)) {
            // This is a Customer_Tambak-only record
            const syntheticCustomer = {
              No_pelanggan: tambak.No_pelanggan,
              Nama_Pelanggan: null,
              Alamat: null,
              Kontak: null,
              Nama_Pelanggan_Tambak: tambak.Nama_Pelanggan_Tambak
            };
            results.push(processCustomer(syntheticCustomer, 'Customer_Tambak_Only', tambak));
            seenCustomers.add(tambak.No_pelanggan);
          }
        });
      }

      // Filter out results without coordinates for maps view
      const mapResults = results.filter(customer => 
        customer.coordinates || (customer.latitude && customer.longitude)
      );

      console.log('🗺️ Maps search complete:', {
        totalResults: results.length,
        mapResults: mapResults.length,
        phoneMatches: results.filter(c => c.has_phone_match).length,
        dataCustomerPrimary: results.filter(c => c.data_source === 'Data_Customer_Primary').length,
        customerTambakOnly: results.filter(c => c.data_source === 'Customer_Tambak_Only').length
      });

      return {
        data: mapResults,
        totalFound: results.length,
        mapReady: mapResults.length,
        hasPhoneMatches: results.some(c => c.has_phone_match),
        searchInfo: {
          query,
          customerType,
          limit
        }
      };
      
    } catch (error) {
      console.error('❌ Maps view search failed:', error);
      throw error;
    }
  },

  // COMPREHENSIVE: Search ALL customers from both tables for interaction dropdowns (NO filtering by Tipe_Pelanggan)
  async searchCustomersForInteractions(query: string, options: { 
    page?: number; 
    limit?: number; 
    offset?: number;
  } = {}) {
    await ensureAuthenticated();
    
    const { page = 1, limit = 20, offset } = options;
    const actualOffset = offset || (page - 1) * limit;
    
    console.log('🔍 INTERACTION SEARCH: Getting ALL customers from both tables for interaction dropdown');
    console.log('🎯 This search includes customers with ALL Tipe_Pelanggan values (not just Aquaculture)');
    
    try {
      const allCustomers: any[] = [];
      const processedIds = new Set<string>();
      
      // Parse query to extract name and ID parts
      const parseQuery = (input: string) => {
        // Handle format "Name (ID)" - extract both parts
        const match = input.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
        if (match) {
          return {
            nameQuery: match[1].trim(),
            idQuery: match[2].trim(),
            fullQuery: input.trim()
          };
        }
        return {
          nameQuery: input.trim(),
          idQuery: input.trim(),
          fullQuery: input.trim()
        };
      };

      const { nameQuery, idQuery, fullQuery } = parseQuery(query);
      console.log('🔍 Query parsing:', { nameQuery, idQuery, fullQuery });

      // Phase 1: Get all matching customers from Data_Customer (primary source with all types)
      try {
        console.log('📊 Phase 1: Searching Data_Customer for all customer types');
        const dataCustomers = await directus.request(readItems('Data_Customer' as any, {
          ...(query.trim() === '' ? {} : {
            filter: {
              _or: [
                { Nama_Pelanggan: { _icontains: nameQuery } },
                { Nama_Pelanggan: { _icontains: fullQuery } },
                { No_pelanggan: { _icontains: idQuery } },
                { No_pelanggan: { _icontains: fullQuery } },
                { Alamat: { _icontains: fullQuery } },
                { Kontak: { _icontains: fullQuery } },
                { Email: { _icontains: fullQuery } },
                { Kota: { _icontains: fullQuery } },
                { Provinsi: { _icontains: fullQuery } }
              ]
            }
          }),
          fields: ['*'],
          sort: ['Nama_Pelanggan']
        }));
        
        console.log(`✅ Found ${dataCustomers.length} customers in Data_Customer`);
        
        // Transform Data_Customer records to standard format
        for (const customer of dataCustomers) {
          if (!customer.No_pelanggan) continue;
          
          processedIds.add(customer.No_pelanggan);
          allCustomers.push({
            id: customer.id,
            idcust: customer.No_pelanggan,
            No_pelanggan: customer.No_pelanggan,
            name: customer.Nama_Pelanggan || customer.No_pelanggan,
            Nama_Pelanggan: customer.Nama_Pelanggan,
            NamaPelangganTambak: customer.Nama_Pelanggan,
            address: customer.Alamat || '',
            Alamat: customer.Alamat,
            phone: customer.Kontak || '',
            Phone: customer.Kontak,
            Contacs: customer.Kontak,
            email: customer.Email || '',
            Email: customer.Email,
            
            // CRITICAL: Include ALL Tipe_Pelanggan values
            tipe_pelanggan: customer.Tipe_Pelanggan || '',
            Tipe_Pelanggan: customer.Tipe_Pelanggan || '',
            
            // Geographic data
            Kabupaten: customer.Kota,
            Provinsi: customer.Provinsi,
            
            // Status and metadata
            status: 'active',
            priority: 'medium',
            farmIsActive: true,
            source_priority: 'Data_Customer',
            has_tambak_data: false, // Will be updated if tambak data exists
            
            // Default dates
            created_at: customer.date_created || customer.created_at || new Date().toISOString(),
            updated_at: customer.date_updated || customer.updated_at || new Date().toISOString()
          });
        }
      } catch (dataError) {
        console.warn('⚠️ Data_Customer search failed, continuing with Customer_Tambak only:', dataError);
      }
      
      // Phase 2: Get matching customers from Customer_Tambak that are NOT in Data_Customer
      try {
        console.log('🏞️ Phase 2: Searching Customer_Tambak for additional customers');
        const tambakCustomers = await directus.request(readItems('Customer_Tambak', {
          ...(query.trim() === '' ? {} : {
            filter: {
              _or: [
                { Nama_Pelanggan_Tambak: { _icontains: nameQuery } },
                { Nama_Pelanggan_Tambak: { _icontains: fullQuery } },
                { No_pelanggan: { _icontains: idQuery } },
                { No_pelanggan: { _icontains: fullQuery } },
                { Alamat: { _icontains: fullQuery } },
                { Contacs: { _icontains: fullQuery } },
                { PIC: { _icontains: fullQuery } }
              ]
            }
          }),
          fields: [
            '*',
            'PIC.id', 'PIC.first_name', 'PIC.last_name', 'PIC.email'
          ],
          sort: ['Nama_Pelanggan_Tambak']
        }));
        
        console.log(`✅ Found ${tambakCustomers.length} customers in Customer_Tambak`);
        
        // Transform Customer_Tambak records and add new ones
        for (const customer of tambakCustomers) {
          if (!customer.No_pelanggan) continue;
          
          // Check if this customer exists in Data_Customer (update tambak info)
          const existingIndex = allCustomers.findIndex(c => c.No_pelanggan === customer.No_pelanggan);
          
          if (existingIndex >= 0) {
            // Update existing customer with tambak data
            const existing = allCustomers[existingIndex];
            allCustomers[existingIndex] = {
              ...existing,
              
              // Add tambak-specific fields while preserving Data_Customer priority
              NamaPelangganTambak: customer.Nama_Pelanggan_Tambak || existing.name,
              tambak_name: customer.Nama_Pelanggan_Tambak,
              tambak_size: customer.ukuranTambak,
              ukuranTambak: customer.ukuranTambak,
              year_built: customer.tahunDibangun,
              tahunDibangun: customer.tahunDibangun,
              last_purchase_date: customer.tglPembelianTerakhir,
              tglPembelianTerakhir: customer.tglPembelianTerakhir,
              last_visit: customer.tglInteraksiTerakhir,
              tglInteraksiTerakhir: customer.tglInteraksiTerakhir,

              // Contact and PIC info - prioritize Customer_Tambak phone if available
              phone: customer.Contacs || existing.phone,
              Phone: customer.Contacs || existing.phone,
              Contacs: customer.Contacs || existing.phone,
              PIC: customer.PIC,
              pic_id: customer.PIC?.id || customer.PIC,
              contact_person: customer.PIC ?
                (typeof customer.PIC === 'object' ?
                  `${customer.PIC.first_name || ''} ${customer.PIC.last_name || ''}`.trim() || customer.PIC.email || customer.PIC.id
                  : customer.PIC)
                : '',
              assigned_pic: customer.PIC ?
                (typeof customer.PIC === 'object' ?
                  `${customer.PIC.first_name || ''} ${customer.PIC.last_name || ''}`.trim() || customer.PIC.email || customer.PIC.id
                  : customer.PIC)
                : '',
              
              // Update tambak info
              has_tambak_data: true,
              farmIsActive: customer.farmIsActive !== undefined ? customer.farmIsActive : true,
              statusAktif: customer.statusAktif,
              Description: customer.Description,
              coordinates: customer.coordinates,
              
              // Approval workflow
              approval_status: customer.approval_status || 'approved',
              approval_date: customer.approval_date,
              approval_notes: customer.approval_notes
            };
          } else if (!processedIds.has(customer.No_pelanggan)) {
            // Add new customer that only exists in Customer_Tambak
            processedIds.add(customer.No_pelanggan);
            allCustomers.push({
              id: customer.id,
              idcust: customer.No_pelanggan,
              No_pelanggan: customer.No_pelanggan,
              name: customer.Nama_Pelanggan_Tambak || customer.No_pelanggan,
              Nama_Pelanggan: customer.Nama_Pelanggan_Tambak,
              NamaPelangganTambak: customer.Nama_Pelanggan_Tambak,
              address: customer.Alamat || '',
              Alamat: customer.Alamat,
              phone: customer.Contacs || '',
              Phone: customer.Contacs,
              Contacs: customer.Contacs,
              
              // Try to get Tipe_Pelanggan from Data_Customer later if needed
              tipe_pelanggan: '', // Will be enhanced if possible
              Tipe_Pelanggan: '',
              
              // Tambak-specific data
              tambak_name: customer.Nama_Pelanggan_Tambak,
              tambak_size: customer.ukuranTambak,
              ukuranTambak: customer.ukuranTambak,
              year_built: customer.tahunDibangun,
              tahunDibangun: customer.tahunDibangun,
              last_purchase_date: customer.tglPembelianTerakhir,
              tglPembelianTerakhir: customer.tglPembelianTerakhir,
              last_visit: customer.tglInteraksiTerakhir,
              tglInteraksiTerakhir: customer.tglInteraksiTerakhir,
              
              // Contact and PIC info
              PIC: customer.PIC,
              pic_id: customer.PIC?.id || customer.PIC,
              contact_person: customer.PIC ? 
                (typeof customer.PIC === 'object' ? 
                  `${customer.PIC.first_name || ''} ${customer.PIC.last_name || ''}`.trim() || customer.PIC.email || customer.PIC.id 
                  : customer.PIC) 
                : '',
              assigned_pic: customer.PIC ? 
                (typeof customer.PIC === 'object' ? 
                  `${customer.PIC.first_name || ''} ${customer.PIC.last_name || ''}`.trim() || customer.PIC.email || customer.PIC.id 
                  : customer.PIC) 
                : '',
              
              // Status and metadata
              status: customer.statusAktif === 1 ? 'active' : 'active',
              priority: 'medium',
              farmIsActive: customer.farmIsActive !== undefined ? customer.farmIsActive : true,
              statusAktif: customer.statusAktif,
              has_tambak_data: true,
              source_priority: 'Customer_Tambak',
              Description: customer.Description,
              coordinates: customer.coordinates,
              
              // Approval workflow
              approval_status: customer.approval_status || 'approved',
              approval_date: customer.approval_date,
              approval_notes: customer.approval_notes,
              
              // Timestamps
              created_at: customer.date_created || customer.created_at || new Date().toISOString(),
              updated_at: customer.date_updated || customer.updated_at || new Date().toISOString()
            });
          }
        }
      } catch (tambakError) {
        console.warn('⚠️ Customer_Tambak search failed, continuing with Data_Customer only:', tambakError);
      }
      
      // Phase 3: Sort and paginate results
      console.log(`🔗 Phase 3: Processing ${allCustomers.length} total customers`);
      
      // Sort by name for consistency
      allCustomers.sort((a, b) => {
        const nameA = (a.name || a.NamaPelangganTambak || '').toLowerCase();
        const nameB = (b.name || b.NamaPelangganTambak || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      // Apply pagination
      const paginatedResults = allCustomers.slice(actualOffset, actualOffset + limit);
      
      console.log(`✅ INTERACTION SEARCH COMPLETE: Returning ${paginatedResults.length} customers`);
      console.log('🎯 Customer types included:', [...new Set(allCustomers.map(c => c.tipe_pelanggan).filter(Boolean))]);
      
      return {
        data: paginatedResults,
        page,
        limit,
        offset: actualOffset,
        hasMore: (actualOffset + limit) < allCustomers.length,
        totalFound: allCustomers.length
      };
      
    } catch (error) {
      console.error('❌ Comprehensive interaction search failed:', error);
      throw error;
    }
  },

  async searchCustomers(query: string, options: { 
    page?: number; 
    limit?: number; 
    offset?: number;
  } = {}) {
    // Ensure authentication before making request
    await ensureAuthenticated();
    
    const { page = 1, limit = 20, offset } = options;
    const actualOffset = offset || (page - 1) * limit;
    
    
    const searchFilter = query.trim() === '' ? {} : {
      filter: {
        _or: [
          { Nama_Pelanggan_Tambak: { _icontains: query } },
          { No_pelanggan: { _icontains: query } },
          { Alamat: { _icontains: query } }
        ]
      }
    };

    try {
      
      // Verify token is set
      await directus.getToken();
      
      const result = await directus.request(readItems('Customer_Tambak', {
        fields: ['*'],
        ...searchFilter,
        limit,
        offset: actualOffset, // Sort by most recently updated
      }));
      
      console.log('✅ Search successful, found:', result.length, 'customers for page', page);
      
      // Apply intelligent ranking to search results
      const rankedResults = this.rankSearchResults(result, query);
      console.log('🎯 Applied search ranking to results');
      
      // ENHANCEMENT: Try to get Tipe_Pelanggan for each customer from Data_Customer
      const enhancedResults = await this.enhanceCustomersWithTipePelanggan(rankedResults);
      
      return {
        data: enhancedResults,
        page,
        limit,
        offset: actualOffset,
        hasMore: result.length === limit // If we got full page, there might be more
      };
    } catch (error) {
      console.error('❌ Full search failed:', error);
      
      // Try fallback search without No_pelanggan field
      try {
        const fallbackFilter = query.trim() === '' ? {} : {
          filter: {
            _or: [
              { Nama_Pelanggan_Tambak: { _icontains: query } },
              { Alamat: { _icontains: query } }
            ]
          }
        };

        const fallbackResult = await directus.request(readItems('Customer_Tambak', {
          fields: ['*'],
          ...fallbackFilter,
          limit,
          offset: actualOffset,
        }));
        
        if (env.DEBUG) console.warn('⚠️ Note: No_pelanggan field not accessible. Check Directus permissions.');
        
        // Apply intelligent ranking to fallback results
        const rankedFallbackResults = this.rankSearchResults(fallbackResult, query);
        console.log('🎯 Applied search ranking to fallback results');
        
        return {
          data: rankedFallbackResults,
          page,
          limit,
          offset: actualOffset,
          hasMore: fallbackResult.length === limit
        };
      } catch (fallbackError) {
        console.error('❌ Fallback search also failed:', fallbackError);
        
        // Last resort: name-only search
        try {
          const nameFilter = query.trim() === '' ? {} : {
            filter: { Nama_Pelanggan_Tambak: { _icontains: query } }
          };

          const nameOnlyResult = await directus.request(readItems('Customer_Tambak', {
            fields: ['*'],
            ...nameFilter,
            limit,
            offset: actualOffset,
          }));
          
          if (env.DEBUG) console.warn('⚠️ Note: Only name search available. Check field permissions.');
          
          // Apply intelligent ranking to name-only results
          const rankedNameOnlyResults = this.rankSearchResults(nameOnlyResult, query);
          console.log('🎯 Applied search ranking to name-only results');
          
          return {
            data: rankedNameOnlyResults,
            page,
            limit,
            offset: actualOffset,
            hasMore: nameOnlyResult.length === limit
          };
        } catch (nameOnlyError) {
          console.error('❌ All search attempts failed:', nameOnlyError);
          throw error;
        }
      }
    }
  },

  // Enhanced comprehensive customer search with additional data
  async searchCustomersWithDetails(query: string, options: { 
    page?: number; 
    limit?: number; 
    offset?: number;
  } = {}) {
    await ensureAuthenticated();
    
    const { page = 1, limit = 20, offset = (page - 1) * limit } = options;
    

    try {
      // Step 1: Search customers from multiple sources
      let customerResults: any[] = [];

      // Primary search in Customer_Tambak
      try {
        const tambakResults = await directus.request(readItems('Customer_Tambak', {
          filter: {
            _or: [
              { Nama_Pelanggan_Tambak: { _icontains: query } },
              { No_pelanggan: { _icontains: query } },
              { Alamat: { _icontains: query } },
              { Contacs: { _icontains: query } },
              { PIC: { _icontains: query } },
              { Description: { _icontains: query } }
            ]
          },
          fields: [
            'id', 'No_pelanggan', 'Nama_Pelanggan_Tambak', 'Alamat', 'Contacs',
            'ukuranTambak', 'tahunDibangun', 'tglPembelianTerakhir', 'tglInteraksiTerakhir',
            'source', 'PIC', 'SIC', 'Description', 'statusAktif', 'farmIsActive'
          ],
          limit,
          offset,
          sort: ['-date_updated']
        }));
        
        customerResults = tambakResults.map((customer: any) => ({
          ...customer,
          source_table: 'Customer_Tambak'
        }));
        
      } catch (tambakError) {
        if (env.DEBUG) console.warn('⚠️ Customer_Tambak search failed:', tambakError);
      }

      // Secondary search in Data_Customer if needed
      if (customerResults.length === 0 || customerResults.length < limit) {
        try {
          const dataCustomerResults = await directus.request(readItems('Data_Customer' as any, {
            filter: {
              _or: [
                { Nama_Pelanggan: { _icontains: query } },
                { No_pelanggan: { _icontains: query } },
                { Alamat: { _icontains: query } },
                { Kontak: { _icontains: query } },
                { Kota: { _icontains: query } }
              ]
            },
            fields: [
              'id', 'No_pelanggan', 'Nama_Pelanggan', 'Alamat',
              'Kontak', 'Email', 'Tipe_Pelanggan', 'Kota', 'Provinsi'
            ],
            limit: Math.max(0, limit - customerResults.length)
          }));
          
          const mappedDataCustomer = dataCustomerResults.map((customer: any) => ({
            id: customer.id,
            No_pelanggan: customer.No_pelanggan,
            Nama_Pelanggan_Tambak: customer.Nama_Pelanggan,
            Alamat: customer.Alamat,
            Contacs: customer.Kontak,
            source_table: 'Data_Customer',
            // Additional data from Data_Customer
            Email: customer.Email,
            Type_pelanggan: customer.Type_pelanggan,
            Kabupaten: customer.Kota,
            Provinsi: customer.Provinsi
          }));
          
          customerResults = [...customerResults, ...mappedDataCustomer];
        } catch (dataCustomerError) {
          if (env.DEBUG) console.warn('⚠️ Data_Customer search failed:', dataCustomerError);
        }
      }

      // Step 2: Enhance results with phone numbers and additional details
      const enhancedResults = await Promise.all(customerResults.map(async (customer: any) => {
        try {
          // Get phone numbers for this customer
          const phoneNumbers = await this.getCustomerPhoneNumbers(customer.No_pelanggan);
          
          return {
            No_pelanggan: customer.No_pelanggan,
            customer_name: customer.Nama_Pelanggan_Tambak || customer.Nama_Pelanggan || 'Unknown Customer',
            tambak_name: customer.source_table === 'Customer_Tambak' ? customer.Nama_Pelanggan_Tambak : undefined,
            address: customer.Alamat || '',
            phone_numbers: phoneNumbers,
            primary_phone: phoneNumbers.length > 0 ? phoneNumbers[0].number : customer.Contacs || '',
            whatsapp_available: phoneNumbers.some(p => p.type === 'whatsapp'),
            phone_count: phoneNumbers.length,
            // Additional details
            farm_size: customer.ukuranTambak,
            built_year: customer.tahunDibangun,
            last_purchase: customer.tglPembelianTerakhir,
            last_interaction: customer.tglInteraksiTerakhir,
            pic: customer.PIC,
            description: customer.Description,
            status: customer.statusAktif === 1 ? 'active' : 'inactive',
            source_table: customer.source_table,
            // Data_Customer specific fields
            email: customer.Email,
            type: customer.Type_pelanggan,
            kabupaten: customer.Kota,
            provinsi: customer.Provinsi
          };
        } catch (phoneError) {
          if (env.DEBUG) console.warn(`⚠️ Failed to get phone numbers for ${customer.No_pelanggan}:`, phoneError);
          return {
            No_pelanggan: customer.No_pelanggan,
            customer_name: customer.Nama_Pelanggan_Tambak || customer.Nama_Pelanggan || 'Unknown Customer',
            tambak_name: customer.source_table === 'Customer_Tambak' ? customer.Nama_Pelanggan_Tambak : undefined,
            address: customer.Alamat || '',
            phone_numbers: [],
            primary_phone: customer.Contacs || '',
            whatsapp_available: false,
            phone_count: 0,
            farm_size: customer.ukuranTambak,
            built_year: customer.tahunDibangun,
            last_purchase: customer.tglPembelianTerakhir,
            last_interaction: customer.tglInteraksiTerakhir,
            pic: customer.PIC,
            description: customer.Description,
            status: customer.statusAktif === 1 ? 'active' : 'inactive',
            source_table: customer.source_table,
            email: customer.Email,
            type: customer.Type_pelanggan,
            kabupaten: customer.Kota,
            provinsi: customer.Provinsi
          };
        }
      }));

      return enhancedResults;

    } catch (error: any) {
      console.error('❌ Enhanced customer search failed:', error.message);
      throw error;
    }
  },

  async getNearbyCustomers(latitude: number, longitude: number, _radius: number = 5) {
    // For Customer_Tambak, we don't have lat/lng fields, so return all customers
    // In future, coordinates could be stored in a JSON field or separate lat/lng fields
    await ensureAuthenticated();
    return directus.request(readItems('Customer_Tambak', {
      fields: ['*']
    }));
  },

  async getCustomerById(customerId: string) {
    await ensureAuthenticated();
    
    try {
      // First try by id field
      const result = await directus.request(readItems('Customer_Tambak', {
        fields: ['*'],
        filter: { id: { _eq: customerId } },
        limit: 1
      }));
      
      if (result && result.length > 0) {
        return result[0];
      }
    } catch {
      // An ID lookup can fail on older schemas; continue with the customer-number fallback.
    }
    
    try {
      // Fallback: try by No_pelanggan
      const result = await directus.request(readItems('Customer_Tambak', {
        fields: ['*'],
        filter: { No_pelanggan: { _eq: customerId } },
        limit: 1
      }));
      
      return result[0] || null;
    } catch (error) {
      console.error('❌ Both id and No_pelanggan lookup failed:', error);
      return null;
    }
  },

  async getCustomerStats() {
    const customers = await this.getCustomers();
    return {
      total_customers: customers.length,
      active_customers: customers.filter(c => c.status === 'active').length,
      high_priority: customers.filter(c => c.priority === 'high').length,
      recent_visits: customers.filter(c => c.last_visit).length
    };
  },

  // Get total customer count from Data_Customer table (main customer table)
  async getTotalCustomerCount() {
    await ensureAuthenticated();
    const currentUser = authManager.getState().user;

    if (!currentUser) {
      console.error('❌ No authenticated user found');
      return { total_customers: 0 };
    }

    console.log(`📊 Counting Customer_Tambak records for PIC user: ${currentUser.id}`);

    // ONLY query Customer_Tambak with PIC filter
    try {
      const result = await directus.request(readItems('Customer_Tambak', {
        filter: {
          PIC: { _eq: currentUser.id }
        },
        limit: 0,
        meta: 'total_count'
      }));

      let totalCount = 0;
      if (result && typeof result === 'object' && 'meta' in result && result.meta) {
        totalCount = (result.meta as any).total_count || (result.meta as any).totalCount || 0;
      }

      console.log(`✅ Total Customer_Tambak count for PIC ${currentUser.id}: ${totalCount}`);
      return { total_customers: totalCount };
    } catch (error) {
      console.error('❌ Failed to count Customer_Tambak records:', error);
      return { total_customers: 0 };
    }
  },

  // Get customer basic data (No_pelanggan and Tipe_Pelanggan only) - Optimized query
  async getCustomersBasicData() {
    await ensureAuthenticated();
    
    try {
      const customers = await directus.request(readItems('Data_Customer' as any, {
        fields: ['No_pelanggan', 'Tipe_Pelanggan'], // Only fetch essential fields
        limit: -1 // Get all records
      }));
      
      return Array.isArray(customers) ? customers : [];
    } catch (error) {
      console.error('❌ Failed to fetch customer basic data:', error);
      throw error;
    }
  },

  // Get currently loaded customer count from Customer_Tambak (for display purposes)
  async getCustomerCount() {
    await ensureAuthenticated();
    
    try {
      // Use limit 0 to get only metadata without actual records
      const result = await directus.request(readItems('Customer_Tambak', {
        limit: 0,
        meta: 'total_count'
      }));
      
      
      // Check different ways the count might be returned
      let totalCount = 0;
      
      if (result && typeof result === 'object') {
        if ('meta' in result && result.meta && typeof result.meta === 'object') {
          totalCount = (result.meta as any).total_count || (result.meta as any).totalCount || 0;
        } else if ('total_count' in result) {
          totalCount = (result as any).total_count;
        } else if (Array.isArray(result) && result.length === 0) {
          // If we get an empty array, meta might be separate
          // Try to get count by requesting with limit 1
          const countResult = await directus.request(readItems('Customer_Tambak', {
            limit: 1,
            meta: 'total_count'
          }));
          
          if (countResult && typeof countResult === 'object' && 'meta' in countResult) {
            totalCount = (countResult.meta as any).total_count || (countResult.meta as any).totalCount || 0;
          }
        }
      }
      
      
      return {
        total_customers: totalCount
      };
    } catch (error) {
      console.error('❌ Failed to get Customer_Tambak count efficiently, using fallback:', error);
      // Fallback to loading all customers and counting
      const customers = await this.getCustomers();
      const totalCount = customers.length;
      return {
        total_customers: totalCount
      };
    }
  },

  // Transaction operations - for customer transaction sorting  
  async getCustomerTransactions() {
    await ensureAuthenticated();
    
    console.log('🔍 Fetching customer transaction data from laporanJualBarang');
    
    try {
      const result = await directus.request(readItems('laporanJualBarang' as any, {
        fields: [
          'id',
          'NoPelanggan',
          'TglFaktur',
          'HargaJual',
          'Jumlah',
          'NamaPelanggan'
        ],
        sort: ['-TglFaktur'], // Sort by transaction date desc
        limit: 10000 // Get a large number of recent transactions
      }));
      
      console.log('✅ Successfully fetched', result?.length || 0, 'transaction records');
      return result || [];
    } catch (error) {
      console.error('❌ Failed to fetch customer transactions:', error);
      throw error;
    }
  },

  // Optimized: Get only latest transaction per customer for sorting (prevents duplication)
  async getLatestCustomerTransactions() {
    await ensureAuthenticated();
    
    console.log('🚀 Fetching latest transaction per customer from laporanJualBarang (optimized)');
    
    try {
      // First, get all transactions sorted by date
      const allTransactions = await directus.request(readItems('laporanJualBarang' as any, {
        fields: [
          'id',
          'NoPelanggan',
          'TglFaktur',
          'HargaJual',
          'Jumlah',
          'NamaPelanggan'
        ],
        sort: ['-TglFaktur'], // Sort by transaction date desc to get latest first
        limit: 50000 // Increased limit to ensure we get all customers
      }));
      
      if (!allTransactions || allTransactions.length === 0) {
        console.log('⚠️ No transactions found in laporanJualBarang');
        return [];
      }
      
      // Deduplicate to get only latest transaction per customer
      const customerLatestMap = new Map<string, any>();
      
      for (const transaction of allTransactions) {
        const customerId = transaction.NoPelanggan;
        if (!customerId) continue;
        
        // Only keep the first occurrence (latest due to sort order)
        if (!customerLatestMap.has(customerId)) {
          customerLatestMap.set(customerId, transaction);
        }
      }
      
      const latestTransactions = Array.from(customerLatestMap.values());
      
      console.log(`✅ Optimized: Reduced ${allTransactions.length} transactions to ${latestTransactions.length} latest per customer`);
      console.log(`📊 Efficiency gain: ${Math.round((1 - latestTransactions.length / allTransactions.length) * 100)}% data reduction`);
      
      return latestTransactions;
    } catch (error) {
      console.error('❌ Failed to fetch transaction data:', error);
      throw new Error(`Failed to fetch transaction data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // NEW: Schema-compliant transaction sorting function following user's 5-step process
  // IMPROVEMENT: Only returns customers that exist in Data_Customer table (hides transaction-only customers)
  // IMPROVEMENT: Limits results to 20 valid customers (Data_Customer available only)
  async getCustomersSortedByTransactionSchema(sortOrder: 'asc' | 'desc' = 'desc', limit: number = 20) {
    await ensureAuthenticated();
    const currentUser = authManager.getState().user;

    if (!currentUser) {
      console.error('❌ No authenticated user found');
      return [];
    }

    console.log(`🔄 Starting transaction sorting for PIC user ${currentUser.id} (Customer_Tambak with PIC filter)`);
    console.log(`📊 Sort order: ${sortOrder} (${sortOrder === 'desc' ? 'newest first' : 'oldest first'})`);
    console.log(`🔢 Customer limit: ${limit}`);
    
    try {
      // Step 1: Get sort on laporanJualBarang - Sort Directus database by {asc/desc} 'TglFaktur'
      console.log('📈 Step 1: Sorting laporanJualBarang by TglFaktur');
      const sortedTransactions = await directus.request(readItems('laporanJualBarang' as any, {
        fields: [
          'id',
          'NoPelanggan', 
          'TglFaktur',
          'HargaJual',
          'Jumlah',
          'NamaPelanggan'
        ],
        sort: sortOrder === 'desc' ? ['-TglFaktur'] : ['TglFaktur'],
        limit: 50000 // Ensure we get all transactions
      }));
      
      if (!sortedTransactions || sortedTransactions.length === 0) {
        console.log('⚠️ Step 1: No transactions found in laporanJualBarang');
        return [];
      }
      
      console.log(`✅ Step 1: Found ${sortedTransactions.length} transactions sorted by TglFaktur ${sortOrder}`);
      
      // Step 2: Get list data - Retrieve the sorted transaction list
      console.log('📊 Step 2: Processing sorted transaction list');
      const transactionList = sortedTransactions;
      
      // Step 3: Search by 'noPelanggan' - Find transactions by customer ID from 'laporanJualBarang'
      console.log('🔍 Step 3: Extracting unique customer IDs (noPelanggan) from transactions');
      const uniqueCustomerIds = [...new Set(
        transactionList
          .map(transaction => transaction.NoPelanggan)
          .filter(Boolean) // Remove null/undefined values
      )];
      
      console.log(`✅ Step 3: Found ${uniqueCustomerIds.length} unique customer IDs from transaction data`);
      
      if (uniqueCustomerIds.length === 0) {
        console.log('⚠️ Step 3: No valid customer IDs found in transactions');
        return [];
      }
      
      // Step 4: REMOVED - Previously queried Data_Customer, now using Customer_Tambak with PIC filter only

      // Step 5: Get Customer_Tambak data with PIC filter
      console.log(`🏞️ Querying Customer_Tambak with PIC filter for user ${currentUser.id}`);
      let customerTambakRecords: any[] = [];

      try {
        customerTambakRecords = await directus.request(readItems('Customer_Tambak', {
          filter: {
            _and: [
              { PIC: { _eq: currentUser.id } }, // REQUIRED: PIC filter
              { No_pelanggan: { _in: uniqueCustomerIds } }
            ]
          },
          fields: ['*', 'PIC.id', 'PIC.first_name', 'PIC.last_name', 'PIC.email']
        }));

        console.log(`✅ Found ${customerTambakRecords.length} customers in Customer_Tambak for PIC ${currentUser.id}`);
      } catch (error) {
        console.warn('⚠️ Step 5: Customer_Tambak lookup failed, continuing without tambak data:', error);
        customerTambakRecords = [];
      }

      // Create lookup maps for efficient data merging (Customer_Tambak only with PIC filter)
      const tambakMap = new Map(customerTambakRecords.map(c => [c.No_pelanggan, c]));
      const transactionMap = new Map<string, any>();

      // Get latest transaction per customer (respecting sort order)
      for (const transaction of transactionList) {
        const customerId = transaction.NoPelanggan;
        if (!customerId || transactionMap.has(customerId)) continue;
        transactionMap.set(customerId, transaction);
      }

      // Build final customer list (Customer_Tambak with PIC filter only)
      const schemaCompliantCustomers: any[] = [];

      for (const customerId of uniqueCustomerIds) {
        const transaction = transactionMap.get(customerId);
        const tambakCustomer = tambakMap.get(customerId);

        if (!transaction) continue; // Skip if no transaction

        // ONLY show customers from Customer_Tambak with PIC filter
        if (!tambakCustomer) {
          console.log(`🚫 Skipping customer ${customerId} - not assigned to PIC user ${currentUser.id}`);
          continue; // Skip customers not assigned to current PIC
        }

        console.log(`✅ Customer ${customerId}: Available in Customer_Tambak for PIC ${currentUser.id}`);
        
        // Name validation for Customer_Tambak
        const customerName = tambakCustomer.Nama_Pelanggan_Tambak?.trim() || transaction.NamaPelanggan?.trim();

        if (!customerName) {
          console.log(`🚫 Skipping customer ${customerId} - no valid name found`);
          continue; // Skip customers without valid names
        }
        // Build customer object from Customer_Tambak + transaction data (PIC filtered only)
        const customer = {
          // Primary identifier from Customer_Tambak
          id: tambakCustomer.id,
          idcust: customerId,
          No_pelanggan: customerId,

          // Name from Customer_Tambak (validated above)
          name: customerName,
          Nama_Pelanggan: customerName,
          Nama_Pelanggan_Tambak: tambakCustomer.Nama_Pelanggan_Tambak,
          NamaPelangganTambak: tambakCustomer.Nama_Pelanggan_Tambak,

          // Address from Customer_Tambak
          address: tambakCustomer.Alamat || '',
          Alamat: tambakCustomer.Alamat,

          // Contact information from Customer_Tambak
          phone: tambakCustomer.Contacs || '',
          contact_person: tambakCustomer.PIC ?
            `${tambakCustomer.PIC.first_name || ''} ${tambakCustomer.PIC.last_name || ''}`.trim() || tambakCustomer.PIC.email : '',
          pic_id: tambakCustomer.PIC?.id,
          assigned_pic: tambakCustomer.PIC ?
            `${tambakCustomer.PIC.first_name || ''} ${tambakCustomer.PIC.last_name || ''}`.trim() || tambakCustomer.PIC.email : '',

          // Transaction data (latest for this customer)
          last_transaction_date: transaction.TglFaktur,
          last_transaction_amount: transaction.HargaJual,
          last_transaction_quantity: transaction.Jumlah,

          // Additional fields for compatibility
          priority: 'medium' as const,
          status: 'active' as const,
          farmIsActive: tambakCustomer.farmIsActive !== undefined ? tambakCustomer.farmIsActive : true,
          last_visit: tambakCustomer.tglInteraksiTerakhir || '',
          lastInteraction: tambakCustomer.tglInteraksiTerakhir ?
            new Date(tambakCustomer.tglInteraksiTerakhir).toLocaleDateString() : 'Belum ada',
          revenue: transaction.HargaJual ? `Rp ${Number(transaction.HargaJual).toLocaleString('id-ID')}` : 'Rp 0',
          distance: '-',

          // Data source tracking (Customer_Tambak with PIC filter only)
          data_source: 'Customer_Tambak_PIC_Filtered',
          has_tambak_data: true,
          primary_source: 'Customer_Tambak',
          transaction_date: transaction.TglFaktur,

          // Timestamps from Customer_Tambak
          created_at: tambakCustomer.created_at || transaction.created_at || new Date().toISOString(),
          updated_at: tambakCustomer.updated_at || transaction.updated_at || new Date().toISOString(),

          // Schema compliance markers
          schema_compliant: true,
          sort_method: 'transaction_schema',
          sort_order: sortOrder
        };
        
        schemaCompliantCustomers.push(customer);
      }
      
      console.log('🎯 Schema-compliant sorting completed successfully!');
      console.log(`📊 Final result: ${schemaCompliantCustomers.length} customers sorted by transaction date ${sortOrder}`);
      console.log(`🔍 Filters applied: 1) Must exist in Customer_Tambak for the current PIC, 2) Must have a valid name`);
      console.log(`📈 Visibility: ${schemaCompliantCustomers.length} customers shown (from ${customerTambakRecords.length} PIC-filtered Customer_Tambak records and ${uniqueCustomerIds.length} unique transaction customers)`);
      console.log(`📈 Top customer by ${sortOrder} transaction:`, {
        id: schemaCompliantCustomers[0]?.idcust,
        name: schemaCompliantCustomers[0]?.name,
        transaction_date: schemaCompliantCustomers[0]?.transaction_date
      });
      
      if (schemaCompliantCustomers.length > 1) {
        console.log(`📉 Last customer by ${sortOrder} transaction:`, {
          id: schemaCompliantCustomers[schemaCompliantCustomers.length - 1]?.idcust,
          name: schemaCompliantCustomers[schemaCompliantCustomers.length - 1]?.name,
          transaction_date: schemaCompliantCustomers[schemaCompliantCustomers.length - 1]?.transaction_date
        });
      }
      
      return schemaCompliantCustomers;
      
    } catch (error) {
      console.error('❌ Schema-compliant transaction sorting failed:', error);
      throw new Error(`Schema-compliant transaction sorting failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Phone number operations
  async getCustomerPhoneNumbers(customerIdcust: string) {
    await ensureAuthenticated();
    
    const phoneNumbers: Array<{
      number: string;
      source: 'phone_customer' | 'customer_tambak';
      type: 'whatsapp' | 'phone';
    }> = [];


    try {
      // First, get customer data from Customer_Tambak using idcust
      const customerData = await directus.request(readItems('Customer_Tambak', {
        filter: { No_pelanggan: { _eq: customerIdcust } },
        limit: 1
      }));
      

      if (customerData && customerData.length > 0 && customerData[0].Contacs) {
        // Parse Contacs field (could be comma-separated)
        const contacts = customerData[0].Contacs.split(',').map((contact: string) => contact.trim());
        contacts.forEach((contact: string) => {
          if (contact) {
            // All Indonesian mobile numbers can be WhatsApp numbers
            // But we'll mark them as 'whatsapp' only if they're mobile numbers
            const isMobile = contact.match(/^\+?62[8-9]|^08/);
            phoneNumbers.push({
              number: contact,
              source: 'customer_tambak',
              type: isMobile ? 'whatsapp' : 'phone'
            });
          }
        });
      }

      // Then, get phone numbers from Phone_Customer table
      const phoneCustomerData = await directus.request(readItems('Phone_Customer', {
        filter: { No_pelanggan: { _eq: customerIdcust } }
      }));
      

      phoneCustomerData.forEach((phoneEntry: any) => {
        if (phoneEntry.Phone_Parsed) {
          // Phone_Parsed is comma-separated
          const parsedNumbers = phoneEntry.Phone_Parsed.split(',').map((phone: string) => phone.trim());
          parsedNumbers.forEach((phone: string) => {
            if (phone) {
              // Detect if it's a mobile number (potential WhatsApp)
              const isMobile = phone.match(/^\+?62[8-9]|^08/);
              
              phoneNumbers.push({
                number: phone,
                source: 'phone_customer',
                type: isMobile ? 'whatsapp' : 'phone'
              });
            }
          });
        }

        if (phoneEntry.Phone_Number) {
          // Also add the raw phone number
          const isMobile = phoneEntry.Phone_Number.match(/^\+?62[8-9]|^08/);

          phoneNumbers.push({
            number: phoneEntry.Phone_Number,
            source: 'phone_customer',
            type: isMobile ? 'whatsapp' : 'phone'
          });
        }
      });

      // Remove duplicates
      const uniqueNumbers = phoneNumbers.filter((phone, index, self) => 
        index === self.findIndex(p => p.number === phone.number)
      );

      return uniqueNumbers;
    } catch (error) {
      console.error('Error fetching customer phone numbers:', error);
      console.error('Error details:', error);
      throw error;
    }
  },

  // File operations
  async uploadFile(file: File) {
    await ensureAuthenticated();
    const formData = new FormData();
    formData.append('file', file);
    return directus.request(uploadFiles(formData));
  },

  /**
   * Create a customer interaction file record
   * @param fileId - The uploaded file ID from Directus
   * @param userId - The user who uploaded the file (optional)
   * @returns Promise<any> - The created customerInteractionFile record
   */
  async createCustomerInteractionFile(fileId: string, userId?: string) {
    await ensureAuthenticated();

    const data: any = {
      image: fileId,
      date_created: new Date().toISOString(),
      date_updated: new Date().toISOString()
    };

    // Add user_created if provided
    if (userId) {
      data.user_created = userId;
    }

    return directus.request(createItem('customerInteractionFile', data));
  },

  /**
   * Upload file and create customerInteractionFile record in one operation
   * @param file - File to upload
   * @param userId - The user who uploaded the file (optional)
   * @returns Promise<{fileRecord: any, interactionFileRecord: any}> - Both records
   */
  async uploadAndCreateCustomerInteractionFile(file: File, userId?: string) {
    try {
      await ensureAuthenticated();

      // First upload the file
      const fileUploadResult = await this.uploadFile(file);
      const fileId = fileUploadResult.id;

      // Then create the customerInteractionFile record
      const interactionFileRecord = await this.createCustomerInteractionFile(fileId, userId);

      return {
        fileRecord: fileUploadResult,
        interactionFileRecord: interactionFileRecord,
        fileId: fileId
      };
    } catch (error) {
      console.error('Error uploading file and creating interaction file record:', error);
      throw error;
    }
  },

  /**
   * Get authenticated asset URL for files like avatars
   * @param fileId - The Directus file ID (UUID)
   * @param transforms - Optional transform parameters (width, height, fit, etc.)
   * @returns Promise<string> - The authenticated asset URL
   */
  async getAssetUrl(fileId: string, transforms: Record<string, string | number> = {}): Promise<string> {
    if (!fileId) {
      throw new Error('File ID is required');
    }

    // Ensure we have a valid authentication token
    await ensureAuthenticated();
    const token = authManager.getPreferredToken();
    
    if (!token) {
      throw new Error('No authentication token available for asset access');
    }

    // Build the base URL
    const baseUrl = `${env.VITE_DIRECTUS_URL}/assets/${fileId}`;
    
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add the authentication token
    params.append('access_token', token);
    
    // Add transform parameters
    Object.entries(transforms).forEach(([key, value]) => {
      params.append(key, String(value));
    });
    
    const finalUrl = `${baseUrl}?${params.toString()}`;
    
    return finalUrl;
  },

  /**
   * Get authenticated avatar URL with default transforms
   * @param fileId - The Directus file ID for the avatar
   * @returns Promise<string> - The authenticated avatar URL with standard transforms
   */
  async getAvatarUrl(fileId: string): Promise<string> {
    return this.getAssetUrl(fileId, {
      width: 200,
      height: 200,
      fit: 'cover',
      quality: 80
    });
  },

  // ============================================================================
  // CUSTOMER VISITS OPERATIONS - sesuai dengan structure tabel emp_visits
  // ============================================================================

  /**
   * Create a new customer visit
   */
  async createVisit(visitData: {
    customer_id?: number;
    customer_name: string;
    customer_address: string;
    latitude: number;
    longitude: number;
    notes?: string;
    customer_tambak_id?: string; // Field yang masuk dari frontend
    coordinates?: { lat: number; lng: number }; // Coordinates object dari frontend
    start_time?: string;
    visit_purpose?: string;
    visit_type?: string;
    user_id?: string;
    location_accuracy?: number;
    // Fields for linking visit to interaction (for auto-completion)
    original_interaction_id?: number;
    isFromInteraction?: boolean;
  }) {
    await ensureAuthenticated();
    
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');

    // Format visit_date dari start_time atau current WIB time
    const visitDate = visitData.start_time || getWIBTime();
    
    // Format coordinates sebagai JSON object untuk visit_coordinates
    let coordinates = null;
    if (visitData.coordinates) {
      coordinates = {
        lat: visitData.coordinates.lat,
        lng: visitData.coordinates.lng
      };
    } else if (visitData.latitude && visitData.longitude) {
      coordinates = {
        lat: visitData.latitude,
        lng: visitData.longitude
      };
    }
    
    // Get current user data for PIC field
    const currentUserData = authManager.getState().user;
    const picValue = currentUserData?.id || 'unknown';
    
    const payload = {
      // Map to actual customer_visits database fields
      visit_date: visitDate, // REQUIRED field
      customer_tambak_pelanggan: visitData.customer_tambak_id || 'Unknown', // Customer identifier (prefer ID)
      PIC: picValue, // Current user ID
      status: visitData.status || 'active', // Use proper status values
      visit_location: visitData.customer_address || `Location for ${visitData.customer_name}`,
      visit_coordinates: coordinates,
      visit_purpose: visitData.visit_purpose || 'Customer interaction follow-up',
      // Ensure customer name is prominently stored in visit_notes for extraction
      visit_notes: `Visit for ${visitData.customer_name}. ${visitData.notes || visitData.visit_purpose || 'Customer interaction follow-up'}.`,
      // Fields for linking visit to interaction (for auto-completion)
      original_interaction_id: visitData.original_interaction_id || null,
      isFromInteraction: visitData.isFromInteraction || false
    };
    
    // Debug log to verify payload in development
    if (import.meta.env?.DEV) {
      console.log('🚀 createVisit payload debug:', {
        customer_name_input: visitData.customer_name,
        customer_tambak_id_input: visitData.customer_tambak_id,
        payload_customer_tambak_pelanggan: payload.customer_tambak_pelanggan,
        payload_visit_notes: payload.visit_notes,
        // Debug interaction linking fields
        original_interaction_id_input: visitData.original_interaction_id,
        isFromInteraction_input: visitData.isFromInteraction,
        payload_original_interaction_id: payload.original_interaction_id,
        payload_isFromInteraction: payload.isFromInteraction
      });
    }

    
    const result = await directus.request(createItem('customer_visits' as any, payload));
    
    // Transform the result to match the frontend Visit interface
    return transformCustomerVisitToVisit(result);
  },

  /**
   * Get visits list
   */
  async getVisits(limit: number = 50) {
    await ensureAuthenticated();
    
    const currentUser = authManager.getState().user;
    if (!currentUser) {
      console.error('❌ No authenticated user found for getVisits');
      throw new Error('No authenticated user');
    }
    
    // FIXED: Filter visits by current logged-in user for accurate "This Week" calculation
    const visits = await directus.request(readItems('customer_visits' as any, {
      filter: {
        user_created: { _eq: currentUser.id } // Only get visits created by current logged-in user
      },
      limit,
      sort: ['-visit_date'], // Use actual database field
      fields: [
        'id',
        'visit_date',
        'visit_location',
        'visit_coordinates',
        'visit_purpose',
        'visit_notes',
        'PIC',
        'status',
        'date_created',
        'date_updated',
        'customer_tambak_pelanggan',
        'Progress',
        'end_time',
        'satisfaction_rating',
        'user_created'
      ]
    }));
    
    console.log(`📊 getVisits: Fetched ${visits.length} visits for current user ${currentUser.id} (${currentUser.first_name} ${currentUser.last_name})`);
    
    // Transform each visit to match frontend interface
    return visits.map(transformCustomerVisitToVisit);
  },

  /**
   * Get active visit (status = 'active')
   */
  async getActiveVisit() {
    await ensureAuthenticated();
    
    const currentUser = authManager.getState().user;
    if (!currentUser) {
      console.error('❌ No authenticated user found for getActiveVisit');
      throw new Error('No authenticated user');
    }
    
    
    try {
      // First try without user filtering to see if we can access the data
      const result = await directus.request(readItems('customer_visits' as any, {
        filter: { 
          status: { _eq: 'active' } // Use correct status value for customer_visits
        },
        limit: 1,
        sort: ['-visit_date'],
        fields: [
          'id',
          'visit_date',
          'visit_location',
          'visit_coordinates',
          'visit_purpose',
          'visit_notes',
          'PIC',
          'status',
          'date_created',
          'date_updated',
          'customer_tambak_pelanggan',
          'Progress',
          'end_time',
          'satisfaction_rating',
          'user_created'
        ]
      }));
      
      
      // If we get data, try to filter by user if possible
      if (result.length > 0) {
        
        // Try to get the first visit to see if it belongs to current user
        const firstVisit = result[0];
        
        // Return the first active visit, transformed to frontend format
        // Note: User filtering should be implemented at Directus permission level
        return transformCustomerVisitToVisit(firstVisit);
      }
      
      return null;
    } catch (error: any) {
      console.error('❌ Error getting active visit:', error);
      
      // Handle specific error cases
      if (error?.status === 403) {
        if (env.DEBUG) console.warn('⚠️ 403 Forbidden - User may not have permission to access emp_visits');
        // Return null instead of throwing to prevent app crash
        return null;
      }
      
      if (error?.status === 401) {
        if (env.DEBUG) console.warn('⚠️ 401 Unauthorized - Token may be expired, attempting refresh');
        try {
          await authManager.forceTokenRefresh();
          // Retry once after refresh
          const retryResult = await directus.request(readItems('customer_visits' as any, {
            filter: { 
              status: { _eq: 'active' } 
            },
            limit: 1,
            sort: ['-visit_date'],
            fields: [
              'id',
              'visit_date',
              'visit_location',
              'visit_coordinates',
              'visit_purpose',
              'visit_notes',
              'PIC',
              'status',
              'customer_tambak_pelanggan',
              'Progress',
              'end_time',
              'satisfaction_rating'
            ]
          }));
          return retryResult[0] ? transformCustomerVisitToVisit(retryResult[0]) : null;
        } catch (retryError: any) {
          console.error('❌ Retry after token refresh failed:', retryError);
          return null;
        }
      }
      
      // For other errors, return null to prevent app crash
      return null;
    }
  },

  /**
   * Update visit 
   */
  async updateVisit(visitId: string, data: {
    visit_notes?: string;
    status?: string;
    visit_purpose?: string;
  }) {
    await ensureAuthenticated();
    
    return await directus.request(updateItem('customer_visits' as any, visitId, {
      ...data,
      date_updated: getWIBTimeForDirectus()
    }));
  },

  /**
   * End visit (set status to 'completed')
   * Also updates related interaction's done field to true if visit was created from instant visit
   */
  async endVisit(visitId: string) {
    await ensureAuthenticated();

    try {
      // First, get the visit details to check for original_interaction_id
      const visit = await directus.request(readItem('customer_visits' as any, visitId));

      // Update the visit status to completed
      const updatedVisit = await directus.request(updateItem('customer_visits' as any, visitId, {
        status: 'completed',
        date_updated: getWIBTimeForDirectus()
      }));

      // If this visit was created from an instant visit (has original_interaction_id),
      // update the related interaction's done field to true
      if (visit.original_interaction_id) {
        try {
          await directus.request(updateItem('customer_Interaction' as any, visit.original_interaction_id, {
            done: true
          }));
          console.log('✅ Successfully marked interaction as done:', visit.original_interaction_id);
        } catch (interactionError) {
          console.warn('⚠️ Failed to update interaction done status, but visit ended successfully:', interactionError);
          // Don't throw here - visit ending is more important than interaction update
        }
      }

      return updatedVisit;
    } catch (error) {
      console.error('❌ Failed to end visit:', error);
      throw error;
    }
  },

  /**
   * Pause visit (set status to 'paused')
   */
  async pauseVisit(visitId: string) {
    await ensureAuthenticated();
    
    return await directus.request(updateItem('customer_visits' as any, visitId, {
      status: 'paused',
      date_updated: getWIBTimeForDirectus()
    }));
  },

  /**
   * Resume visit (set status back to 'active')
   */
  async resumeVisit(visitId: string) {
    await ensureAuthenticated();
    
    return await directus.request(updateItem('customer_visits' as any, visitId, {
      status: 'active',
      date_updated: getWIBTimeForDirectus()
    }));
  },

  /**
   * Get visit by ID
   */
  async getVisitById(visitId: string) {
    await ensureAuthenticated();
    
    return await directus.request(readItems('customer_visits' as any, {
      filter: { id: { _eq: visitId } },
      limit: 1,
      fields: [
        'id',
        'customer_tambak_id',
        'visit_date',
        'visit_location',
        'visit_coordinates',
        'visit_purpose',
        'visit_notes',
        'PIC',
        'status',
        'date_created',
        'date_updated'
      ]
    }));
  },

  // ============================================================================
  // CUSTOMER INTERACTION OPERATIONS - customer_Interaction table
  // ============================================================================

  /**
   * Get customer interactions with related data
   * ⚠️ SECURITY WARNING: This function does NOT enforce user filtering.
   * Use getUserCustomerInteractions() or getUserCustomerInteractionsWithFilter() instead.
   * This function should only be used for admin operations or when user filtering is explicitly handled.
   */
  async getCustomerInteractions(options: {
    limit?: number;
    offset?: number;
    filter?: any;
    sort?: string[];
  } = {}) {
    await ensureAuthenticated();
    
    // 🛡️ SECURITY GUARD: Log usage for monitoring potential security issues
    console.warn('⚠️ SECURITY: getCustomerInteractions() called without user filtering. Consider using getUserCustomerInteractions() instead.');
    
    const { limit = 50, offset = 0, filter = {}, sort = ['-date_created'] } = options;
    
    try {
      // Step 1: Get basic interaction records without problematic relationships
      const interactions = await executeWithRetry(async () => {
        return await directus.request(readItems('customer_Interaction' as any, {
          fields: [
            'id',
            'No_pelanggan',
            'date_created',
            'priority',
            'keterangan',
            'interaction_type',
            'PIC',
            'customer_tambak_id',
            'visit_id',
            'done' // CRITICAL: Include done field for status determination
          ],
          limit,
          offset,
          filter,
          sort
        }));
      }, 'get customer interactions');


      // Step 2: Get unique customer numbers for name lookup
      const customerNumbers = [...new Set(interactions.map(item => item.No_pelanggan).filter(Boolean))];

      // Step 3: Get customer names from both tables with comprehensive lookup
      let customerLookup: Record<string, string> = {};
      if (customerNumbers.length > 0) {
        // ✅ PERBAIKAN: Lookup dari kedua tabel untuk cakupan maksimal

        // Step 3.1: Coba Data_Customer table terlebih dahulu
        try {
          const dataCustomers = await directus.request(readItems('Data_Customer' as any, {
            filter: {
              No_pelanggan: { _in: customerNumbers }
            },
            fields: ['No_pelanggan', 'Nama_Pelanggan']
          }));

          customerLookup = { ...customerLookup, ...dataCustomers.reduce((acc: Record<string, string>, customer: any) => {
            if (customer.No_pelanggan && customer.Nama_Pelanggan) {
              acc[customer.No_pelanggan] = customer.Nama_Pelanggan;
            }
            return acc;
          }, {})};

          console.log(`✅ Found ${dataCustomers.length} customer names from Data_Customer`);
        } catch (dataCustomerError) {
          console.warn('⚠️ Data_Customer lookup failed:', dataCustomerError?.message);
        }

        // Step 3.2: Tambahkan Customer_Tambak table untuk melengkapi data yang belum ada
        try {
          // Filter to find customers NOT already in customerLookup
          const customersToLookup = customerNumbers.filter(no => !customerLookup[no]);

          // Only query if there are customers to lookup (avoid empty array error)
          let tambakFilter = {};
          if (customersToLookup.length > 0) {
            tambakFilter = {
              No_pelanggan: { _in: customersToLookup }
            };
          }

          const tambakCustomers = customersToLookup.length > 0
            ? await directus.request(readItems('Customer_Tambak', {
                filter: tambakFilter,
                fields: ['No_pelanggan', 'Nama_Pelanggan_Tambak']
              }))
            : [];

          const tambakLookup = tambakCustomers.reduce((acc: Record<string, string>, customer: any) => {
            if (customer.No_pelanggan && customer.Nama_Pelanggan_Tambak) {
              acc[customer.No_pelanggan] = customer.Nama_Pelanggan_Tambak;
            }
            return acc;
          }, {});

          // Merge dengan existing lookup (Data_Customer lebih diprioritaskan)
          customerLookup = { ...tambakLookup, ...customerLookup };

          console.log(`✅ Found ${tambakCustomers.length} customer names from Customer_Tambak`);
        } catch (tambakError) {
          console.warn('⚠️ Customer_Tambak lookup failed:', tambakError?.message);
        }

        console.log(`📊 Total customer names found: ${Object.keys(customerLookup).length}/${customerNumbers.length}`);

        // Log untuk debugging
        const missingCustomers = customerNumbers.filter(no => !customerLookup[no]);
        if (missingCustomers.length > 0) {
          console.log(`❌ Missing customer names for: ${missingCustomers.join(', ')}`);
        }
      }

      // Step 4: Transform the data with proper string values
      return interactions.map((item: any) => ({
        ...item,
        customer_name: customerLookup[item.No_pelanggan] || item.No_pelanggan || 'Unknown Customer',
        pic_name: 'Unknown PIC', // Will handle PIC lookup separately if needed
        tambak_name: null // Will handle tambak lookup separately if needed
      }));

    } catch (error) {
      console.error('❌ Failed to fetch customer interactions:', error);
      throw error;
    }
  },

  /**
   * Get customer interactions for specific customer - 🛡️ SECURITY: Only returns current user's interactions
   */
  async getCustomerInteractionsByCustomer(customerId: string, limit: number = 20) {
    // 🛡️ SECURITY FIX: Use getUserCustomerInteractionsWithFilter to enforce PIC filtering
    return this.getUserCustomerInteractionsWithFilter({
      limit,
      additionalFilter: {
        No_pelanggan: { _eq: customerId }
      }
    });
  },

  /**
   * Get customer interactions for current user
   */
  async getUserCustomerInteractions(limit: number = 50) {
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');

    return this.getCustomerInteractions({
      limit,
      filter: {
        PIC: { _eq: currentUser.id }
      }
    });
  },

  /**
   * Get user's customer interactions with additional filtering
   */
  async getUserCustomerInteractionsWithFilter(options: {
    limit?: number;
    offset?: number;
    additionalFilter?: any;
    sort?: string[];
  } = {}) {
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');

    const { limit = 1000, offset = 0, additionalFilter = {}, sort = ['-date_created'] } = options;

    // Combine user filter with additional filters
    const filter = {
      _and: [
        { PIC: { _eq: currentUser.id } },
        additionalFilter
      ]
    };

    return this.getCustomerInteractions({
      limit,
      offset,
      filter,
      sort
    });
  },

  /**
   * Create new customer interaction
   */
  async createCustomerInteraction(interactionData: {
    No_pelanggan: string;
    keterangan?: string;
    interaction_type?: string;
    customer_tambak_id?: number;
    visit_id?: number;
    priority?: 'low' | 'medium' | 'high';
    date_created?: string;
    done?: boolean;
    progress?: string | null; // ✅ Add progress field to interface
  }) {
    await ensureAuthenticated();
    
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');

    const data = {
      No_pelanggan: interactionData.No_pelanggan,
      keterangan: interactionData.keterangan || '',
      interaction_type: interactionData.interaction_type || null,
      PIC: currentUser.id,
      customer_tambak_id: interactionData.customer_tambak_id || null,
      visit_id: interactionData.visit_id || null,
      priority: interactionData.priority || 'low',
      done: interactionData.done !== undefined ? interactionData.done : false, // Default to false if not specified
      Progress: interactionData.progress || null, // ✅ Add Progress field for CREATE operations
      ...(interactionData.date_created && { date_created: interactionData.date_created })
    };

    return await executeWithRetry(async () => {
      return await directus.request(createItem('customer_Interaction' as any, data));
    }, 'create customer interaction');
  },

  /**
   * Update customer interaction - 🛡️ SECURITY: Validates user ownership before update
   */
  async updateCustomerInteraction(interactionId: number, updateData: {
    keterangan?: string;
    interaction_type?: string;
    customer_tambak_id?: number;
    visit_id?: number;
    priority?: 'low' | 'medium' | 'high';
    done?: boolean | null;
    date_created?: string; // Allow date rescheduling
    completed_date?: string; // Completion timestamp
    file?: (string | number)[]; // UPDATED: Array of customerInteractionFile IDs (UUIDs or integers)
    Progress?: 'Visit' | 'Presentation' | 'Trial' | 'Rejected' | 'MOU Signed' | null; // Progress status field
    Hidden?: boolean; // Hide interaction from view when true
  }) {
    await ensureAuthenticated();

    // 🛡️ SECURITY VALIDATION: Verify user owns this interaction before updating
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');

    // First, verify ownership by getting the interaction
    let existingInteraction;
    try {
      console.log(`🔍 SECURITY: Checking ownership for interaction ${interactionId} by user ${currentUser.id}`);
      existingInteraction = await executeWithRetry(async () => {
        return await directus.request(readItem('customer_Interaction' as any, interactionId, {
          fields: ['id', 'PIC']
        }));
      }, 'verify interaction ownership');
    } catch (readError: any) {
      console.error(`❌ SECURITY: Failed to read interaction ${interactionId}:`, readError);
      throw new Error(`🛡️ SECURITY: Cannot verify ownership - ${readError.message || 'read permission denied'}`);
    }

    if (!existingInteraction) {
      throw new Error('Interaction not found');
    }

    console.log(`📋 SECURITY: Interaction ${interactionId} PIC=${existingInteraction.PIC}, Current user=${currentUser.id}`);

    if (existingInteraction.PIC !== currentUser.id) {
      // EXCEPTION: Allow visit completion updates (done, visit_id, keterangan) from any user
      const isVisitCompletionUpdate = (
        Object.keys(updateData).length <= 3 && // Max 3 fields
        Object.keys(updateData).every(key => ['done', 'visit_id', 'keterangan'].includes(key)) &&
        updateData.done === true && // Must be marking as done
        updateData.visit_id // Must be linking to a visit
      );

      if (!isVisitCompletionUpdate) {
        throw new Error('🛡️ SECURITY: Access denied - user does not own this interaction');
      }

      console.log('✅ SECURITY: Allowing visit completion update for interaction', interactionId, 'by user', currentUser.id);
    } else {
      // User owns the interaction - validate that this is a safe interaction update
      const allowedKeys = ['keterangan', 'interaction_type', 'file', 'priority', 'customer_tambak_id', 'done', 'visit_id', 'date_created', 'completed_date', 'status', 'Progress'];
      const isValidInteractionUpdate = Object.keys(updateData).every(key => allowedKeys.includes(key));

      if (!isValidInteractionUpdate) {
        const invalidKeys = Object.keys(updateData).filter(key => !allowedKeys.includes(key));
        throw new Error(`🛡️ SECURITY: Invalid update fields: ${invalidKeys.join(', ')}`);
      }

      console.log('✅ SECURITY: Allowing interaction update for interaction', interactionId, 'by PIC user', currentUser.id, 'with fields:', Object.keys(updateData));
    }

    // 🎯 PROACTIVE OPTIMIZATION: If file field present, try optimized format first
    let optimizedPayload = updateData;
    if (updateData.file && Array.isArray(updateData.file) && updateData.file.length > 0) {
      console.log(`🎯 PROACTIVE: Optimizing file array format for better compatibility...`);

      // Check if all file IDs are UUIDs (strings) and try to optimize
      const hasUUIDs = updateData.file.every(id => typeof id === 'string' && id.includes('-'));

      if (hasUUIDs) {
        console.log(`🎯 PROACTIVE: Detected UUID format, keeping as strings for M2M relationship`);
        // Keep as strings but ensure clean format
        optimizedPayload = {
          ...updateData,
          file: updateData.file.map(id => String(id).trim())
        };
      }

      console.log(`🎯 PROACTIVE: Using optimized payload:`, optimizedPayload);
    }

    try {
      console.log(`🔧 SECURITY: Updating interaction ${interactionId} with optimized payload:`, optimizedPayload);

      const result = await executeWithRetry(async () => {
        return await directus.request(updateItem('customer_Interaction' as any, interactionId, optimizedPayload));
      }, 'update customer interaction');
      console.log(`✅ SECURITY: Successfully updated interaction ${interactionId}`);
      return result;
    } catch (updateError: any) {
      console.error(`❌ SECURITY: Failed to update interaction ${interactionId}:`, updateError);

      // 🔍 DEBUG: Log error structure for troubleshooting
      console.log(`🔍 ERROR DEBUG: Error structure analysis:`, {
        hasResponse: !!updateError?.response,
        responseStatus: updateError?.response?.status,
        directStatus: updateError?.status,
        hasResponseData: !!updateError?.response?.data,
        hasErrors: !!updateError?.errors,
        errorMessage: updateError?.response?.data?.errors?.[0]?.message || updateError?.errors?.[0]?.message
      });

      // 🛡️ PERMISSION HANDLING: If 403 error and file field present, try without file field
      const is403Error = updateError?.response?.status === 403 || updateError?.status === 403 || (updateError?.response && updateError.response.status === 403);

      // Check for FORBIDDEN code in multiple possible locations
      const errorCode = updateError?.response?.data?.errors?.[0]?.extensions?.code ||
                       updateError?.errors?.[0]?.extensions?.code ||
                       updateError?.response?.errors?.[0]?.extensions?.code;

      // Check for permission message in multiple possible locations
      const errorMessage = updateError?.response?.data?.errors?.[0]?.message ||
                           updateError?.errors?.[0]?.message ||
                           updateError?.response?.errors?.[0]?.message || '';

      const isForbiddenError = errorCode === 'FORBIDDEN' ||
                              errorMessage.toLowerCase().includes('permission') ||
                              errorMessage.toLowerCase().includes('forbidden');

      // 🔍 DETAILED FALLBACK LOGGING: Log all conditions for debugging
      console.log(`🔍 FALLBACK CONDITIONS:`, {
        is403Error,
        isForbiddenError,
        hasFileField: !!optimizedPayload.file,
        fileCount: optimizedPayload.file?.length || 0,
        shouldTriggerFallback: is403Error && isForbiddenError && optimizedPayload.file,
        errorResponseStatus: updateError?.response?.status,
        errorDirectStatus: updateError?.status,
        detectedErrorCode: errorCode,
        detectedErrorMessage: errorMessage,
        fullErrorStructure: {
          hasResponse: !!updateError?.response,
          hasDirectErrors: !!updateError?.errors,
          hasResponseErrors: !!updateError?.response?.errors,
          hasResponseData: !!updateError?.response?.data
        }
      });

      if (is403Error && isForbiddenError && optimizedPayload.file) {
        console.log(`⚠️ PERMISSION: File field permission denied. Retrying without file field...`);
        console.log(`📎 FALLBACK: Files were uploaded to customerInteractionFile but cannot be linked due to Directus permissions`);
        console.log(`🔧 FALLBACK: Uploaded file IDs: ${optimizedPayload.file.join(', ')}`);

        // Create fallback payload without file field
        const fallbackData = { ...optimizedPayload };
        delete fallbackData.file;

        try {
          const fallbackResult = await executeWithRetry(async () => {
            return await directus.request(updateItem('customer_Interaction' as any, interactionId, fallbackData));
          }, 'update customer interaction (without file field)');

          console.log(`✅ FALLBACK: Successfully updated interaction ${interactionId} (without file linking)`);

          // 🔧 MANUAL JUNCTION: Try to create junction records manually as a backup
          console.log(`🔧 MANUAL JUNCTION: Attempting to create junction records manually...`);
          let manualJunctionSuccess = 0;
          const failedJunctions: (string | number)[] = [];

          for (const fileId of optimizedPayload.file) {
            try {
              await directus.request(createItem('customer_Interaction_customerInteractionFile' as any, {
                customer_Interaction_id: interactionId,
                customerInteractionFile_id: fileId
              }));
              console.log(`✅ MANUAL JUNCTION: Created link for file ${fileId}`);
              manualJunctionSuccess++;
            } catch (junctionError: any) {
              failedJunctions.push(fileId);
              console.error(`❌ MANUAL JUNCTION: Failed to create link for file ${fileId}:`, junctionError);
            }
          }

          if (manualJunctionSuccess > 0) {
            console.log(`✅ NOTICE: ${manualJunctionSuccess}/${optimizedPayload.file.length} files manually linked via junction table`);
          } else {
            if (failedJunctions.length > 0) {
              console.log(`⚠️ WARNING: ${failedJunctions.length}/${optimizedPayload.file.length} files failed to link - May need manual admin intervention`);
              console.log(`❌ FAILED FILE IDs: ${failedJunctions.join(', ')}`);
            } else {
              console.log(`⚠️ NOTICE: Files uploaded but not linked - Directus admin must fix file field permissions`);
            }
          }

          return fallbackResult;
        } catch (fallbackError: any) {
          console.error(`❌ FALLBACK: Failed to update interaction ${interactionId} even without file field:`, fallbackError);
          throw fallbackError;
        }
      }
      // 🔧 ALTERNATIVE FORMAT ATTEMPT: Try different file array formats as last resort
      if (updateError?.response?.status === 403 && optimizedPayload.file) {
        console.log(`🔧 ALTERNATIVE FORMAT: Attempting different array formats...`);
        const failedJunctions: (string | number)[] = [];
        for (const fileId of optimizedPayload.file) {
          try {
            await directus.request(createItem('customer_Interaction_customerInteractionFile' as any, {
              customer_Interaction_id: interactionId,
              customerInteractionFile_id: fileId
            }));
            console.log(`✅ MANUAL JUNCTION: Created link for file ${fileId}`);
          } catch (junctionError: any) {
            failedJunctions.push(fileId);
            console.error(`❌ MANUAL JUNCTION: Failed to create link for file ${fileId}:`, junctionError);
          }
        }
      }

      // 🔧 ALTERNATIVE FORMAT ATTEMPT: Try different file array formats as last resort
      if (updateError?.response?.status === 403 && optimizedPayload.file) {
        console.log(`🔧 ALTERNATIVE FORMAT: Attempting different array formats...`);

        const alternativeFormats = [
          // Try null first (discovered this works!)
          { name: 'null', file: null },
          // Try empty array
          { name: 'empty array', file: [] },
          // Try numeric IDs (convert UUIDs to hash codes) as last resort
          {
            name: 'numeric hash',
            file: optimizedPayload.file.map(id => {
              if (typeof id === 'string') {
                // Simple hash function to convert UUID to number
                let hash = 0;
                for (let i = 0; i < id.length; i++) {
                  const char = id.charCodeAt(i);
                  hash = ((hash << 5) - hash) + char;
                  hash = hash & hash; // Convert to 32bit integer
                }
                return Math.abs(hash);
              }
              return id;
            })
          }
        ];

        for (const format of alternativeFormats) {
          try {
            console.log(`🧪 ALTERNATIVE FORMAT: Trying ${format.name} format:`, format.file);

            const altPayload = { ...optimizedPayload, file: format.file };
            const altResult = await executeWithRetry(async () => {
              return await directus.request(updateItem('customer_Interaction' as any, interactionId, altPayload));
            }, `update customer interaction (${format.name} format)`);

            console.log(`✅ ALTERNATIVE FORMAT: ${format.name} format succeeded!`);
            return altResult;

          } catch (altError: any) {
            console.log(`❌ ALTERNATIVE FORMAT: ${format.name} format failed:`, altError?.response?.status || altError?.message);
          }
        }

        console.log(`❌ ALTERNATIVE FORMAT: All formats failed, giving up on file field`);
      }

      throw updateError;
    }
  },

  /**
   * Delete customer interaction - 🛡️ SECURITY: Validates user ownership before deletion
   */
  async deleteCustomerInteraction(interactionId: number) {
    await ensureAuthenticated();

    // 🛡️ SECURITY VALIDATION: Verify user owns this interaction before deleting
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');

    // First, verify ownership by getting the interaction
    const existingInteraction = await executeWithRetry(async () => {
      return await directus.request(readItem('customer_Interaction' as any, interactionId, {
        fields: ['id', 'PIC']
      }));
    }, 'verify interaction ownership');

    if (!existingInteraction) {
      throw new Error('Interaction not found');
    }

    if (existingInteraction.PIC !== currentUser.id) {
      throw new Error('🛡️ SECURITY: Access denied - user does not own this interaction');
    }

    return await executeWithRetry(async () => {
      return await directus.request(deleteItem('customer_Interaction' as any, interactionId));
    }, 'delete customer interaction');
  },

  /**
   * Soft delete customer interaction - 🛡️ SECURITY: Validates user ownership, sets hidden=true
   */
  async softDeleteCustomerInteraction(interactionId: number) {
    await ensureAuthenticated();

    // 🛡️ SECURITY VALIDATION: Verify user owns this interaction before soft deleting
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');

    // First, verify ownership by getting the interaction
    const existingInteraction = await executeWithRetry(async () => {
      return await directus.request(readItem('customer_Interaction' as any, interactionId, {
        fields: ['id', 'PIC', 'Hidden']
      }));
    }, 'verify interaction ownership for soft delete');

    if (!existingInteraction) {
      throw new Error('Interaction not found');
    }

    if (existingInteraction.PIC !== currentUser.id) {
      throw new Error('🛡️ SECURITY: Access denied - user does not own this interaction');
    }

    // Check if already hidden (soft deleted)
    if (existingInteraction.Hidden === true) {
      throw new Error('Interaction is already deleted');
    }

    // Soft delete by setting hidden = true
    return await executeWithRetry(async () => {
      return await directus.request(updateItem('customer_Interaction' as any, interactionId, {
        Hidden: true
      }));
    }, 'soft delete customer interaction');
  },

  /**
   * Get customer interaction by ID - 🛡️ SECURITY: Validates user ownership before returning data
   */
  async getCustomerInteractionById(interactionId: number) {
    await ensureAuthenticated();

    // 🛡️ SECURITY VALIDATION: First verify ownership, then return full data
    const currentUser = authManager.getState().user;
    if (!currentUser) throw new Error('No authenticated user');

    const interaction = await executeWithRetry(async () => {
      return await directus.request(readItem('customer_Interaction' as any, interactionId, {
        fields: [
          'id',
          'No_pelanggan',
          'date_created',
          'keterangan',
          'interaction_type',
          'PIC',
          'customer_tambak_id',
          'visit_id',
          'done', // CRITICAL: Include done field for status determination
          'priority', // Include priority field
          // Related data
          'No_pelanggan.Nama_Pelanggan',
          'PIC.first_name',
          'PIC.last_name',
          'customer_tambak_id.Nama_Pelanggan_Tambak'
        ]
      }));
    }, 'get customer interaction by id');

    if (!interaction) {
      throw new Error('Interaction not found');
    }

    if (interaction.PIC !== currentUser.id) {
      throw new Error('🛡️ SECURITY: Access denied - user does not own this interaction');
    }

    return interaction;
  },

  /**
   * Search customer interactions - 🛡️ SECURITY: Only returns current user's interactions
   */
  async searchCustomerInteractions(query: string, options: {
    limit?: number;
    offset?: number;
  } = {}) {
    const { limit = 20, offset = 0 } = options;
    
    // 🛡️ SECURITY FIX: Use getUserCustomerInteractionsWithFilter to enforce PIC filtering
    return this.getUserCustomerInteractionsWithFilter({
      limit,
      offset,
      additionalFilter: {
        _and: [
          {
            _or: [
              { keterangan: { _icontains: query } },
              { 'No_pelanggan.Nama_Pelanggan': { _icontains: query } },
              { interaction_type: { _icontains: query } }
            ]
          }
          // TODO: Re-enable after hidden field permissions are fixed
          // { hidden: { _neq: true } } // Filter out soft deleted interactions
        ]
      }
    });
  },

  /**
   * Get recent customer interactions - 🛡️ SECURITY: Only returns current user's interactions
   */
  async getRecentCustomerInteractions(days: number = 7, limit: number = 20) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    // 🛡️ SECURITY FIX: Use getUserCustomerInteractionsWithFilter to enforce PIC filtering
    return this.getUserCustomerInteractionsWithFilter({
      limit,
      additionalFilter: {
        date_created: { _gte: cutoffDate.toISOString() }
      },
      sort: ['-date_created']
    });
  },

  /**
   * Get unique customers from current user's interactions
   * Returns a list of customers with whom the current user has had interactions
   * Algorithm:
   * 1. Get all customer_Interaction records for current user (filtered by PIC)
   * 2. Extract unique No_pelanggan values
   * 3. Fetch customer details from Data_Customer using No_pelanggan
   * 4. Fallback to Customer_tambak using customer_tambak_id if needed
   */
  async getCustomersWithInteractions(options: {
    limit?: number;
    offset?: number;
  } = {}) {
    const { limit = -1, offset = 0 } = options;

    try {
      const currentUser = authManager.getState().user;
      if (!currentUser) throw new Error('No authenticated user');

      console.log('🔍 Fetching customers with interactions for current user:', currentUser.id);

      // Step 1: Get all interactions for current user with customer info
      const interactions = await this.getUserCustomerInteractionsWithFilter({
        limit: 1000, // Get sufficient interactions to find unique customers
        additionalFilter: {
          _or: [
            { Hidden: { _null: true } },
            { Hidden: { _eq: false } }
          ] // Filter out soft deleted interactions
        },
        sort: ['-date_created']
      });

      console.log('📊 Found', interactions.length, 'interactions for current user');

      // DEBUG: Log sample interaction to verify structure
      if (interactions.length > 0) {
        console.log('🔍 Sample interaction:', JSON.stringify(interactions[0], null, 2));
      }

      // Step 2: Extract unique customer IDs (No_pelanggan) and customer_tambak_id
      const uniqueCustomerIds = new Set<string>();
      const uniqueTambakIds = new Set<number>();

      interactions.forEach((interaction: any) => {
        console.log('🔍 Processing interaction:', {
          No_pelanggan: interaction.No_pelanggan,
          customer_tambak_id: interaction.customer_tambak_id
        });

        if (interaction.No_pelanggan) {
          uniqueCustomerIds.add(interaction.No_pelanggan);
        }
        if (interaction.customer_tambak_id) {
          uniqueTambakIds.add(interaction.customer_tambak_id);
        }
      });

      console.log('🎯 Found', uniqueCustomerIds.size, 'unique customers from No_pelanggan:', Array.from(uniqueCustomerIds));
      console.log('🎯 Found', uniqueTambakIds.size, 'unique customers from customer_tambak_id:', Array.from(uniqueTambakIds));

      const customers: any[] = [];

      // Step 3: Fetch customer details from Data_Customer table
      if (uniqueCustomerIds.size > 0) {
        const dataCustomers = await directus.request(readItems('Data_Customer', {
          filter: {
            No_pelanggan: { _in: Array.from(uniqueCustomerIds) }
          },
          fields: ['*'],
          limit: -1 // Get all matching customers
        }));

        console.log('✅ Fetched', dataCustomers.length, 'customers from Data_Customer');
        customers.push(...dataCustomers.map((c: any) => ({
          ...c,
          data_source: 'Data_Customer',
          Nama_Pelanggan_Tambak: c.Nama_Pelanggan, // Map for consistency
          idcust: c.No_pelanggan
        })));
      }

      // Step 4: Fetch additional customers from Customer_tambak table if needed
      if (uniqueTambakIds.size > 0) {
        const tambakCustomers = await directus.request(readItems('Customer_Tambak', {
          filter: {
            id: { _in: Array.from(uniqueTambakIds) }
          },
          fields: ['*', 'PIC.first_name', 'PIC.last_name', 'PIC.email'],
          limit: -1
        }));

        console.log('✅ Fetched', tambakCustomers.length, 'customers from Customer_Tambak');

        // Only add tambak customers that aren't already in the list (avoid duplicates)
        const existingNoPelanggan = new Set(customers.map(c => c.No_pelanggan));
        tambakCustomers.forEach((c: any) => {
          if (!existingNoPelanggan.has(c.No_pelanggan)) {
            customers.push({
              ...c,
              data_source: 'Customer_Tambak',
              Nama_Pelanggan: c.Nama_Pelanggan_Tambak, // Map for consistency
              idcust: c.No_pelanggan
            });
          }
        });
      }

      console.log('📦 Total unique customers with interactions:', customers.length);

      // Apply pagination if needed
      const paginatedCustomers = limit > 0
        ? customers.slice(offset, offset + limit)
        : customers.slice(offset);

      return paginatedCustomers;

    } catch (error) {
      console.error('❌ Failed to get customers with interactions:', error);
      throw error;
    }
  },

  /**
   * 🎯 DEDICATED FUNCTION: Get customers from customer_interaction table
   * Specifically designed to show customers that the current user has interacted with
   *
   * Algorithm:
   * 1. Query customer_Interaction table directly with current user's PIC
   * 2. Get unique customer IDs (No_pelanggan and customer_tambak_id)
   * 3. Fetch customer details from Data_Customer and Customer_Tambak
   * 4. Return deduplicated customer list with interaction metadata
   *
   * @returns Array of customers with interaction data
   */
  async getCustomersFromInteractionTable(options: {
    limit?: number;
    offset?: number;
    includeHidden?: boolean; // Include soft-deleted interactions
    includeDone?: boolean; // Include completed interactions (done = true)
    includePending?: boolean; // Include pending interactions (done = false)
  } = {}) {
    const { limit = -1, offset = 0, includeHidden = false, includeDone = true, includePending = true } = options;

    try {
      const currentUser = authManager.getState().user;
      if (!currentUser) {
        throw new Error('❌ No authenticated user');
      }

      console.log('═══════════════════════════════════════════════════════');
      console.log('🎯 Getting customers from customer_Interaction table');
      console.log('User:', currentUser.first_name, '(', currentUser.email, ')');
      console.log('User ID:', currentUser.id);
      console.log('═══════════════════════════════════════════════════════');

      // Step 1: Build filter for customer_Interaction table
      const interactionFilter: any = {
        _and: [
          { PIC: { _eq: currentUser.id } } // Filter by current user as PIC
        ]
      };

      // Filter by completion status (done field)
      const statusFilters: any[] = [];
      if (includeDone && includePending) {
        // Include both done and pending (no filter needed)
        console.log('🔄 Including both completed and pending interactions');
      } else if (includeDone) {
        // Include only completed interactions
        statusFilters.push({ done: { _eq: true } });
        console.log('✅ Including only COMPLETED interactions (done = true)');
      } else if (includePending) {
        // Include only pending interactions
        statusFilters.push({ done: { _eq: false } });
        console.log('⏳ Including only PENDING interactions (done = false)');
      }

      // Add status filter to main filter if specified
      if (statusFilters.length > 0) {
        if (statusFilters.length === 1) {
          interactionFilter._and.push(statusFilters[0]);
        } else {
          interactionFilter._and.push({ _or: statusFilters });
        }
      }

      // Optionally filter out hidden/soft-deleted interactions
      if (!includeHidden) {
        interactionFilter._and.push({
          _or: [
            { Hidden: { _null: true } },
            { Hidden: { _eq: false } }
          ]
        });
      }

      console.log('🔍 Filter:', JSON.stringify(interactionFilter, null, 2));

      // Step 2: Fetch interactions from customer_Interaction table
      const interactions = await directus.request(readItems('customer_Interaction' as any, {
        filter: interactionFilter,
        fields: [
          'id',
          'No_pelanggan', // Customer ID from Data_Customer
          'customer_tambak_id', // Customer ID from Customer_Tambak
          'date_created',
          'interaction_type',
          'keterangan',
          'priority',
          'done'
        ],
        limit: 1000, // Get all interactions to find unique customers
        sort: ['-date_created']
      }));

      console.log('📊 Found', interactions.length, 'interactions in customer_Interaction table');

      if (interactions.length > 0) {
        console.log('🔍 Sample interaction:', {
          id: interactions[0].id,
          No_pelanggan: interactions[0].No_pelanggan,
          customer_tambak_id: interactions[0].customer_tambak_id,
          date_created: interactions[0].date_created
        });
      }

      // Step 3: Extract unique customer IDs
      const uniqueNoPelanggan = new Set<string>();
      const uniqueTambakIds = new Set<number>();
      const interactionMetadata = new Map<string, any>(); // Store interaction metadata per customer

      interactions.forEach((interaction: any) => {
        // Collect No_pelanggan (Data_Customer IDs)
        if (interaction.No_pelanggan) {
          const id = interaction.No_pelanggan;
          uniqueNoPelanggan.add(id);

          // Store latest interaction metadata
          if (!interactionMetadata.has(id) ||
              new Date(interaction.date_created) > new Date(interactionMetadata.get(id).date_created)) {
            interactionMetadata.set(id, {
              last_interaction: interaction.date_created,
              interaction_type: interaction.interaction_type,
              interaction_count: (interactionMetadata.get(id)?.interaction_count || 0) + 1
            });
          }
        }

        // Collect customer_tambak_id (Customer_Tambak IDs)
        if (interaction.customer_tambak_id) {
          uniqueTambakIds.add(interaction.customer_tambak_id);
        }
      });

      console.log('🎯 Unique customers found:');
      console.log('   - From Data_Customer (No_pelanggan):', uniqueNoPelanggan.size, 'customers');
      console.log('     IDs:', Array.from(uniqueNoPelanggan));
      console.log('   - From Customer_Tambak (customer_tambak_id):', uniqueTambakIds.size, 'customers');
      console.log('     IDs:', Array.from(uniqueTambakIds));

      const customers: any[] = [];

      // Step 4: Fetch customer details from Data_Customer table
      if (uniqueNoPelanggan.size > 0) {
        console.log('📥 Fetching', uniqueNoPelanggan.size, 'customers from Data_Customer...');

        const dataCustomers = await directus.request(readItems('Data_Customer', {
          filter: {
            No_pelanggan: { _in: Array.from(uniqueNoPelanggan) }
          },
          fields: ['*'], // Get all fields
          limit: -1
        }));

        console.log('✅ Fetched', dataCustomers.length, 'customers from Data_Customer');

        // Map customers with interaction metadata
        dataCustomers.forEach((customer: any) => {
          const metadata = interactionMetadata.get(customer.No_pelanggan) || {};
          customers.push({
            ...customer,
            // Standardized fields
            idcust: customer.No_pelanggan,
            name: customer.Nama_Pelanggan,
            address: customer.Alamat || customer.Alamat_1,
            phone: customer.Kontak,
            // Source metadata
            data_source: 'Data_Customer',
            from_interaction_table: true,
            // Interaction metadata
            lastInteraction: metadata.last_interaction || customer.date_followup,
            last_interaction_type: metadata.interaction_type,
            interaction_count: metadata.interaction_count || 0
          });
        });

        console.log('✅ Mapped', dataCustomers.length, 'Data_Customer records');
      }

      // Step 5: Fetch additional customers from Customer_Tambak table
      if (uniqueTambakIds.size > 0) {
        console.log('📥 Fetching', uniqueTambakIds.size, 'customers from Customer_Tambak...');

        const tambakCustomers = await directus.request(readItems('Customer_Tambak', {
          filter: {
            id: { _in: Array.from(uniqueTambakIds) }
          },
          fields: ['*', 'PIC.first_name', 'PIC.last_name', 'PIC.email'],
          limit: -1
        }));

        console.log('✅ Fetched', tambakCustomers.length, 'customers from Customer_Tambak');

        // Deduplicate: Only add tambak customers not already in the list
        const existingIds = new Set(customers.map(c => c.No_pelanggan));
        tambakCustomers.forEach((customer: any) => {
          if (!existingIds.has(customer.No_pelanggan)) {
            customers.push({
              ...customer,
              // Standardized fields
              idcust: customer.No_pelanggan,
              name: customer.Nama_Pelanggan_Tambak,
              address: customer.Alamat,
              // Source metadata
              data_source: 'Customer_Tambak',
              from_interaction_table: true,
              // Tambak-specific fields
              has_tambak_data: true,
              tambak_id: customer.id
            });
            existingIds.add(customer.No_pelanggan);
          }
        });

        console.log('✅ Added', tambakCustomers.length - (tambakCustomers.length - customers.length), 'unique Customer_Tambak records');
      }

      console.log('═══════════════════════════════════════════════════════');
      console.log('📦 RESULT: Found', customers.length, 'unique customers from customer_Interaction');
      console.log('═══════════════════════════════════════════════════════');

      // Step 6: Apply pagination if needed
      const result = limit > 0
        ? customers.slice(offset, offset + limit)
        : customers.slice(offset);

      return result;

    } catch (error) {
      console.error('═══════════════════════════════════════════════════════');
      console.error('❌ ERROR: Failed to get customers from customer_Interaction');
      console.error('Error:', error);
      console.error('═══════════════════════════════════════════════════════');
      throw error;
    }
  },

  /**
   * Get customers from Customer_Tambak table where PIC matches current user
   * This complements getCustomersFromInteractionTable by also showing customers
   * directly assigned to the current user as PIC in Customer_Tambak
   *
   * @param options - Query options
   * @returns Array of customers from Customer_Tambak where user is PIC
   */
  async getCustomersFromTambakByPIC(options: {
    limit?: number;
    offset?: number;
  } = {}) {
    const { limit = -1, offset = 0 } = options;

    try {
      const currentUser = authManager.getState().user;
      if (!currentUser) {
        throw new Error('❌ No authenticated user');
      }

      console.log('═══════════════════════════════════════════════════════');
      console.log('🎯 Getting customers from Customer_Tambak where PIC = current user');
      console.log('User:', currentUser.first_name, '(', currentUser.email, ')');
      console.log('User ID:', currentUser.id);
      console.log('═══════════════════════════════════════════════════════');

      try {
        // ATTEMPT 1: Try direct query to Customer_Tambak (requires read permission)
        console.log('📡 Attempt 1: Direct query to Customer_Tambak...');
        const tambakCustomers = await directus.request(readItems('Customer_Tambak', {
          filter: {
            PIC: { _eq: currentUser.id } // Filter by current user as PIC
          },
          fields: ['*', 'PIC.first_name', 'PIC.last_name', 'PIC.email'],
          limit: limit,
          offset: offset,
          sort: ['-date_created']
        }));

        console.log('✅ Direct query succeeded! Found', tambakCustomers.length, 'customers');

        // Map to standardized format
        const customers = tambakCustomers.map((customer: any) => ({
          ...customer,
          // Standardized fields
          idcust: customer.No_pelanggan || `tambak_${customer.id}`,
          name: customer.Nama_Pelanggan_Tambak || customer.NamaPelangganTambak,
          address: customer.Alamat,
          phone: customer.Kontak || customer.Contacs,
          // Source metadata
          data_source: 'Customer_Tambak_PIC',
          from_tambak_pic: true,
          // Tambak-specific fields
          has_tambak_data: true,
          tambak_id: customer.id,
          pic_name: customer.PIC?.first_name || customer.PIC?.last_name
            ? `${customer.PIC.first_name || ''} ${customer.PIC.last_name || ''}`.trim()
            : undefined
        }));

        console.log('═══════════════════════════════════════════════════════');
        console.log('📦 RESULT: Found', customers.length, 'customers from Customer_Tambak by PIC');
        console.log('═══════════════════════════════════════════════════════');

        return customers;

      } catch (directError: any) {
        // Check if it's a permission error (403)
        if (directError?.response?.status === 403 || directError?.errors?.[0]?.extensions?.code === 'FORBIDDEN') {
          console.warn('⚠️ Direct query failed with 403 Forbidden - trying alternative approach...');
          console.warn('   This means the user lacks read permission for Customer_Tambak collection');

          // ATTEMPT 2: Fallback to querying through customer_visits (which user likely has access to)
          console.log('📡 Attempt 2: Querying through customer_visits...');

          try {
            // Get all visits for current user, which contain customer_tambak_pelanggan references
            const visits = await directus.request(readItems('customer_visits' as any, {
              filter: {
                PIC: { _eq: currentUser.id }
              },
              fields: ['customer_tambak_pelanggan', 'visit_notes', 'visit_location'],
              limit: -1,
              sort: ['-date_created']
            }));

            console.log('📊 Found', visits.length, 'visits for current user');

            // Extract unique customer names from visits
            const uniqueCustomers = new Map<string, any>();

            visits.forEach((visit: any) => {
              const customerId = visit.customer_tambak_pelanggan;
              if (customerId && !uniqueCustomers.has(customerId)) {
                // Extract customer name from visit_notes (format: "Visit for [Customer Name]. ...")
                let customerName = customerId;
                if (visit.visit_notes) {
                  const nameMatch = visit.visit_notes.match(/Visit for (.+?)\./);
                  if (nameMatch) {
                    customerName = nameMatch[1];
                  }
                }

                uniqueCustomers.set(customerId, {
                  id: customerId,
                  idcust: customerId,
                  No_pelanggan: customerId,
                  name: customerName,
                  Nama_Pelanggan_Tambak: customerName,
                  NamaPelangganTambak: customerName,
                  address: visit.visit_location || '',
                  Alamat: visit.visit_location || '',
                  // Source metadata
                  data_source: 'Customer_Tambak_Via_Visits',
                  from_tambak_pic: true,
                  has_tambak_data: true,
                  tambak_id: customerId,
                  pic_name: `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim()
                });
              }
            });

            const customers = Array.from(uniqueCustomers.values());

            console.log('✅ Fallback query succeeded! Found', customers.length, 'unique customers from visits');
            console.log('═══════════════════════════════════════════════════════');
            console.log('📦 RESULT: Found', customers.length, 'customers via customer_visits fallback');
            console.log('═══════════════════════════════════════════════════════');

            return customers;

          } catch (visitError) {
            console.error('❌ Fallback query also failed:', visitError);
            throw visitError;
          }
        } else {
          // Some other error, rethrow
          throw directError;
        }
      }

    } catch (error) {
      console.error('═══════════════════════════════════════════════════════');
      console.error('❌ ERROR: All attempts failed to get customers from Customer_Tambak by PIC');
      console.error('Error:', error);
      console.error('═══════════════════════════════════════════════════════');

      // Return empty array instead of throwing to prevent UI crash
      console.warn('⚠️ Returning empty array to prevent UI crash');
      return [];
    }
  },

  /**
   * ✅ Get customers with COMPLETED interactions (done = true) filtered by current user as PIC
   * This function specifically returns customers where:
   * 1. PIC = current user (filter by user)
   * 2. done = true (interactions that are checked/completed in Directus)
   */
  async getCustomersWithCompletedInteractions(options: {
    limit?: number;
    offset?: number;
  } = {}) {
    const { limit = -1, offset = 0 } = options;

    try {
      const currentUser = authManager.getState().user;
      if (!currentUser) {
        throw new Error('❌ No authenticated user');
      }

      console.log('═══════════════════════════════════════════════════════');
      console.log('✅ Getting customers with COMPLETED interactions (done = true)');
      console.log('User:', currentUser.first_name, '(', currentUser.email, ')');
      console.log('User ID:', currentUser.id);
      console.log('═══════════════════════════════════════════════════════');

      // Build filter: PIC = current user AND done = true (ONLY checked items)
      // 🔧 FIXED: Handle database reality where done might be boolean or null
      // Filter for records that are explicitly completed (done = true)
      const interactionFilter: any = {
        _and: [
          { PIC: { _eq: currentUser.id } }, // Filter by current user as PIC
          { done: { _eq: true } }          // ✅ ONLY explicitly checked items
        ]
      };

      console.log('🔍 COMPLETED Filter:', JSON.stringify(interactionFilter, null, 2));

      // Fetch completed interactions
      const interactions = await directus.request(readItems('customer_Interaction' as any, {
        filter: interactionFilter,
        fields: [
          'id',
          'No_pelanggan', // Customer ID from Data_Customer
          'customer_tambak_id', // Customer ID from Customer_Tambak
          'date_created',
          'interaction_type',
          'keterangan',
          'priority',
          'done',
          'completed_date' // When it was marked as completed
        ],
        limit: limit,
        offset: offset,
        sort: ['-date_created'] // Most recent completed interactions first
      }));

      console.log('✅ Found', interactions.length, 'COMPLETED interactions (done = true OR done = null)');

      if (interactions.length > 0) {
        console.log('🔍 Sample completed interaction:', {
          id: interactions[0].id,
          No_pelanggan: interactions[0].No_pelanggan,
          done: interactions[0].done,
          done_type: typeof interactions[0].done,
          completed_date: interactions[0].completed_date
        });
      }

      // 🔧 DEBUG: Show breakdown of done values
      const doneCounts = interactions.reduce((acc, interaction) => {
        if (interaction.done === true) acc.true++;
        else if (interaction.done === false) acc.false++;
        else if (interaction.done === null) acc.null++;
        else acc.other++;
        return acc;
      }, { true: 0, false: 0, null: 0, other: 0 });

      console.log('📊 Done value breakdown:', doneCounts);

      // Extract unique customer IDs from completed interactions
      const uniqueNoPelanggan = new Set<string>();
      const uniqueTambakIds = new Set<number>();
      const completedMetadata = new Map<string, any>();

      interactions.forEach((interaction: any) => {
        // Collect No_pelanggan (Data_Customer IDs)
        if (interaction.No_pelanggan) {
          const id = interaction.No_pelanggan;
          uniqueNoPelanggan.add(id);

          // Store completion metadata
          if (!completedMetadata.has(id) ||
              new Date(interaction.date_created) > new Date(completedMetadata.get(id).date_created)) {
            completedMetadata.set(id, {
              last_completed: interaction.date_created,
              completed_date: interaction.completed_date,
              interaction_type: interaction.interaction_type,
              completed_count: (completedMetadata.get(id)?.completed_count || 0) + 1
            });
          }
        }

        // Collect customer_tambak_id (Customer_Tambak IDs)
        if (interaction.customer_tambak_id) {
          uniqueTambakIds.add(interaction.customer_tambak_id);
        }
      });

      console.log('🎯 Unique customers with COMPLETED interactions:');
      console.log('   - From Data_Customer (No_pelanggan):', uniqueNoPelanggan.size, 'customers');
      console.log('   - From Customer_Tambak (customer_tambak_id):', uniqueTambakIds.size, 'customers');

      const customers: any[] = [];

      // Fetch customer details from Data_Customer table
      if (uniqueNoPelanggan.size > 0) {
        console.log('📥 Fetching', uniqueNoPelanggan.size, 'customers from Data_Customer...');

        const dataCustomers = await directus.request(readItems('Data_Customer', {
          filter: {
            No_pelanggan: { _in: Array.from(uniqueNoPelanggan) }
          },
          fields: ['*'], // Get all fields
          limit: -1
        }));

        console.log('✅ Fetched', dataCustomers.length, 'customers from Data_Customer');

        // Process Data_Customer customers
        dataCustomers.forEach((customer: any) => {
          const metadata = completedMetadata.get(customer.No_pelanggan);
          customers.push({
            ...customer,
            // Standardized fields
            idcust: customer.No_pelanggan,
            name: customer.Nama_Pelanggan,
            address: customer.Alamat,
            phone: customer.Kontak,
            // Source metadata
            data_source: 'Data_Customer_Completed',
            from_completed_interactions: true,
            // Completion metadata
            completion_info: metadata,
            last_completed: metadata?.last_completed,
            completed_date: metadata?.completed_date,
            completed_interactions_count: metadata?.completed_count || 1
          });
        });
      }

      console.log('═══════════════════════════════════════════════════════');
      console.log('📦 RESULT: Found', customers.length, 'customers with COMPLETED interactions (done = true)');
      console.log('═══════════════════════════════════════════════════════');

      return customers;

    } catch (error) {
      console.error('═══════════════════════════════════════════════════════');
      console.error('❌ ERROR: Failed to get customers with completed interactions');
      console.error('Error:', error);
      console.error('═══════════════════════════════════════════════════════');

      // Return empty array instead of throwing to prevent UI crash
      console.warn('⚠️ Returning empty array to prevent UI crash');
      return [];
    }
  },

  /**
   * ⏳ Get customers with PENDING interactions (done = false) filtered by current user as PIC
   * This function specifically returns customers where:
   * 1. PIC = current user (filter by user)
   * 2. done = false (interactions that are NOT checked/completed in Directus)
   */
  async getCustomersWithPendingInteractions(options: {
    limit?: number;
    offset?: number;
  } = {}) {
    const { limit = -1, offset = 0 } = options;

    try {
      const currentUser = authManager.getState().user;
      if (!currentUser) {
        throw new Error('❌ No authenticated user');
      }

      console.log('═══════════════════════════════════════════════════════');
      console.log('⏳ Getting customers with PENDING interactions (done = false)');
      console.log('User:', currentUser.first_name, '(', currentUser.email, ')');
      console.log('User ID:', currentUser.id);
      console.log('═══════════════════════════════════════════════════════');

      // Build filter: PIC = current user AND done = false
      const interactionFilter: any = {
        _and: [
          { PIC: { _eq: currentUser.id } }, // Filter by current user as PIC
          { done: { _eq: false } }, // ⏳ Filter: ONLY pending interactions (not checked in Directus)
          // Exclude hidden interactions
          {
            _or: [
              { Hidden: { _null: true } },
              { Hidden: { _eq: false } }
            ]
          }
        ]
      };

      console.log('🔍 PENDING Filter:', JSON.stringify(interactionFilter, null, 2));

      // Fetch pending interactions
      const interactions = await directus.request(readItems('customer_Interaction' as any, {
        filter: interactionFilter,
        fields: [
          'id',
          'No_pelanggan', // Customer ID from Data_Customer
          'customer_tambak_id', // Customer ID from Customer_Tambak
          'date_created',
          'interaction_type',
          'keterangan',
          'priority',
          'done'
        ],
        limit: limit,
        offset: offset,
        sort: ['-date_created'] // Most recent pending interactions first
      }));

      console.log('⏳ Found', interactions.length, 'PENDING interactions (done = false)');

      // Extract unique customer IDs from pending interactions (similar logic to above)
      const uniqueNoPelanggan = new Set<string>();
      const uniqueTambakIds = new Set<number>();

      interactions.forEach((interaction: any) => {
        if (interaction.No_pelanggan) {
          uniqueNoPelanggan.add(interaction.No_pelanggan);
        }
        if (interaction.customer_tambak_id) {
          uniqueTambakIds.add(interaction.customer_tambak_id);
        }
      });

      const customers: any[] = [];

      // Fetch customer details from Data_Customer table
      if (uniqueNoPelanggan.size > 0) {
        const dataCustomers = await directus.request(readItems('Data_Customer', {
          filter: {
            No_pelanggan: { _in: Array.from(uniqueNoPelanggan) }
          },
          fields: ['*'],
          limit: -1
        }));

        // Process Data_Customer customers
        dataCustomers.forEach((customer: any) => {
          customers.push({
            ...customer,
            // Standardized fields
            idcust: customer.No_pelanggan,
            name: customer.Nama_Pelanggan,
            address: customer.Alamat,
            phone: customer.Kontak,
            // Source metadata
            data_source: 'Data_Customer_Pending',
            from_pending_interactions: true
          });
        });
      }

      console.log('═══════════════════════════════════════════════════════');
      console.log('📦 RESULT: Found', customers.length, 'customers with PENDING interactions (done = false)');
      console.log('═══════════════════════════════════════════════════════');

      return customers;

    } catch (error) {
      console.error('═══════════════════════════════════════════════════════');
      console.error('❌ ERROR: Failed to get customers with pending interactions');
      console.error('Error:', error);
      console.error('═══════════════════════════════════════════════════════');

      console.warn('⚠️ Returning empty array to prevent UI crash');
      return [];
    }
  },

  /**
   * Change user password with old password validation
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      await ensureAuthenticated();
      
      const currentUser = authManager.getState().user;
      if (!currentUser) throw new Error('No authenticated user');

      // First validate the old password by attempting to authenticate
      console.log('🔐 Validating old password...');
      try {
        // Create a temporary directus instance to test the old password
        const tempDirectus = createDirectus(env.VITE_DIRECTUS_URL).with(rest()).with(authentication());
        await tempDirectus.login(currentUser.email, oldPassword);
        console.log('✅ Old password validation successful');
      } catch (authError: any) {
        console.error('❌ Old password validation failed:', authError);
        return {
          success: false,
          message: 'Current password is incorrect'
        };
      }

      // If old password is valid, proceed with password change
      console.log('🔄 Updating password...');
      await directus.request(updateMe({
        password: newPassword
      }));

      console.log('✅ Password changed successfully');
      return { success: true, message: 'Password changed successfully' };
    } catch (error: any) {
      console.error('❌ Failed to change password:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to change password'
      };
    }
  },

  /**
   * Update current user profile information
   */
  async updateProfile(data: Partial<DirectusSchema['directus_users']>): Promise<{ success: boolean; message: string; user?: any }> {
    try {
      await ensureAuthenticated();
      console.log('🔄 Updating user profile with data:', data);

      const result = await directus.request(updateMe(data));

      console.log('✅ Profile updated successfully');

      // Update the auth state with new user data
      const currentUser = authManager.getState().user;
      if (currentUser) {
        authManager.updateUserState({ ...currentUser, ...result });
      }

      return {
        success: true,
        message: 'Profile updated successfully',
        user: result
      };
    } catch (error: any) {
      console.error('❌ Failed to update profile:', error);
      return {
        success: false,
        message: error.message || 'Failed to update profile'
      };
    }
  },

  /**
   * Logout user - clears authentication state and tokens
   */
  async logout(): Promise<void> {
    try {
      await authManager.logout();
      console.log('✅ User logged out successfully');
    } catch (error: any) {
      console.error('❌ Error during logout:', error);
      // Even if logout fails, we still want to clear local state
      authManager.logout();
    }
  },

  // MAP DATA: Query both Data_Customer and Customer_Tambak with deduplication
  async getCustomersForMap() {
    await ensureAuthenticated();
    const currentUser = authManager.getState().user;

    if (!currentUser) {
      console.error('❌ No authenticated user found');
      return [];
    }

    console.log(`🗺️ MAP DATA: Querying Customer_Tambak with coordinates for PIC user ${currentUser.id}`);

    try {
      // ONLY query Customer_Tambak with PIC filter and coordinates
      console.log('📊 Querying Customer_Tambak records with coordinates and PIC filter...');
      const tambakCustomers = await directus.request(readItems('Customer_Tambak', {
        fields: [
          '*', // All Customer_Tambak fields including coordinates
          'PIC.id',
          'PIC.first_name',
          'PIC.last_name',
          'PIC.email'
        ],
        filter: {
          _and: [
            { PIC: { _eq: currentUser.id } }, // REQUIRED: PIC filter
            { coordinates: { _nnull: true } } // Only get records with coordinates for map performance
          ]
        },
        limit: -1 // Get ALL records with coordinates for this PIC
      }));
      
      console.log(`✅ Customer_Tambak query complete: ${tambakCustomers?.length || 0} records with coordinates for PIC ${currentUser.id}`);

      // Format Customer_Tambak records for map view
      const formattedCustomers = (tambakCustomers || []).map(customer => {
        // Parse coordinates (already filtered by database to have coordinates)
        let coordinates = null;
        if (customer.coordinates) {
          if (typeof customer.coordinates === 'string') {
            try {
              coordinates = JSON.parse(customer.coordinates);
            } catch {
              console.warn('Failed to parse Customer_Tambak coordinates for', customer.id, ':', customer.coordinates);
              return null; // Skip invalid coordinate records
            }
          } else {
            coordinates = customer.coordinates;
          }

          // Handle array format from geocoding: [{"lat": ..., "lng": ...}]
          if (Array.isArray(coordinates) && coordinates.length > 0) {
            coordinates = coordinates[0]; // Extract first coordinate object from array
          }

          // Validate coordinates structure - skip if invalid
          if (!coordinates || !coordinates.lat || !coordinates.lng) {
            console.warn('Invalid coordinates structure for Customer_Tambak:', customer.id, coordinates);
            return null; // Skip invalid coordinate records
          }
        } else {
          console.warn('No coordinates found for Customer_Tambak:', customer.id);
          return null; // Skip records without coordinates
        }

        return {
          ...customer,
          coordinates, // Always valid coordinates due to DB filter
          source: 'Customer_Tambak',
          hasCoordinates: true, // Always true due to DB filter
          // Standardize field names for compatibility
          Nama_Pelanggan: customer.Nama_Pelanggan_Tambak,
          Nama_Pelanggan_Tambak: customer.Nama_Pelanggan_Tambak,
          idcust: customer.No_pelanggan || `T-${customer.id}`,
          // PIC info
          pic_id: customer.PIC?.id,
          assigned_pic: customer.PIC ?
            `${customer.PIC.first_name || ''} ${customer.PIC.last_name || ''}`.trim() || customer.PIC.email :
            ''
        };
      }).filter(Boolean); // Remove null entries

      console.log('🗺️ MAP DATA SUMMARY (PIC-filtered coordinates only):');
      console.log(`  📊 Customer_Tambak records with coordinates for PIC ${currentUser.id}:`, formattedCustomers.length);
      console.log('  📍 All records have valid coordinates (100% mappable)');
      console.log('  ⚡ Performance: Database-filtered for PIC and coordinates');

      return formattedCustomers;
      
    } catch (error: any) {
      console.error('❌ Failed to fetch map customers:', error);
      throw new Error(`Failed to fetch customers for map: ${error.message}`);
    }
  },

  /**
   * DEBUG: Inspect Data_Customer table structure
   */
  async inspectDataCustomerTable() {
    await ensureAuthenticated();
    console.log('🔍 INSPECTING: Data_Customer table structure');
    
    try {
      // Get first record with all fields
      const firstRecord = await directus.request(readItems('Data_Customer' as any, {
        fields: ['*'],
        limit: 1
      }));
      
      if (firstRecord && firstRecord.length > 0) {
        const record = firstRecord[0];
        console.log('🔍 FIRST Data_Customer RECORD:');
        console.log('📊 Available fields:', Object.keys(record));
        console.log('📋 Full record data:', record);
        console.log('🔍 Tipe_Pelanggan field:', record.Tipe_Pelanggan);
        console.log('🔍 tipe_pelanggan field (lowercase):', record.tipe_pelanggan);
        return record;
      } else {
        console.log('⚠️ No records found in Data_Customer');
        return null;
      }
    } catch (error) {
      console.error('❌ Failed to inspect Data_Customer:', error);
      throw error;
    }
  },

  // ENHANCEMENT: Try to add Tipe_Pelanggan to Customer_Tambak results by individual lookups
  async enhanceCustomersWithTipePelanggan(customers: any[]) {
    if (!customers || customers.length === 0) {
      return customers;
    }

    console.log('🔍 ENHANCE: Attempting to add Tipe_Pelanggan to', customers.length, 'customers');

    const enhancedCustomers = await Promise.all(
      customers.map(async (customer) => {
        // Skip if already has tipe_pelanggan
        if (customer.tipe_pelanggan && customer.tipe_pelanggan !== '') {
          return customer;
        }

        try {
          // Try to get individual customer record from Data_Customer
          const dataCustomerRecord = await directus.request(readItem('Data_Customer' as any, customer.No_pelanggan, {
            fields: ['Tipe_Pelanggan']
          }));

          if (dataCustomerRecord && dataCustomerRecord.Tipe_Pelanggan) {
            console.log('✅ ENHANCE: Found Tipe_Pelanggan for', customer.No_pelanggan, ':', dataCustomerRecord.Tipe_Pelanggan);
            return {
              ...customer,
              tipe_pelanggan: dataCustomerRecord.Tipe_Pelanggan,
              Tipe_Pelanggan: dataCustomerRecord.Tipe_Pelanggan
            };
          }
        } catch {
          // Try alternative method with filter
          try {
            const altRecord = await directus.request(readItems('Data_Customer' as any, {
              filter: { No_pelanggan: { _eq: customer.No_pelanggan } },
              fields: ['Tipe_Pelanggan'],
              limit: 1
            }));

            if (Array.isArray(altRecord) && altRecord.length > 0 && altRecord[0].Tipe_Pelanggan) {
              console.log('✅ ENHANCE (alt): Found Tipe_Pelanggan for', customer.No_pelanggan, ':', altRecord[0].Tipe_Pelanggan);
              return {
                ...customer,
                tipe_pelanggan: altRecord[0].Tipe_Pelanggan,
                Tipe_Pelanggan: altRecord[0].Tipe_Pelanggan
              };
            }
          } catch {
            console.warn('⚠️ ENHANCE: Could not fetch Tipe_Pelanggan for', customer.No_pelanggan);
          }
        }

        // Return original customer if lookup failed
        return customer;
      })
    );

    const successCount = enhancedCustomers.filter(c => c.tipe_pelanggan && c.tipe_pelanggan !== '').length;
    console.log('✅ ENHANCE: Successfully enhanced', successCount, 'out of', customers.length, 'customers with Tipe_Pelanggan');

    return enhancedCustomers;
  },

  // Get unique customer types (tipe_pelanggan) from Data_Customer
  async getUniqueCustomerTypes() {
    await ensureAuthenticated();
    console.log('🏷️ Getting unique customer types from Data_Customer');

    try {
      // Query all Data_Customer records to get unique Tipe_Pelanggan values
      const customers = await directus.request(readItems('Data_Customer' as any, {
        fields: ['Tipe_Pelanggan'],
        filter: {
          Tipe_Pelanggan: { _nnull: true }, // Only records with non-null types
          Dihentikan: { _neq: 'Yes' } // Exclude deactivated customers
        },
        limit: -1 // Get all records
      }));

      console.log(`✅ Found ${customers.length} customers with types`);

      // Extract unique types and remove duplicates
      const uniqueTypes = [...new Set(
        customers
          .map(customer => customer.Tipe_Pelanggan)
          .filter(type => type && type.trim() !== '') // Remove empty strings
          .map(type => type.trim()) // Trim whitespace
      )].sort(); // Sort alphabetically

      console.log('🎯 Unique customer types:', uniqueTypes);

      // Add default options
      const customerTypeOptions = [
        { value: 'all', label: 'Show All' },
        ...uniqueTypes.map(type => ({ value: type, label: type })),
        { value: 'customer_tambak', label: 'Customer Tambak' }
      ];

      console.log(`✅ Generated ${customerTypeOptions.length} customer type filter options`);
      
      return customerTypeOptions;
      
    } catch (error) {
      console.error('❌ Failed to get unique customer types:', error);
      
      // Return default options on error
      return [
        { value: 'all', label: 'Show All' },
        { value: 'customer_tambak', label: 'Customer Tambak' }
      ];
    }
  },

  // ============================================================================
  // PRODUCTS, INCENTIVES, AND PROMOTIONS OPERATIONS
  // ============================================================================

  /**
   * Get active product incentives (delegates to standalone function)
   */
  async getProductIncentives(limit = 20) {
    return await getProductIncentives(limit);
  },

  /**
   * Get active promotions (delegates to standalone function)
   */
  async getPromotions(limit = 20) {
    return await getPromotions(limit);
  },

  /**
   * Get active coupons (delegates to standalone function)
   */
  async getCoupons(limit = 20) {
    return await getCoupons(limit);
  },

  /**
   * Get user's issued coupons (delegates to standalone function)
   */
  async getUserCoupons(userId?: string) {
    return await getUserCoupons(userId);
  },

  /**
   * Get coupon by ID (delegates to standalone function)
   */
  async getCouponById(couponId: string) {
    return await getCouponById(couponId);
  },

  /**
   * Issue coupon to user (delegates to standalone function)
   */
  async issueCouponToUser(couponId: string, userId?: string) {
    return await issueCouponToUser(couponId, userId);
  },

  /**
   * Get incentive by ID (delegates to standalone function)
   */
  async getIncentiveById(incentiveId: string) {
    return await getIncentiveById(incentiveId);
  },

  /**
   * Get promotion by ID (delegates to standalone function)
   */
  async getPromotionById(promotionId: string) {
    return await getPromotionById(promotionId);
  },

  /**
   * Get products (delegates to standalone function)
   */
  async getProducts(limit = 100) {
    return await getProducts(limit);
  },

  /**
   * Get product by ID (delegates to standalone function)
   */
  async getProductById(productId: string) {
    return await getProductById(productId);
  }
};

// Export specific functions for components
export const getCustomerPhoneNumbers = directusOperations.getCustomerPhoneNumbers.bind(directusOperations);
export const searchCustomersWithDetails = directusOperations.searchCustomersWithDetails.bind(directusOperations);
export const testDataCustomerAccess = directusOperations.testDataCustomerAccess.bind(directusOperations);
export const testNopelanganSearch = directusOperations.testNopelanganSearch.bind(directusOperations);
export const getAuthenticatedAssetUrl = directusOperations.getAssetUrl.bind(directusOperations);
export const getAuthenticatedAvatarUrl = directusOperations.getAvatarUrl.bind(directusOperations);

// IMPROVED: Export improved customer functions that use Data_Customer as primary source
export const getCustomersImproved = directusOperations.getCustomersImproved.bind(directusOperations);
export const searchCustomersImproved = directusOperations.searchCustomersComprehensive.bind(directusOperations);
export const getCustomersSortedByTransactionSchema = directusOperations.getCustomersSortedByTransactionSchema.bind(directusOperations);

// COMPREHENSIVE: Export comprehensive search function for interaction dropdowns (includes ALL customer types)
export const searchCustomersForInteractions = directusOperations.searchCustomersForInteractions.bind(directusOperations);

// Maps view search with comprehensive phone and PIC lookup
export const searchCustomersForMapsView = directusOperations.searchCustomersForMapsView.bind(directusOperations);

// Get customers with interactions for current user
export const getCustomersWithInteractions = directusOperations.getCustomersWithInteractions.bind(directusOperations);

// 🎯 NEW: Dedicated function to get customers from customer_interaction table
export const getCustomersFromInteractionTable = directusOperations.getCustomersFromInteractionTable.bind(directusOperations);

// ✅ NEW: Get customers with COMPLETED interactions (done = true) filtered by PIC
export const getCustomersWithCompletedInteractions = directusOperations.getCustomersWithCompletedInteractions.bind(directusOperations);

// ⏳ NEW: Get customers with PENDING interactions (done = false) filtered by PIC
export const getCustomersWithPendingInteractions = directusOperations.getCustomersWithPendingInteractions.bind(directusOperations);

// 🎯 NEW: Get customers from Customer_Tambak where PIC = current user
export const getCustomersFromTambakByPIC = directusOperations.getCustomersFromTambakByPIC.bind(directusOperations);

// 🔍 DEBUG: Test query for checking completed interactions count
export const debugTestCompletedInteractionsCount = async () => {
  await ensureAuthenticated();

  const currentUser = authManager.getState().user;
  if (!currentUser) {
    console.error('❌ No authenticated user');
    return { error: 'No authenticated user' };
  }

  console.log('═══════════════════════════════════════════════════════');
  console.log('🔍 DEBUG: Testing completed interactions count for user');
  console.log('User:', currentUser.first_name, '(', currentUser.email, ')');
  console.log('User ID:', currentUser.id);
  console.log('═══════════════════════════════════════════════════════');

  try {
    // Test 1: Count ALL interactions for this user
    const allInteractions = await directus.request(readItems('customer_Interaction' as any, {
      filter: {
        PIC: { _eq: currentUser.id }
      },
      fields: ['id', 'done', 'No_pelanggan', 'date_created'],
      limit: -1
    }));

    console.log('📊 ALL Interactions for user:', allInteractions.length);
    console.log('Sample:', allInteractions.slice(0, 3));

    // Test 2a: Check available fields in customer_Interaction table
    const sampleInteraction = await directus.request(readItems('customer_Interaction' as any, {
      filter: { PIC: { _eq: currentUser.id } },
      fields: ['*'], // Get ALL fields
      limit: 1
    }));

    if (sampleInteraction.length > 0) {
      console.log('🔍 Available fields in customer_Interaction:', Object.keys(sampleInteraction[0]));
      console.log('Sample interaction:', sampleInteraction[0]);
    }

    // Test 2b: Count ONLY completed interactions (done = true)
    const completedInteractions = await directus.request(readItems('customer_Interaction' as any, {
      filter: {
        _and: [
          { PIC: { _eq: currentUser.id } },
          { done: { _eq: true } }
        ]
      },
      fields: ['id', 'done', 'No_pelanggan', 'date_created', 'keterangan'],
      limit: -1
    }));

    console.log('✅ COMPLETED Interactions (done = true):', completedInteractions.length);
    console.log('Sample:', completedInteractions.slice(0, 3));

    // REMOVED: Test 3 - Alternative field names test was causing 403 errors
    // The field name is 'done', not 'completed' or other alternatives
    let completedAlternative = 0;

    // Test 3: Count ONLY pending interactions (done = false)
    const pendingInteractions = await directus.request(readItems('customer_Interaction' as any, {
      filter: {
        _and: [
          { PIC: { _eq: currentUser.id } },
          { done: { _eq: false } }
        ]
      },
      fields: ['id', 'done', 'No_pelanggan', 'date_created'],
      limit: -1
    }));

    console.log('⏳ PENDING Interactions (done = false):', pendingInteractions.length);
    console.log('Sample:', pendingInteractions.slice(0, 3));

    // Test 4: Check done field values
    const doneFieldValues = [...new Set(allInteractions.map(i => i.done))];
    console.log('🔍 Done field values found:', doneFieldValues);

    // Test 5: Check unique customers
    const uniqueCustomersFromCompleted = [...new Set(completedInteractions.map(i => i.No_pelanggan).filter(Boolean))];
    console.log('👥 Unique customers from completed interactions:', uniqueCustomersFromCompleted.length);

    console.log('═══════════════════════════════════════════════════════');
    console.log('📋 SUMMARY:');
    console.log('   Total interactions:', allInteractions.length);
    console.log('   Completed (done = true):', completedInteractions.length);
    console.log('   Alternative completed fields:', completedAlternative);
    console.log('   Pending (done = false):', pendingInteractions.length);
    console.log('   Unique customers with completed interactions:', uniqueCustomersFromCompleted.length);
    console.log('═══════════════════════════════════════════════════════');

    return {
      total: allInteractions.length,
      completed: completedInteractions.length,
      completedAlternative: completedAlternative,
      pending: pendingInteractions.length,
      uniqueCustomersCompleted: uniqueCustomersFromCompleted.length,
      user: {
        id: currentUser.id,
        name: currentUser.first_name,
        email: currentUser.email
      },
      availableFields: sampleInteraction.length > 0 ? Object.keys(sampleInteraction[0]) : [],
      sampleInteraction: sampleInteraction.length > 0 ? sampleInteraction[0] : null
    };

  } catch (error: any) {
    console.error('❌ DEBUG: Error testing completed interactions:', error);
    return { error: error.message };
  }
};

// DEBUG: Direct Customer_Tambak search for troubleshooting
export const debugSearchCustomerTambakDirect = async (query: string) => {
  await ensureAuthenticated();
  
  console.log('🐛 DEBUG: Direct Customer_Tambak search for:', query);
  
  try {
    // First, try to get a few records without any filter to test connection
    const allRecords = await directus.request(readItems('Customer_Tambak', {
      fields: ['id', 'No_pelanggan', 'Nama_Pelanggan_Tambak'],
      limit: 5
    }));
    console.log('🐛 DEBUG: Sample Customer_Tambak records (no filter):', allRecords);
    
    // Then try the search
    const result = await directus.request(readItems('Customer_Tambak', {
      filter: {
        Nama_Pelanggan_Tambak: { _icontains: query }
      },
      fields: ['id', 'No_pelanggan', 'Nama_Pelanggan_Tambak', 'Alamat'],
      limit: 10
    }));
    
    console.log('🐛 DEBUG: Direct Customer_Tambak search results:', result);
    
    // Also try case-sensitive exact match
    const exactResult = await directus.request(readItems('Customer_Tambak', {
      filter: {
        Nama_Pelanggan_Tambak: { _eq: query }
      },
      fields: ['id', 'No_pelanggan', 'Nama_Pelanggan_Tambak', 'Alamat'],
      limit: 10
    }));
    
    console.log('🐛 DEBUG: Exact match results:', exactResult);
    
    return { search: result, exact: exactResult, sample: allRecords };
  } catch (error) {
    console.error('🐛 DEBUG: Direct Customer_Tambak search failed:', error);
    throw error;
  }
};

// SIMPLE: Direct Customer_Tambak search as backup solution
export const searchCustomerTambakOnly = async (query: string, options: { page?: number; limit?: number } = {}) => {
  await ensureAuthenticated();
  
  const { page = 1, limit = 20 } = options;
  const actualOffset = (page - 1) * limit;
  
  console.log('🏞️ SIMPLE: Direct Customer_Tambak search for:', query);
  
  try {
    const tambakCustomers = await directus.request(readItems('Customer_Tambak', {
      ...(query.trim() === '' ? {} : {
        filter: {
          _or: [
            { Nama_Pelanggan_Tambak: { _icontains: query } },
            { No_pelanggan: { _icontains: query } },
            { Alamat: { _icontains: query } }
          ]
        }
      }),
      fields: ['*'],
      sort: ['Nama_Pelanggan_Tambak'],
      limit,
      offset: actualOffset
    }));
    
    console.log('✅ Customer_Tambak direct search results:', tambakCustomers.length);
    
    // Format results to match expected customer interface
    const formattedCustomers = tambakCustomers.map((customer: any) => ({
      id: customer.id,
      No_pelanggan: customer.No_pelanggan,
      name: customer.Nama_Pelanggan_Tambak,
      Nama_Pelanggan: customer.Nama_Pelanggan_Tambak,
      NamaPelangganTambak: customer.Nama_Pelanggan_Tambak,
      address: customer.Alamat || '',
      Alamat: customer.Alamat || '',
      phone: customer.Contacs || '',
      data_source: 'Customer_Tambak_Direct',
      source: 'Customer_Tambak',
      idcust: customer.No_pelanggan || customer.id.toString(),
      tambak_name: customer.Nama_Pelanggan_Tambak,
      priority: 'medium' as const,
      status: 'active' as const
    }));
    
    return {
      data: formattedCustomers,
      page,
      limit,
      offset: actualOffset,
      hasMore: tambakCustomers.length === limit
    };
    
  } catch (error) {
    console.error('❌ Customer_Tambak direct search failed:', error);
    throw error;
  }
};

// COMPREHENSIVE: Export comprehensive customer functions that include all customers from both tables
export const getCustomersComprehensive = directusOperations.getCustomersComprehensive.bind(directusOperations);

// ALPHABETICAL SORTING: Export Data_Customer-based alphabetical sorting functions
export const getAllCustomersSortedAlphabetically = directusOperations.getAllCustomersSortedAlphabetically.bind(directusOperations);

// MAP DATA: Export dual-table map data function with deduplication
export const getCustomersForMap = directusOperations.getCustomersForMap.bind(directusOperations);

// DEBUG: Export table inspection function
export const inspectDataCustomerTable = directusOperations.inspectDataCustomerTable.bind(directusOperations);

// OPTIMIZED: Export optimized customer basic data function
export const getCustomersBasicData = directusOperations.getCustomersBasicData.bind(directusOperations);

// FILTER: Export customer type filter function
export const getUniqueCustomerTypes = directusOperations.getUniqueCustomerTypes.bind(directusOperations);

/**
 * CREATE LOCAL VISIT RECORD for Save Photo functionality
 * This function now only creates a local visit record without sending to Directus
 * Data will be sent to Directus only when user clicks "End Visit"
 */
export const createCustomerVisitFromPhoto = async (data: {
  latitude: number;
  longitude: number;
  customerId: string; // Customer tambak ID like A-0001
  customerName?: string;
  customerAddress?: string;
}) => {
  await ensureAuthenticated();
  
  const currentUser = authManager.getState().user;
  if (!currentUser) throw new Error('No authenticated user');
  
  console.log('Creating customer visit from photo:', {
    customerId: data.customerId,
    customerName: data.customerName,
    customerAddress: data.customerAddress,
    latitude: data.latitude,
    longitude: data.longitude
  });
  
  // Use customer ID as-is, no prefixes or transformations needed
  const customerTambakPelanggan = data.customerId; // Just use the original ID like "A-0001"
  
  
  // Format waktu WIB tanpa milliseconds: "2025-07-30T02:15:38"
  const formattedTime = getWIBTime(); // Proper WIB time handling
  
  // Get current user data for PIC field
  const currentUserData = authManager.getState().user;
  const picValue = currentUserData?.id || 'unknown';
  
  // Create temporary visit ID for local state management
  const tempVisitId = `temp_visit_${Date.now()}`; // This is fine for unique IDs
  
  // Note: GPS address lookup removed from here as it's not needed for local visits
  // GPS address will be fetched when completing the visit in ActiveVisit.tsx
  
  // Create local visit object (NOT sent to Directus yet)
  const localVisit = {
    id: tempVisitId, // Temporary ID for local state
    visit_date: formattedTime,
    customer_tambak_pelanggan: customerTambakPelanggan,
    customer_id: data.customerId,
    customer_name: data.customerName || 'Unknown Customer',
    customer_address: data.customerAddress || '',
    PIC: picValue,
    visit_location: data.visit_location,
    visit_coordinates: {
      lat: data.latitude,
      lng: data.longitude
    },
    visit_purpose: 'Customer visit with arrival photo',
    visit_notes: null, // Will be filled from textarea
    Progress: null, // Will be filled from dropdown in ActiveVisit
    status: 'active',
    start_time: formattedTime,
    created_at: formattedTime,
    updated_at: formattedTime,
    isLocalOnly: true // Flag to indicate this is not yet in Directus
  };
  
  
  return localVisit;
};

/**
 * CREATE COMPLETE VISIT RECORD in Directus when End Visit is clicked
 * This function sends all visit data including notes, evidence, and coordinates to Directus
 */
export const createCompleteVisit = async (visitData: {
  customer_id: string; // Customer tambak ID like A-0001
  customer_name: string;
  visit_location?: string; // Optional for backward compatibility
  customer_address?: string; // New field for schedule data
  visit_coordinates?: { lat: number; lng: number }; // Optional for backward compatibility
  Coordinates_Home?: { lat: number; lng: number }; // New field for schedule data
  visit_notes: string | null;
  satisfaction_rating?: number;
  visit_purpose?: string;
  start_time: string;
  status?: string;
  progress_status?: string; // Progress status from dropdown
  existing_visit_id?: string; // ID of existing visit to update instead of creating new
  // Farm PIC data
  farm_pic_name?: string;
  farm_pic_contact?: string;
  farm_pic_position?: string;
}) => {
  await ensureAuthenticated();
  
  const currentUser = authManager.getState().user;
  if (!currentUser) throw new Error('No authenticated user');
  
  
  // Get current user data for PIC field
  const picValue = currentUser.id || 'unknown';
  
  // Format end time as WIB
  const endTime = getWIBTime(); // Proper WIB time handling
  
  // Create complete payload untuk database emp_visits
  const payload = {
    visit_date: visitData.start_time, // REQUIRED field
    customer_tambak_pelanggan: visitData.customer_id, // Customer ID
    PIC: picValue, // Add PIC field with current user ID
    visit_location: visitData.visit_location || visitData.customer_address || null, // Support both fields
    visit_coordinates: visitData.visit_coordinates || null, // Backward compatibility
    Coordinates_Home: visitData.Coordinates_Home || null, // New field for schedule data
    visit_purpose: visitData.visit_purpose || 'Customer visit with arrival photo',
    visit_notes: visitData.visit_notes, // From textarea input
    satisfaction_rating: visitData.satisfaction_rating || null,
    Progress: visitData.progress_status || null, // Progress status from dropdown (mapped to Progress field)
    status: visitData.status || 'completed',
    end_time: endTime,
    date_created: visitData.start_time,
    date_updated: endTime,
    // Farm PIC fields
    Farm_Pic: visitData.farm_pic_name || null,
    contact: visitData.farm_pic_contact || null,
    Position: visitData.farm_pic_position || null
  };
  
  console.log('📤 Creating COMPLETE customer visit with payload:', payload);
  console.log('📍 visit_coordinates format:', payload.visit_coordinates);
  console.log('🏠 Coordinates_Home format:', payload.Coordinates_Home);
  console.log('📍 visit_location value:', payload.visit_location);
  console.log('📝 visit_notes value:', payload.visit_notes);
  console.log('📊 Progress value:', payload.Progress);
  console.log('👤 PIC value:', payload.PIC);
  console.log('🆔 customer_tambak_pelanggan value:', payload.customer_tambak_pelanggan);
  console.log('⏰ end_time value:', payload.end_time);
  console.log('👤 Farm_Pic value:', payload.Farm_Pic);
  console.log('📞 contact value:', payload.contact);
  console.log('💼 Position value:', payload.Position);
  
  try {
    let result;
    
    // Safety check: Never try to update temporary IDs
    const existingId = String(visitData.existing_visit_id || '');
    const isTemporaryId = existingId.startsWith('temp_') || existingId.includes('temp_visit_');
    
    if (visitData.existing_visit_id && !isTemporaryId) {
      // Update existing visit record to preserve selfie and other data
      result = await directus.request(
        updateItem('customer_visits' as any, visitData.existing_visit_id, payload)
      );
    } else {
      // Create new visit record (default behavior)
      result = await directus.request(
        createItem('customer_visits' as any, payload)
      );
    }
    
    return result;
      
  } catch (error: any) {
    console.error('❌ Failed to create complete customer visit:', error.message);
    
    // Log detailed error information for debugging
    if (error.errors && Array.isArray(error.errors)) {
      error.errors.forEach((err: any, index: number) => {
        console.error(`Error ${index + 1}:`, {
          message: err.message,
          code: err.extensions?.code,
          field: err.extensions?.field,
          collection: err.extensions?.collection,
          value: err.extensions?.value
        });
      });
    }
    
    throw error;
  }
};

/**
 * START SCHEDULED VISIT - Update a planned/scheduled visit to active status
 * This is used when user clicks "Mulai Kunjungan" from schedule detail modal
 */
export const startScheduledVisit = async (visitId: string) => {
  await ensureAuthenticated();
  
  // Update the visit status to active and set start time
  const startTime = getWIBTime(); // Proper WIB time handling
  
  const updateData = {
    status: 'active',
    visit_purpose: 'Visit started from schedule',
    date_updated: startTime,
  };
  
  try {
    const result = await directus.request(
      updateItem('customer_visits' as any, visitId, updateData)
    );
    
    // Ensure the returned result contains the original visitId
    // Sometimes Directus doesn't return the ID in the update response
    const returnResult = {
      ...result,
      id: result.id || visitId
    };
    
    return returnResult;
    
  } catch (error: any) {
    console.error('❌ Failed to start scheduled visit:', error.message);

    // Log detailed error information for debugging
    if (error.errors && Array.isArray(error.errors)) {
      error.errors.forEach((err: any, index: number) => {
        console.error(`Error ${index + 1}:`, {
          message: err.message,
          code: err.extensions?.code,
          field: err.extensions?.field,
          collection: err.extensions?.collection,
          value: err.extensions?.value
        });
      });
    }

    throw error;
  }
};

// ============================================================================
// PRODUCTS, INCENTIVES, AND PROMOTIONS OPERATIONS
// ============================================================================

/**
 * Get active coupons from couponUser and coupon tables
 */
export const getProductIncentives = async (limit = 20) => {
    await ensureAuthenticated();

    try {
      console.log('🔍 Fetching coupons from Directus...');
      const now = new Date().toISOString();

      // Query couponUser table and expand to get coupon details
      const couponUsers = await directus.request(readItems('couponUser', {
        fields: [
          '*',
          'coupon.coupon_id.*'  // M2M expansion to get coupon details
        ],
        filter: {
          claimed: { _eq: true }  // Only get issued coupons
        },
        limit
      }));

      console.log(`✅ Fetched ${couponUsers?.length || 0} coupon users from Directus`);

      // Extract and flatten coupon data, filter by date validity
      const coupons: any[] = [];
      (couponUsers || []).forEach((couponUser: any) => {
        if (couponUser.coupon && Array.isArray(couponUser.coupon)) {
          couponUser.coupon.forEach((relation: any) => {
            const coupon = relation.coupon_id;
            if (coupon) {
              // Check if coupon is valid (date range)
              const validFrom = coupon.validFrom ? new Date(coupon.validFrom).toISOString() : null;
              const validUntil = coupon.validUntil ? new Date(coupon.validUntil).toISOString() : null;

              const isValid = (!validFrom || validFrom <= now) && (!validUntil || validUntil >= now);

              if (isValid) {
                coupons.push({
                  ...coupon,
                  issuedTime: couponUser.issuedTime
                });
              }
            }
          });
        }
      });

      console.log(`✅ Found ${coupons.length} valid coupons`);
      return coupons;
    } catch (error: any) {
      console.warn('⚠️ Coupon collection not available or error fetching:', error.message);
      console.warn('⚠️ Error details:', error);
      // Return empty array if collection doesn't exist or has errors
      return [];
    }
};

/**
 * Get active promotions with date filtering
 */
export const getPromotions = async (limit = 20) => {
    await ensureAuthenticated();

    try {
      console.log('🔍 Fetching promotions from Directus...');
      const now = new Date().toISOString();

      const promotions = await directus.request(readItems('emp_promotions', {
        fields: ['*', 'banner_image.*'],
        filter: {
          _and: [
            { is_active: { _eq: true } },
            { valid_from: { _lte: now } },
            { valid_to: { _gte: now } }
          ]
        },
        sort: ['-valid_to'],
        limit
      }));

      console.log(`✅ Fetched ${promotions?.length || 0} promotions from Directus`);

      // Map promotions to include displayable banner_image_url
      const promotionsWithUrls = await Promise.all((promotions || []).map(async (promotion: any) => {
        let banner_image_url = null;

        if (promotion.banner_image?.id) {
          try {
            banner_image_url = await getAuthenticatedAssetUrl(promotion.banner_image.id);
          } catch (error) {
            console.warn('Failed to get banner image URL for promotion:', promotion.id, error);
          }
        }

        return {
          ...promotion,
          banner_image_url
        };
      }));

      console.log(`✅ Processed ${promotionsWithUrls.length} promotions with URLs`);
      return promotionsWithUrls;
    } catch (error: any) {
      console.warn('⚠️ Promotions collection not available or error fetching:', error.message);
      console.warn('⚠️ Error details:', error);
      // Return empty array if collection doesn't exist or has errors
      return [];
    }
};

/**
 * Get detailed coupon information by ID
 */
export const getIncentiveById = async (couponId: string) => {
    await ensureAuthenticated();

    try {
      console.log(`🔍 Fetching coupon details for ID: ${couponId}`);
      const coupon = await directus.request(readItem('coupon', couponId));

      if (!coupon) {
        throw new Error(`Coupon with ID ${couponId} not found`);
      }

      console.log('✅ Fetched coupon details:', coupon.nama);
      return coupon;
    } catch (error: any) {
      console.error('❌ Failed to fetch coupon details:', error);
      throw new Error(`Failed to fetch coupon details: ${error.message}`);
    }
};

/**
 * Get detailed promotion information by ID
 */
export const getPromotionById = async (promotionId: string) => {
  await ensureAuthenticated();

  try {
    const promotion = await directus.request(readItem('emp_promotions', promotionId, {
      fields: ['*', 'banner_image.*']
    }));

    if (!promotion) {
      throw new Error(`Promotion with ID ${promotionId} not found`);
    }

    // Add displayable banner_image_url
    let banner_image_url = null;
    if (promotion.banner_image?.id) {
      try {
        banner_image_url = await getAuthenticatedAssetUrl(promotion.banner_image.id);
      } catch (error) {
        console.warn('Failed to get banner image URL for promotion:', promotionId, error);
      }
    }

    return {
      ...promotion,
      banner_image_url
    };
  } catch (error: any) {
    console.error('❌ Failed to fetch promotion details:', error);
    throw new Error(`Failed to fetch promotion details: ${error.message}`);
  }
};

// =============================================================================
// COUPON OPERATIONS - Using actual coupon and couponUser tables
// =============================================================================

/**
 * Get active coupons with date filtering
 */
export const getCoupons = async (limit = 20) => {
  await ensureAuthenticated();

  try {
    const now = new Date().toISOString();

    const coupons = await directus.request(readItems('coupon', {
      filter: {
        _and: [
          {
            _or: [
              { validFrom: { _null: true } },
              { validFrom: { _lte: now } }
            ]
          },
          {
            _or: [
              { validUntil: { _null: true } },
              { validUntil: { _gte: now } }
            ]
          }
        ]
      },
      sort: ['-date_created'],
      limit
    }));

    return coupons || [];
  } catch (error: any) {
    console.error('❌ Failed to fetch coupons:', error);
    throw new Error(`Failed to fetch coupons: ${error.message}`);
  }
};

/**
 * Get user's issued coupons with full coupon details
 */
export const getUserCoupons = async (userId?: string) => {
  await ensureAuthenticated();

  try {
    const currentUser = await directusOperations.getCurrentUser();
    const targetUserId = userId || currentUser.id;

    const userCoupons = await directus.request(readItems('couponUser', {
      fields: [
        '*',
        'coupon.id',
        'coupon.nama',
        'coupon.details',
        'coupon.validFrom',
        'coupon.validUntil',
        'user.id',
        'user.first_name',
        'user.last_name',
        'user.email'
      ],
      filter: {
        user: {
          directus_users_id: { _eq: targetUserId }
        }
      },
      sort: ['-issuedTime', '-date_created']
    }));

    return userCoupons || [];
  } catch (error: any) {
    console.error('❌ Failed to fetch user coupons:', error);
    throw new Error(`Failed to fetch user coupons: ${error.message}`);
  }
};

/**
 * Get coupon by ID with full details
 */
export const getCouponById = async (couponId: string) => {
  await ensureAuthenticated();

  try {
    const coupon = await directus.request(readItem('coupon', couponId));

    if (!coupon) {
      throw new Error(`Coupon with ID ${couponId} not found`);
    }

    return coupon;
  } catch (error: any) {
    console.error('❌ Failed to fetch coupon details:', error);
    throw new Error(`Failed to fetch coupon details: ${error.message}`);
  }
};

/**
 * Issue a coupon to a user
 */
export const issueCouponToUser = async (couponId: string, userId?: string) => {
  await ensureAuthenticated();

  try {
    const currentUser = await directusOperations.getCurrentUser();
    const targetUserId = userId || currentUser.id;

    // Create couponUser entry
    const couponUser = await directus.request(createItem('couponUser', {
      claimed: true,
      issuedTime: new Date().toISOString()
    }));

    // Link to coupon via junction table
    await directus.request(createItem('couponUser_coupon', {
      couponUser_id: couponUser.id,
      coupon_id: couponId
    }));

    // Link to user via junction table
    await directus.request(createItem('couponUser_directus_users', {
      couponUser_id: couponUser.id,
      directus_users_id: targetUserId
    }));

    return couponUser;
  } catch (error: any) {
    console.error('❌ Failed to issue coupon:', error);
    throw new Error(`Failed to issue coupon: ${error.message}`);
  }
};

/**
 * Get products from product_tqs with nested files
 */
export const getProducts = async (limit = 100) => {
  await ensureAuthenticated();

  try {
    console.log('🔍 Fetching products from Directus...');
    const products = await directus.request(readItems('product_tqs', {
      fields: [
        '*',
        'availability',
        'files.product_files_id.id',
        'files.product_files_id.file_type',
        'files.product_files_id.sort',
        'files.product_files_id.file.id',
        'files.product_files_id.file.filename_download',
        'files.product_files_id.file.type',
        'files.product_files_id.file.filesize',
        'files.product_files_id.file.width',
        'files.product_files_id.file.height',
        'productList.ProductList_id.id',
        'productList.ProductList_id.Item_Name',
        'productList.ProductList_id.Harga_Jual',
        'productList.ProductList_id.Komisi',
        'productList.ProductList_id.Persen_harga_jual'
      ],
      filter: {
        status: { _eq: 'active' }
      },
      sort: ['product_name'],
      limit
    }));

    console.log(`✅ Fetched ${products?.length || 0} products from Directus`);
    if (products && products.length > 0) {
      console.log('📦 Sample product data:', {
        id: products[0].id,
        product_name: products[0].product_name,
        availability: products[0].availability,
        hasProductList: !!products[0].productList,
        productListCount: products[0].productList?.length || 0
      });
    }

    // Map products to include cover image URL
    const productsWithUrls = await Promise.all((products || []).map(async (product: any) => {
      let cover_image_url = null;

      // Find image file type for cover
      const imageFile = product.files?.find((f: any) =>
        f.product_files_id?.file_type === 'image'
      );

      if (imageFile?.product_files_id?.file?.id) {
        try {
          cover_image_url = await getAuthenticatedAssetUrl(imageFile.product_files_id.file.id);
        } catch (error) {
          console.warn('Failed to get cover image URL for product:', product.id, error);
        }
      }

      return {
        ...product,
        cover_image_url,
        // Map Directus fields to interface fields
        name: product.product_name,
        code: product.coda_id,
        category: product.type
      };
    }));

    console.log(`✅ Processed ${productsWithUrls.length} products with URLs`);
    return productsWithUrls;
  } catch (error: any) {
    console.error('❌ Failed to fetch products:', error);
    throw new Error(`Failed to fetch products: ${error.message}`);
  }
};

/**
 * Get product by ID with nested files
 */
export const getProductById = async (productId: string) => {
  await ensureAuthenticated();

  try {
    const product = await directus.request(readItem('product_tqs', productId, {
      fields: [
        '*',
        'availability',
        'files.product_files_id.*',
        'files.product_files_id.file.*',
        'productList.ProductList_id.id',
        'productList.ProductList_id.Item_Name',
        'productList.ProductList_id.Harga_Jual',
        'productList.ProductList_id.Komisi',
        'productList.ProductList_id.Persen_harga_jual'
      ]
    }));

    if (!product) {
      throw new Error(`Product with ID ${productId} not found`);
    }

    // Map product to include cover image URL and documents
    let cover_image_url = null;
    const documents: any[] = [];

    if (product.files && Array.isArray(product.files)) {
      for (const fileRelation of product.files) {
        const productFile = fileRelation.product_files_id;

        if (!productFile?.file?.id) continue;

        try {
          const fileUrl = await getAuthenticatedAssetUrl(productFile.file.id);

          if (productFile.file_type === 'image' && !cover_image_url) {
            cover_image_url = fileUrl;
          } else if (productFile.file_type === 'brosur' || productFile.file_type === 'document') {
            documents.push({
              id: productFile.id,
              type: productFile.file_type,
              name: productFile.file.filename_download,
              fileId: productFile.file.id,
              mimeType: productFile.file.type,
              size: productFile.file.filesize,
              downloadUrl: `${env.DIRECTUS_URL}/assets/${productFile.file.id}?download`,
              viewUrl: fileUrl
            });
          }
        } catch (error) {
          console.warn('Failed to get file URL for product:', product.id, error);
        }
      }
    }

    return {
      ...product,
      cover_image_url,
      documents: documents.sort((a, b) => (a.sort || 0) - (b.sort || 0)),
      // Map Directus fields to interface fields
      name: product.product_name,
      code: product.coda_id,
      category: product.type
    };
  } catch (error: any) {
    console.error('❌ Failed to fetch product by ID:', error);
    throw new Error(`Failed to fetch product by ID: ${error.message}`);
  }
};

/**
 * UPDATE ACTIVE VISIT DATA - Partial update for existing visit records
 * This implements partial updates following specific rules:
 * 1. Only updates fields that are sent in form_data
 * 2. Preserves all other fields unchanged
 * 3. Handles null values correctly (if sent, sets to null)
 * 4. Never removes or modifies other fields
 */
export const updateActiveVisitData = async (visitId: string, form_data: Record<string, any>) => {
  await ensureAuthenticated();
  
  // Map frontend field names to customer_visits database field names
  const mappedData: Record<string, any> = {};
  
  // Map common fields
  if (form_data.visit_notes !== undefined) mappedData.visit_notes = form_data.visit_notes;
  if (form_data.satisfaction_rating !== undefined) mappedData.satisfaction_rating = form_data.satisfaction_rating;
  if (form_data.Progress !== undefined) mappedData.Progress = form_data.Progress;
  if (form_data.status !== undefined) mappedData.status = form_data.status;
  if (form_data.end_time !== undefined) mappedData.end_time = form_data.end_time;
  if (form_data.visit_location !== undefined) mappedData.visit_location = form_data.visit_location;
  if (form_data.visit_coordinates !== undefined) mappedData.visit_coordinates = form_data.visit_coordinates;
  
  // Map Farm PIC fields to customer_visits schema
  if (form_data.Farm_Pic !== undefined) mappedData.Farm_Pic = form_data.Farm_Pic;
  if (form_data.contact !== undefined) mappedData.contact = form_data.contact;
  if (form_data.Position !== undefined) mappedData.Position = form_data.Position;

  // 🛠️ FIXED: Handle metadata field properly
  if (form_data.metadata !== undefined) {
    // Ensure metadata is always a JSON string for Directus
    mappedData.metadata = typeof form_data.metadata === 'string'
      ? form_data.metadata
      : JSON.stringify(form_data.metadata || {});
  }
  
  // Add timestamp for tracking updates
  const updateData = {
    ...mappedData,
    date_updated: getWIBTimeForDirectus()
  };
  
  // Debug log for field mapping
  if (import.meta.env?.DEV) {
    console.log('🔄 updateActiveVisitData field mapping:', {
      input_fields: Object.keys(form_data),
      mapped_fields: Object.keys(mappedData),
      final_update_data: updateData
    });
  }
  
  try {
    const result = await directus.request(
      updateItem('customer_visits' as any, visitId, updateData)
    );
    
    // Transform the result back to frontend format
    return transformCustomerVisitToVisit(result);
    
  } catch (error: any) {
    console.error('❌ Failed to update active visit data:', error.message);
    
    // Log detailed error information for debugging
    if (error.errors && Array.isArray(error.errors)) {
      error.errors.forEach((err: any, index: number) => {
        console.error(`Error ${index + 1}:`, {
          message: err.message,
          code: err.extensions?.code,
          field: err.extensions?.field,
          collection: err.extensions?.collection,
          value: err.extensions?.value
        });
      });
    }
    
    throw error;
  }
};

// =============================================================================
// SELFIE UPLOAD OPERATIONS
// =============================================================================

/**
 * Upload photo to specific field in customer visit (selfie, frontlocation, or Farm_area)
 */
export const uploadPhotoToCustomerVisit = async (data: {
  visitId: string;
  blob: Blob;
  filename: string;
  location: {
    latitude: number;
    longitude: number;
  };
  timestamp: string;
  description: string;
  fieldName: 'selfie' | 'frontlocation' | 'Farm_area'; // Field to update
  photoType: string; // Description for logging
  metadata?: string; // ✅ ADDED: Metadata JSON string for storage in customer_visits.metadata
}) => {
  await ensureAuthenticated();
  
  console.log(`Uploading ${data.photoType} photo:`, {
    visitId: data.visitId,
    photoSize: data.blob.size,
    photoType: data.blob.type,
    filename: data.filename,
    location: data.location,
    timestamp: data.timestamp,
    description: data.description,
    fieldName: data.fieldName
  });

  try {
    // Step 1: Upload photo file to Directus
    
    // Create File object from blob
    const file = new File([data.blob], data.filename, {
      type: data.blob.type || 'image/jpeg'
    });
    
    // Create FormData for the upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', `${data.photoType} - ${data.description}`);
    formData.append('description', `${data.photoType} taken at location ${data.location.latitude}, ${data.location.longitude} on ${data.timestamp}`);
    formData.append('type', file.type);

    // Upload file using Directus SDK with FormData
    const fileUploadResult = await directus.request(
      uploadFiles(formData)
    );
    
    
    if (!fileUploadResult || !fileUploadResult.id) {
      throw new Error('File upload failed - no file ID returned');
    }

    const fileId = fileUploadResult.id;

    // Step 2: Update customer visit record with photo file ID in the specified field
    const updateData: any = {
      [data.fieldName]: fileId,
      date_updated: getWIBTimeForDirectus()
    };
    
    const updateResult = await directus.request(
      updateItem('customer_visits' as any, data.visitId, updateData)
    );

    
    // Verify the update by checking if the field was actually set
    if (updateResult && updateResult[data.fieldName]) {
      console.log(`✅ ${data.photoType} uploaded successfully to field: ${data.fieldName}`);
    } else {
      if (env.DEBUG) console.warn(`⚠️ ${data.fieldName} field not found in update result. Full result:`, updateResult);
    }

    // 🛠️ FIXED: Use existing updateActiveVisitData pattern for metadata
    if (data.metadata) {
      try {
        console.log(`📊 Updating customer_visits.metadata for ${data.photoType} using existing pattern...`);

        // Get existing visit to preserve existing metadata structure
        const existingVisit = await directus.request(
          readItem('customer_visits' as any, data.visitId, {
            fields: ['metadata']
          })
        );

        // Parse existing metadata or create new structure
        let existingMetadata: any = {
          photos: [],
          last_updated: new Date().toISOString(),
          total_photos: 0
        };

        if (existingVisit?.metadata) {
          try {
            existingMetadata = typeof existingVisit.metadata === 'string'
              ? JSON.parse(existingVisit.metadata)
              : existingVisit.metadata;
          } catch (parseError) {
            console.warn('⚠️ Could not parse existing metadata, starting fresh:', parseError);
          }
        }

        // Parse new metadata
        const newMetadata = typeof data.metadata === 'string'
          ? JSON.parse(data.metadata)
          : data.metadata;

        // Create new photo object (following backend structure)
        const newPhotoObject = {
          id: `photo_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
          type: data.fieldName as 'frontlocation' | 'selfie' | 'Farm_area',
          file_name: data.filename,
          file_size: data.blob.size,
          file_id: fileId,
          captured_at: newMetadata.capture_date || new Date().toISOString(),
          uploaded_at: new Date().toISOString(),
          metadata: newMetadata
        };

        // Update metadata structure
        const updatedPhotos = [...(existingMetadata.photos || []), newPhotoObject];
        const finalMetadata = {
          photos: updatedPhotos,
          last_updated: new Date().toISOString(),
          total_photos: updatedPhotos.length,
          latest_gps: newPhotoObject.metadata.gps,
          latest_camera: newPhotoObject.metadata.camera,
          latest_file: {
            name: data.filename,
            size: data.blob.size,
            mime_type: data.blob.type || 'image/jpeg'
          }
        };

        // Use existing updateActiveVisitData pattern
        await updateActiveVisitData(data.visitId, {
          metadata: finalMetadata
        });

        console.log(`✅ Metadata updated successfully for ${data.photoType} photo. Total photos: ${updatedPhotos.length}`);

      } catch (metadataError) {
        console.warn(`⚠️ Error updating metadata:`, metadataError);
        // Don't fail the whole operation if metadata update fails
      }
    }

    return {
      fileId,
      visitUpdate: updateResult,
      fileUrl: `${directusUrl}/assets/${fileId}`,
      fileName: data.filename,
      fieldName: data.fieldName
    };

  } catch (error: any) {
    console.error(`❌ Failed to upload ${data.photoType}:`, error);
    
    if (error.errors && Array.isArray(error.errors)) {
      console.error('📋 Detailed API errors:');
      error.errors.forEach((err: any, index: number) => {
        console.error(`  Error ${index + 1}:`, {
          message: err.message,
          code: err.extensions?.code,
          field: err.extensions?.field,
          collection: err.extensions?.collection
        });
      });
    }
    
    throw error;
  }
};

/**
 * Upload selfie photo to Directus and update customer visit (legacy wrapper)
 */
export const uploadSelfieToCustomerVisit = async (data: {
  visitId: string;
  blob: Blob;
  filename: string;
  location: {
    latitude: number;
    longitude: number;
  };
  timestamp: string;
  description: string;
}) => {
  // Use the new generic function with selfie field
  return await uploadPhotoToCustomerVisit({
    ...data,
    fieldName: 'selfie',
    photoType: 'Arrival Selfie'
  });
};

/**
 * Create customer visit with selfie in one operation
 */
export const createCustomerVisitWithSelfie = async (data: {
  latitude: number;
  longitude: number;
  customerId: string;
  customerName: string;
  customerAddress: string;
  photoBlob: Blob;
  visitPurpose?: string;
}) => {
  await ensureAuthenticated();
  
  const currentUser = authManager.getState().user;
  if (!currentUser) throw new Error('No authenticated user');
  
  
  try {
    // Step 1: Upload selfie file
    const formData = new FormData();
    const fileName = `arrival_selfie_${Date.now()}.jpg`; // Date.now() is fine for unique filenames
    formData.append('file', data.photoBlob, fileName);
    formData.append('title', `Arrival Selfie - ${data.customerName}`);
    formData.append('description', `Arrival selfie at ${data.customerAddress}`);
    formData.append('folder', '19b26cb0-95e9-4be5-83d2-6757807f8dfe');

    const fileUploadResult = await directus.request(uploadFiles(formData));
    
    if (!fileUploadResult || !fileUploadResult.id) {
      throw new Error('Selfie upload failed');
    }

    const selfieFileId = fileUploadResult.id;

    // Step 2: Create customer visit with selfie
    const formattedTime = getWIBTime(); // Proper WIB time handling

    const visitData = {
      visit_date: formattedTime,
      customer_tambak_pelanggan: data.customerId,
      PIC: currentUser.id,
      visit_location: data.customerAddress,
      visit_coordinates: {
        lat: data.latitude,
        lng: data.longitude
      },
      visit_purpose: data.visitPurpose || 'Customer visit with arrival selfie',
      visit_type: 'sales',
      status: 'active',
      selfie: selfieFileId, // Link to uploaded selfie
      date_created: formattedTime,
      date_updated: formattedTime
    };

    const visitResult = await directus.request(
      createItem('emp_visits' as any, visitData)
    );


    return {
      visit: visitResult,
      selfieFileId,
      selfieUrl: `${directusUrl}/assets/${selfieFileId}`,
      fileName
    };

  } catch (error: any) {
    console.error('❌ Failed to create customer visit with selfie:', error);
    
    if (error.errors && Array.isArray(error.errors)) {
      console.error('📋 Detailed API errors:');
      error.errors.forEach((err: any, index: number) => {
        console.error(`  Error ${index + 1}:`, {
          message: err.message,
          code: err.extensions?.code,
          field: err.extensions?.field,
          collection: err.extensions?.collection
        });
      });
    }
    
    throw error;
  }
};

/**
 * VERIFY SELFIE FIELD EXISTS - Debug function to check if migration was applied
 */
export const verifySelfieField = async () => {
  await ensureAuthenticated();
  
  try {
    
    // Try to get collection fields information
    const response = await fetch(`${directusUrl}/fields/emp_visits`, {
      headers: {
        'Authorization': `Bearer ${authManager.getToken()}`
      }
    });
    
    if (response.ok) {
      const fields = await response.json();
      const selfieField = fields.data?.find((field: any) => field.field === 'selfie');
      
      if (selfieField) {
        return { exists: true, field: selfieField };
      } else {
        if (env.DEBUG) console.warn('⚠️ Selfie field not found in emp_visits collection');
        return { exists: false, availableFields: fields.data?.map((f: any) => f.field) };
      }
    } else {
      console.error('❌ Failed to fetch fields information:', response.status);
      return { exists: false, error: `HTTP ${response.status}` };
    }
  } catch (error) {
    console.error('❌ Error checking selfie field:', error);
    return { exists: false, error: error };
  }
};

// =============================================================================
// DIRECTUS USERS OPERATIONS
// =============================================================================

/**
 * Resolve user ID to full name (first_name + last_name)
 * This function can be used throughout the application to consistently display user names
 * instead of UIDs for better user experience.
 */
export async function resolveUserName(userId: string): Promise<string> {
  console.log('🔍 resolveUserName called with userId:', userId);
  
  if (!userId) {
    console.log('❌ No userId provided, returning "Unknown User"');
    return 'Unknown User';
  }
  
  try {
    console.log('📞 Calling directusUsersOperations.getUserById for:', userId);
    const user = await directusUsersOperations.getUserById(userId);
    console.log('👤 User data received:', user);
    
    if (user && (user.first_name || user.last_name)) {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      console.log('✅ Full name resolved:', fullName);
      return fullName || user.email || 'Unknown User';
    }
    
    // Fallback to email if no name is available
    const fallback = user?.email || 'Unknown User';
    console.log('📧 No first/last name, using email fallback:', fallback);
    return fallback;
  } catch (error) {
    console.error('❌ Error in resolveUserName for ID:', userId, error);
    return 'Unknown User';
  }
}

/**
 * Resolve multiple user IDs to names in batch (for performance)
 */
export async function resolveUserNames(userIds: string[]): Promise<Record<string, string>> {
  const resolved: Record<string, string> = {};
  
  // Use Promise.allSettled to handle failures gracefully
  const results = await Promise.allSettled(
    userIds.map(id => resolveUserName(id).then(name => ({ id, name })))
  );
  
  results.forEach(result => {
    if (result.status === 'fulfilled') {
      resolved[result.value.id] = result.value.name;
    }
  });
  
  return resolved;
}

export const directusUsersOperations = {
  /**
   * Get all directus users with filtering and pagination
   */
  async getUsers(options: { 
    filter?: any; 
    limit?: number; 
    offset?: number; 
    search?: string;
    fields?: string[];
  } = {}) {
    await ensureAuthenticated();
    
    const { 
      filter = {}, 
      limit = 50, 
      offset = 0, 
      search
    } = options;
    
    // Build search filter if search term provided
    let searchFilter = filter;
    if (search) {
      searchFilter = {
        ...filter,
        _or: [
          { first_name: { _icontains: search } },
          { last_name: { _icontains: search } },
          { email: { _icontains: search } },
          { title: { _icontains: search } }
        ]
      };
    }
    
    console.log('Fetching users with parameters:', {
      filter: searchFilter, 
      limit, 
      offset,
      search 
    });
    
    // Use SDK for directus_users collection access for better security
    try {
      const result = await directus.request(readUsers({
        filter: searchFilter,
        limit: limit,
        offset: offset,
        fields: ['id', 'first_name', 'last_name', 'email', 'title', 'role', 'status']
      }));
      
      return result;
      
    } catch (sdkError) {
      console.error('❌ Failed to fetch users via SDK:', sdkError);
      // Fallback: just return current user
      const currentUser = await directus.request(readMe());
      return [currentUser];
    }
  },

  /**
   * Get current authenticated user details
   */
  async getCurrentUser() {
    await ensureAuthenticated();
    
    const currentUser = authManager.getState().user;
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    
    return currentUser;
  },

  /**
   * Get user by ID
   */
  async getUserById(userId: string) {
    await ensureAuthenticated();
    
    try {
      // Use readUser for core collection instead of readItems
      const user = await directus.request(readUser(userId, {
        fields: ['id', 'first_name', 'last_name', 'email']
      }));
      
      return user;
    } catch (error) {
      console.warn('Failed to get user by ID:', userId, error);
      return null;
    }
  },

  /**
   * Search users by email, name, or title
   */
  async searchUsers(query: string, limit: number = 20) {
    return this.getUsers({
      search: query,
      limit,
      fields: ['id', 'first_name', 'last_name', 'email', 'title', 'status', 'role', 'last_access']
    });
  },

  /**
   * Get users by role
   */
  async getUsersByRole(roleId: string) {
    return this.getUsers({
      filter: { role: { _eq: roleId } },
      fields: ['id', 'first_name', 'last_name', 'email', 'title', 'status', 'last_access']
    });
  },

  /**
   * Get active users only
   */
  async getActiveUsers() {
    return this.getUsers({
      filter: { status: { _eq: 'active' } },
      fields: ['id', 'first_name', 'last_name', 'email', 'title', 'last_access']
    });
  }
};

// Export token management utilities for debugging
export const tokenUtils = {
  // Get current token status
  getTokenStatus: () => {
    const authState = authManager.getState();
    return {
      isAuthenticated: authState.isAuthenticated,
      hasToken: !!authState.token,
      tokenLength: authState.token?.length || 0,
      userId: authState.user?.id,
      userEmail: authState.user?.email
    };
  },
  
  // Force token refresh
  forceRefresh: () => authManager.forceTokenRefresh(),
  
  // Get preferred token
  getPreferredToken: () => authManager.getPreferredToken(),
  
  // Manual token validation
  validateToken: async () => {
    try {
      await ensureAuthenticated();
      return { success: true, message: 'Token is valid' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  }
};

// Enhanced token debugging utilities
export const enhancedTokenUtils = {
  ...tokenUtils,
  
  // Get comprehensive token status
  getDetailedStatus: () => {
    authManager.getState();
    const tokenInfo = authManager.getTokenInfo();
    
    return {
      ...tokenUtils.getTokenStatus(),
      ...tokenInfo,
      isRefreshing: (authManager as any).isRefreshing,
      isOnline: (authManager as any).isOnline,
      retryCount: (authManager as any).retryCount,
      timers: {
        hasRefreshTimer: !!(authManager as any).refreshTimer,
        hasKeepAliveTimer: !!(authManager as any).keepAliveTimer,
        hasTokenCheckTimer: !!(authManager as any).refreshTokenTimer,
      }
    };
  },

  // Force session recovery
  recoverSession: () => authManager.recoverSession(),
  
  // Get all timers status
  getTimersStatus: () => {
    const manager = authManager as any;
    return {
      refreshTimer: !!manager.refreshTimer,
      keepAliveTimer: !!manager.keepAliveTimer,
      refreshTokenTimer: !!manager.refreshTokenTimer,
      isRefreshing: manager.isRefreshing,
      isOnline: manager.isOnline
    };
  },

  // Emergency reset (for debugging) - warns user first
  emergencyReset: () => {
    console.warn('⚠️ Emergency token management reset requested');
    
    const userConsent = confirm(
      '⚠️ EMERGENCY RESET WARNING ⚠️\n\n' +
      'This will reset authentication and refresh the page.\n' +
      'Any unsaved work will be lost.\n\n' +
      'Only proceed if you are experiencing serious authentication issues.\n\n' +
      'Click OK to proceed, or Cancel to try other recovery methods first.'
    );
    
    if (userConsent) {
      console.warn('🚨 User confirmed emergency reset - performing hard reset');
      const manager = authManager as any;
      manager.stopTokenManagement();
      manager.clearPersistedAuth();
      
      // Give user a moment to save any work
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } else {
      console.log('✅ Emergency reset cancelled by user - suggesting soft recovery');
      console.log('💡 Try: window.tokenUtils.recoverSession() for soft recovery');
    }
  }
};

// Test utilities for token expiration handling (dev only)
const tokenExpirationTestUtils = {
  /**
   * Simulate token expiration scenario for testing
   * This will trigger the automatic logout functionality
   */
  simulateTokenExpiration(): void {
    console.warn('🧪 Simulating token expiration for testing');
    
    // Create a mock 401 error with "Token expired" message
    const mockError = {
      status: 401,
      message: 'Token expired.',
      extensions: { code: 'TOKEN_EXPIRED' },
      errors: [{ message: 'Token expired.', extensions: { code: 'TOKEN_EXPIRED' } }]
    };
    
    // Trigger the error handler
    authManager.handleApiError(mockError, 'token expiration test')
      .catch(error => {
        console.log('✅ Token expiration test completed:', error.message);
      });
  },

  /**
   * Test if the token expired detection works correctly
   */
  testTokenExpiredDetection(): void {
    console.group('🧪 Testing token expiration detection');
    
    const testCases = [
      { status: 401, message: 'Unauthorized', expected: true },
      { status: 403, message: 'Forbidden', expected: true },
      { message: 'Token expired.', expected: true },
      { message: 'Session expired', expected: true },
      { extensions: { code: 'TOKEN_EXPIRED' }, expected: true },
      { errors: [{ extensions: { code: 'TOKEN_EXPIRED' } }], expected: true },
      { status: 200, message: 'Success', expected: false },
      { status: 500, message: 'Server Error', expected: false }
    ];
    
    testCases.forEach((testCase, index) => {
      const isExpired = (authManager as any).isTokenExpiredError(testCase);
      const result = isExpired === testCase.expected ? '✅' : '❌';
      console.log(`${result} Test ${index + 1}:`, testCase, '→ Expected:', testCase.expected, 'Got:', isExpired);
    });
    
    console.groupEnd();
  },

  /**
   * Check current token expiration handling setup
   */
  checkSetup(): void {
    console.group('🔧 Token expiration handling setup check');
    
    const hasDispatch = !!(authManager as any).storeDispatch;
    console.log('✅ Redux dispatch registered:', hasDispatch);
    
    const isAuthenticated = authManager.getState().isAuthenticated;
    console.log('🔑 Currently authenticated:', isAuthenticated);
    
    const tokenInfo = authManager.getTokenInfo();
    console.log('⏰ Token info:', tokenInfo);
    
    console.groupEnd();
  }
};

// Make enhanced token utils available globally for debugging (dev only)
if (import.meta.env.DEV) {
  (window as any).tokenUtils = enhancedTokenUtils;
  (window as any).tokenExpirationTest = tokenExpirationTestUtils;
  
  // Import and setup auth testing utilities
  import('./auth-test').then(({ AuthenticationTester, quickAuthCheck }) => {
    (window as any).authTester = new AuthenticationTester();
    (window as any).quickAuthCheck = quickAuthCheck;
  }).catch(error => {
    if (env.DEBUG) console.warn('⚠️ Failed to load auth testing utilities:', error);
  });
  
  console.log('🔧 Debug utilities available:');
  console.log('  - window.tokenUtils (token management)');
  console.log('  - window.tokenExpirationTest (expiration testing)');
  console.log('  - window.authTester (authentication testing)');
}

/**
 * UPDATE CUSTOMER TAMBAK PIC INFORMATION
 * Updates the PIC (Person in Charge) and Contact information for a Customer_Tambak record
 */
export const updateCustomerTambakPIC = async (customerPelanggan: string, farmPicData: {
  name: string;
  contact: string;
  position: string;
}) => {
  await ensureAuthenticated();
  
  console.log('🚜 Updating Customer_Tambak PIC information...');
  console.log('📊 Customer:', customerPelanggan);
  console.log('📊 Farm PIC data:', farmPicData);
  
  try {
    // First, find the Customer_Tambak record by No_pelanggan
    const customerRecords = await directus.request(readItems('Customer_Tambak', {
      filter: { No_pelanggan: { _eq: customerPelanggan } },
      limit: 1
    }));
    
    if (!customerRecords || customerRecords.length === 0) {
      console.warn('⚠️ Customer_Tambak record not found for:', customerPelanggan);
      return;
    }
    
    const customerRecord = customerRecords[0];
    console.log('✅ Found Customer_Tambak record:', customerRecord.id);
    
    // Prepare PIC data in JSON format
    const picData = {
      name: farmPicData.name,
      contact: farmPicData.contact,
      position: farmPicData.position,
      updated_at: getWIBTimeForDirectus()
    };
    
    // Update the Customer_Tambak record
    const updateData = {
      PIC: picData, // Store as JSON object
      Contacs: farmPicData.contact, // Also update the main contact field
      tglInteraksiTerakhir: getWIBTimeForDirectus() // Update last interaction date
    };
    
    console.log('📤 Updating Customer_Tambak with data:', updateData);
    
    const result = await directus.request(
      updateItem('Customer_Tambak', customerRecord.id, updateData)
    );
    
    console.log('✅ Customer_Tambak PIC updated successfully:', result);
    return result;
    
  } catch (error: any) {
    console.error('❌ Failed to update Customer_Tambak PIC:', error.message);
    
    // Log detailed error information for debugging
    if (error.errors && Array.isArray(error.errors)) {
      console.log('📋 Detailed API errors:');
      error.errors.forEach((err: any, index: number) => {
        console.log(`  Error ${index + 1}:`, {
          message: err.message,
          code: err.extensions?.code,
          field: err.extensions?.field,
          collection: err.extensions?.collection,
          value: err.extensions?.value
        });
      });
    }
    
    throw error;
  }
};

/**
 * Fetch all active product promotions
 * @returns Array of product promotions
 */
directusOperations.getProductPromotions = async () => {
  try {
    console.log('📊 Fetching product promotions from Directus...');

    const promotions = await directus.request(
      readItems('emp_product_promotions', {
        filter: {
          status: { _eq: 'active' }
        },
        sort: ['sort_order', 'product_name'],
        limit: -1 // Get all
      })
    );

    console.log(`✅ Fetched ${promotions.length} product promotions`);
    return promotions;
  } catch (error: any) {
    console.error('❌ Failed to fetch product promotions:', error);
    throw error;
  }
};

/**
 * Fetch product promotion by ID
 * @param id - Product promotion ID
 * @returns Product promotion object
 */
directusOperations.getProductPromotionById = async (id: number) => {
  try {
    console.log(`📊 Fetching product promotion ${id}...`);

    const promotion = await directus.request(
      readItem('emp_product_promotions', id)
    );

    console.log('✅ Fetched product promotion:', promotion);
    return promotion;
  } catch (error: any) {
    console.error(`❌ Failed to fetch product promotion ${id}:`, error);
    throw error;
  }
};

/**
 * Fetch product promotions by priority level
 * @param priorityLevel - Priority level (e.g., 'uv_ivs', 'tomato_b3')
 * @returns Array of product promotions
 */
directusOperations.getProductPromotionsByPriority = async (priorityLevel: string) => {
  try {
    console.log(`📊 Fetching product promotions for priority: ${priorityLevel}...`);

    const promotions = await directus.request(
      readItems('emp_product_promotions', {
        filter: {
          priority_level: { _eq: priorityLevel },
          status: { _eq: 'active' }
        },
        sort: ['sort_order', 'product_name']
      })
    );

    console.log(`✅ Fetched ${promotions.length} promotions for ${priorityLevel}`);
    return promotions;
  } catch (error: any) {
    console.error(`❌ Failed to fetch promotions for ${priorityLevel}:`, error);
    throw error;
  }
};

/**
 * Fetch user coupons from couponUser table with filtering by current user and date validation
 * @param userId - Optional user ID (defaults to current authenticated user)
 * @returns Array of user coupons with full coupon details and claimed status
 */
directusOperations.getCouponProduk = async (userId?: string) => {
  try {
    console.log('📊 Fetching user coupons from couponUser table...');

    // Get current user if no userId provided
    const currentUser = await directus.request(readMe({
      fields: ['id']
    }));

    const targetUserId = userId || currentUser?.id;

    if (!targetUserId) {
      throw new Error('User not authenticated');
    }

    // Get current time for date validation
    new Date().toISOString();

    // Query couponUser table and filter by current user with updated structure
    const userCoupons = await directus.request(readItems('couponUser', {
      fields: [
        '*',
        'couponList.*',  // Expand couponList relation to get coupon details (updated structure)
        'doneTransaction'  // Include the new doneTransaction field
      ],
      filter: {
        user: { _eq: targetUserId }  // Fixed: Direct user filter instead of nested
      },
      sort: ['-date_created']
    }));

    console.log(`✅ Fetched ${userCoupons?.length || 0} coupon users from Directus`);

    // Process coupons: extract coupon data and validate date ranges
    const validCoupons: any[] = [];

    (userCoupons || []).forEach((couponUser: any) => {
      // Handle the new structure: couponList is an object, not an array
      if (couponUser.couponList && typeof couponUser.couponList === 'object') {
        const coupon = couponUser.couponList;

        if (coupon) {
          // Check if transaction is done - if doneTransaction=true, hide the coupon completely
          if (couponUser.doneTransaction === true) {
            console.log(`⏭️ Skipping coupon ${coupon.code} - transaction completed (doneTransaction=true)`);
            return; // Skip this coupon entirely
          }

          // Show all coupons regardless of date range (including expired ones)
          // Only filter out coupons with doneTransaction=true
          validCoupons.push({
            ...coupon,
            claimed: couponUser.claimed || false,
            claimedTime: couponUser.claimedTime,
            issuedTime: couponUser.issuedTime,
            doneTransaction: couponUser.doneTransaction || false,
            couponUserId: couponUser.id,
            user_created: couponUser.user_created,
            date_created: couponUser.date_created
          });
        }
      }
    });

    console.log(`✅ Valid coupons after date filtering: ${validCoupons.length}`);
    return validCoupons;
  } catch (error: any) {
    console.error('❌ Failed to fetch user coupons:', error);
    throw error;
  }
};

/**
 * Fetch coupon product by ID from coupon_produk collection
 * @param id - Coupon product ID
 * @returns Coupon product object
 */
directusOperations.getCouponProdukById = async (id: number) => {
  try {
    console.log(`📊 Fetching coupon product ${id}...`);

    const coupon = await directus.request(
      readItem('coupon_produk', id)
    );

    console.log('✅ Fetched coupon product:', coupon);
    return coupon;
  } catch (error: any) {
    console.error(`❌ Failed to fetch coupon product ${id}:`, error);
    throw error;
  }
};

/**
 * Claim a coupon by updating the couponUser table with claimed timestamp
 * @param couponUserId - The couponUser record ID to update
 * @returns Updated couponUser record
 */
directusOperations.claimCouponProduk = async (couponUserId: number) => {
  try {
    console.log(`🎟️ Claiming couponUser ${couponUserId}...`);

    // Get current user info to verify ownership
    const currentUser = await directus.request(readMe({
      fields: ['id', 'first_name', 'last_name', 'email']
    }));

    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    // Update the couponUser record with claimed status and timestamp
    const updatedCouponUser = await directus.request(updateItem('couponUser', couponUserId, {
      claimed: true,
      claimedTime: new Date().toISOString()
    }));

    console.log('✅ Coupon claimed successfully:', updatedCouponUser);

    return {
      success: true,
      couponUserId,
      userId: currentUser.id,
      claimedAt: updatedCouponUser.claimedTime,
      message: 'Coupon successfully claimed',
      data: updatedCouponUser
    };
  } catch (error: any) {
    console.error(`❌ Failed to claim coupon ${couponUserId}:`, error);
    throw new Error(`Failed to claim coupon: ${error.message}`);
  }
};

/**
 * Calculate commission for a product based on promotion rules
 * @param productId - Product promotion ID
 * @param quantity - Quantity of products
 * @returns Calculated commission amount
 */
directusOperations.calculateProductCommission = async (
  productId: number,
  quantity: number
): Promise<{ commission: number; details: string }> => {
  try {
    const promotion = await directusOperations.getProductPromotionById(productId);

    let commission = 0;
    let details = '';

    if (promotion.commission_unit === 'flat_rate') {
      commission = promotion.commission_amount;
      details = `Flat rate: Rp ${commission.toLocaleString('id-ID')}`;
    } else if (promotion.max_products_first_tier && quantity > promotion.max_products_first_tier) {
      // Apply first tier rate for first N products
      const firstTierCommission = promotion.commission_amount * promotion.max_products_first_tier;
      // Assume second tier is lower (could be configured)
      const remainingQuantity = quantity - promotion.max_products_first_tier;
      const secondTierCommission = remainingQuantity * (promotion.commission_amount * 0.8); // 80% rate

      commission = firstTierCommission + secondTierCommission;
      details = `First ${promotion.max_products_first_tier}: Rp ${firstTierCommission.toLocaleString('id-ID')}, Remaining: Rp ${secondTierCommission.toLocaleString('id-ID')}`;
    } else {
      // Standard calculation
      commission = promotion.commission_amount * quantity;
      details = `${quantity} x Rp ${promotion.commission_amount.toLocaleString('id-ID')}`;
    }

    // Add management fee if applicable
    if (promotion.management_amount) {
      commission += promotion.management_amount;
      details += ` + Management: Rp ${promotion.management_amount.toLocaleString('id-ID')}`;
    }

    return { commission, details };
  } catch (error: any) {
    console.error('❌ Failed to calculate commission:', error);
    throw error;
  }
};

export default directus;